import { VisaDocuments } from './VisaDocuments';
import { vizeUrunAdi } from './vize-urun-adi';
import { HotelOfferPicker } from './HotelOfferPicker';
import { isSingleVisaRequest, matchVisaRequest, searchKey, visaMatchedNotice, visaProducts, visaRequestFields } from './sales-catalogue';
import { QuoteCollection } from './QuoteCollection';
import './sales-composer.css';
import { SavedProductPicker } from './SavedProductPicker';
import type { SalesProduct } from './api';
import { COLLECTION_UI as COLLECTION } from './metin/collection';
import { opportunityAfterCustomerChange, prepareQuotePayment, reuseOrCreate } from './sales-flow';
import { DEFAULT_TAX_RATE_BPS, itemTaxInfos, paraAltBirime, priceQuoteDraft, revisionTaxFields, type DiscountMode, type ItemTaxInfo, type TaxChoice } from './quote-pricing';
import { isExpressLine, syncExpressQuantities } from './vize-kisi';
import { musteriAra, musteriSecenekleri } from './musteri-secenek';
import { VergiSecici } from './VergiSecici';
import { TutarKirilimiListesi } from './TutarKirilimiListesi';
import { buildTutarKirilimi, kalemKirilimi, taslakKirilimi } from './teklif-kirilim';
import { kalemKipAnahtari, kalemKipiniAyarla, kalemOranAnahtari, kayitliKalemVergiAlanlari, kayitliVergiKipi, satirlarFarkli, tumSatirlaraUygula, VARSAYILAN_VERGI_KIPI, VARSAYILAN_VERGI_ORANI } from './vergi-secimi';
export { paraAltBirime } from './quote-pricing';
import { OperationsCalendarPanel } from './OperationsCalendar';
import { OPS_DETAIL as D } from './metin/operasyon-detay';
import { TarihAraligiSecici, TarihSaatSecici } from './TarihSecici';
import { BANKA_HESAPLARI } from './banka-hesaplari';
import {
  cloneElement,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import {
  api,
  ApiError,
  publicQuoteLinks,
  WHATSAPP_AMACLARI,
  type AuditRecord,
  type ChecklistEntry,
  type CustomerListItem,
  type FinanceSummary,
  type FulfilmentTask,
  type ItemSuggestion,
  type ManagerExceptions,
  type Me,
  type Opportunity,
  type Order,
  type OrderListItem,
  type Oversight,
  type Paged,
  type PaymentEvidence,
  type PaymentLinkResult,
  type PriceLookupProviderView,
  type PriceLookupView,
  type PriceRecommendationView,
  type Product,
  type Quote,
  type QuoteItemInput,
  type QuoteRequest,
  type QuoteRequestFieldMeta,
  type QuoteRequestFieldPatch,
  type QuoteWhatsAppResult,
  type SerbestBelge,
  type ServiceCompletionResult,
  type SupplierBooking,
  type SupplierCancellation,
  type SupplierCostStatus,
  type SupplierNotificationRow,
  type VisaRequest,
  type VizeBelgeIstegi,
  type VizeBelgeUretimSonucu,
  type VizeBelgeTuru,
  type VizeOtelKaydi,
  type VizeOtelOzeti,
  VIZE_BELGE_KINDLERI,
  VIZE_BELGE_TURLERI,
  VIZE_UCUS_ROTALARI,
  type WhatsAppAmaci,
  type WhatsAppSendResult,
} from './api';
import { durumAdi, kalemTuru, musteriTuru, para, rolAdi, sayi, tamSayi, tarihSaat, ORTAK, TANIMSIZ } from './metin/ortak';
import { WHATSAPP_AMAC_ADI, WHATSAPP_DURUM_ADI, WORKFLOW_METIN as M } from './metin/workflow';
import { OZEL_PAKET, SATIS_UI as S, type SalesView } from './metin/satis-ui';
import { SalesBoard } from './SalesBoard';
import { SalesDetailDrawer, SalesRecords, quoteRecord } from './SalesRecords';
import { gecerlilikBitisi, gecerlilikMetni, gonderilecekGecerlilik, tasinacakGecerlilik, teklifSuresiDolduMu } from './teklif-gecerlilik';
import { FINANCE_UI as F, type FinanceDetailTab } from './metin/finans-ui';
import { FinanceConfirm } from './FinancePrimitives';

/**
 * CRM-A2 rol çalışma alanları. Ekrandaki her rakam uçtan geliyor: tarayıcı yetkili hiçbir durum
 * tutmuyor, kayan noktalı para aritmetiği yapmıyor ve dış bir tedarikçinin, ödeme sağlayıcısının
 * ya da fatura sisteminin bir şeyi teyit ettiğini asla iddia etmiyor.
 *
 * Ekranda çizilen bütün metin `metin/workflow.ts` içindeki tablodan geliyor; burada gömülü dize
 * yok. Kod değerleri (`ADMIN`, `IN_FULFILMENT`, `PARTIAL`) uca olduğu gibi gidiyor ve yalnız
 * ekrana yazılırken `durumAdi` ile karşılıkları gösteriliyor.
 */

export function describe(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : M.genel.istekBasarisiz;
}

/** Para olmayan tam sayı alanları: kazanma olasılığı, kabul edilecek sürüm numarası. */
function parseTamSayi(raw: string): number {
  if (!/^\d+$/.test(raw.trim())) throw new Error(M.genel.tamSayiUyarisi);
  return Number.parseInt(raw.trim(), 10);
}

/** Alt birimi giriş alanına yazılabilir biçime çeviren ters yön. Aynı tam sayı aritmetiği. */
export function altBirimYazi(minor: number): string {
  const isaret = minor < 0 ? '-' : '';
  const mutlak = Math.abs(minor);
  return `${isaret}${Math.trunc(mutlak / 100)},${String(mutlak % 100).padStart(2, '0')}`;
}

/**
 * Ondalıklı yazılan bir oranı **tam sayıya** çeviren tek yer.
 *
 * `paraAltBirime` ile aynı disiplin ve aynı gerekçe: hiçbir adımda kayan noktalı bir çarpma yok.
 * `Math.round(Number('3.6725') * 1e6)` yazmak kolaydı ve bir kurda son basamağı kaydırabilirdi;
 * burada tam kısım ile ondalık kısım ayrı okunuyor ve tam sayı olarak birleştiriliyor.
 *
 * `basamak` kaç ondalık basamağın **taşındığı**: fatura kurunda altı (mikro), marj oranında iki
 * (baz puan). Fazlası sessizce yuvarlanmıyor, geri çevriliyor.
 */
export function ondalikTamSayiya(raw: string, basamak: number, hataMetni: string): number {
  const temiz = raw.trim().replace(/\s/g, '');
  if (temiz === '') throw new Error(hataMetni);
  const eslesme = new RegExp(`^(-?)(\\d+)(?:[.,](\\d{1,${String(basamak)}}))?$`).exec(temiz);
  if (!eslesme) throw new Error(hataMetni);
  const [, isaret, tamKisim, ondalik = ''] = eslesme;
  const kesir = `${ondalik}${'0'.repeat(basamak)}`.slice(0, basamak);
  const carpan = 10 ** basamak;
  const deger = Number.parseInt(tamKisim!, 10) * carpan + Number.parseInt(kesir, 10);
  return isaret === '-' ? -deger : deger;
}

/** Ters yön: taşınan tam sayıyı okunur ondalığa çevirir. Aynı tam sayı aritmetiği. */
export function tamSayiOndaliga(deger: number, basamak: number): string {
  const isaret = deger < 0 ? '-' : '';
  const mutlak = Math.abs(deger);
  const carpan = 10 ** basamak;
  return `${isaret}${Math.trunc(mutlak / carpan)},${String(mutlak % carpan).padStart(basamak, '0')}`;
}

/**
 * Bir gönderim denemesinin kimliği.
 *
 * `crypto.randomUUID()` her çağrıda benzersiz; `Date.now()` ya da bir sayaç, aynı milisaniyede
 * iki sekmeden gönderilen iki denemeyi ayıramazdı ve ikisinden biri sessizce yutulurdu.
 * Tarayıcı desteklemiyorsa (güvenli olmayan bir kaynaktan servis edilen eski bir Safari)
 * rastgele bir dizeye düşüyor: benzersizliği zayıf ama sabit bir anahtardan hâlâ iyi.
 */
function yeniDenemeAnahtari(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  return (uuid ?? `${Math.random().toString(36).slice(2)}${Date.now()}`).slice(0, 80);
}



/**
 * `NONE` bir durum değil, durumun yokluğu: ortak durum sözlüğüne girmesi onu bir duruma
 * çevirirdi. Burada eleniyor ve değeri olmayan alanın işaretiyle yazılıyor.
 */
function durumYaz(kod: string | null | undefined): string {
  return !kod || kod === 'NONE' ? TANIMSIZ : durumAdi(kod);
}

function oncelikAdi(kod: string | null | undefined): string {
  return kod ? (M.oncelik[kod] ?? kod) : TANIMSIZ;
}

function varlikAdi(kod: string): string {
  return M.varlik[kod] ?? kod;
}

function useNotice() {
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Bir eylem okunabilir bir bildirim döndürebilir; başka her sonuç yok sayılıyor.
  const run = useCallback(async (action: () => Promise<unknown>) => {
    setError(null);
    try {
      const message = await action();
      if (typeof message === 'string') setNotice(message);
    } catch (caught) {
      setNotice(null);
      setError(describe(caught));
    }
  }, []);
  return { error, notice, run };
}

/**
 * Bir eylemin sonucu: hata ya da bildirim.
 *
 * Üç ayrı gönderimde uç 400 döndü ve ekranda tek cümle çıkmadı — kusur metnin çizilmemesinde
 * değildi (çiziliyordu), görünürlüğündeydi: küçük kırmızı bir satır, panelin en üstünde,
 * düğme paneli uzunsa görüş alanının dışında kalıyordu. Üç düzeltme birden:
 *
 *   1. `role="alert"` + `aria-live="assertive"`: ekran okuyucu hatayı **okuyor**, satırın
 *      görünür olması gerekmiyor.
 *   2. `.callout`: kırmızı çerçeveli kutu, tek satır kırmızı metinden daha göze çarpıyor.
 *   3. `scrollIntoView`: hata çizildiği anda görüş alanına giriyor — düğme panelin neresinde
 *      olursa olsun.
 */
export function Notices({ scope, error, notice }: { scope: string; error: string | null; notice: string | null }) {
  const errorRef = useRef<HTMLParagraphElement | null>(null);
  /**
   * Ürün Sahibi, 21.09.2026 CRM incelemesi: "bu error alertler gözükmüyor, sayfayı yukarı
   * kaydırmak lazım." İki ayrı sebebi vardı ve ikisi de burada kapanıyor.
   *
   * Birincisi `block: 'nearest'`: eleman kaydırma kabının kenarına **teğet** olduğunda tarayıcı
   * onu "zaten görünür" sayıp hiç kaydırmıyor. `center` böyle bir yorum bırakmıyor.
   *
   * İkincisi yerleşim: uyarı panelin en üstünde duruyor, eylem düğmeleri sihirbazın altında.
   * Kaydırma tamamlanmış olsa bile satışçının gözü düğmedeyken uyarı ekranın dışında kalıyordu.
   * `callout-sabit` uyarıyı panelin üstüne yapıştırıyor (bkz. `sales.css`), yani kaydırma
   * başarısız olsa bile uyarı ekranda duruyor.
   */
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [error]);
  return (
    <>
      {error ? (
        <p className="error callout callout-sabit" role="alert" aria-live="assertive" data-testid={`${scope}-error`} ref={errorRef}>
          {error}
        </p>
      ) : null}
      {notice ? (
        <p className="callout callout-ok" role="status" data-testid={`${scope}-notice`}>
          {notice}
        </p>
      ) : null}
    </>
  );
}

/**
 * Etiketli rakam: sipariş özetindeki dokuz sayının hiçbirinin görünür bir adı yoktu — yalnız
 * `data-testid`leri vardı, ve o özniteliği yalnız test okuyor. Etiket rakamın **yanında** duruyor,
 * içinde değil: mevcut testler değerin metnini ham hâliyle karşılaştırıyor (`toHaveText`).
 */
/** Damgadaki gün, Dubai takvimine göre (`YYYY-MM-DD`). UTC dilimi `slice` gece yarısı civarında bir gün kayar. */
function dubaiGunu(damga: string): string {
  const tarih = new Date(damga);
  if (Number.isNaN(tarih.getTime())) return damga.slice(0, 10);
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dubai', year: 'numeric', month: '2-digit', day: '2-digit' }).format(tarih);
}

/** Geçerlilik alanının altındaki ipucu: kayda gerçekte yazılacak bitiş (Dubai), geçmişteyse uyarıyla. */
function gecerlilikIpucu(bitis: Date | null): string {
  if (!bitis) return M.satis.teklifGecerlilikIpucu;
  const metin = gecerlilikMetni(bitis.toISOString());
  return bitis.getTime() <= Date.now() ? M.satis.teklifGecerlilikGecmis(metin) : M.satis.teklifGecerlilikBitis(metin);
}

/**
 * Görünür etiketli form alanı.
 *
 * Ölçülen kusur: `/satis` ekranındaki 45 alanın 41'i ekranda **tamamen adsız**di. Alan adları
 * yalnız `aria-label` içindeydi, yani ekran okuyucu her alanın adını biliyor, gözüyle bakan
 * bilmiyordu. Somut hâli: bir açılır liste ve beş kutu yan yana, üçü bomboş.
 *
 * Etiket alana `htmlFor`/`id` ile **bağlı**, bir `<span>` olarak yanına konmuş değil: bağ
 * kurulunca etikete tıklamak alana odaklanıyor, ve ekran okuyucunun okuduğu ad artık gözle
 * okunan adla aynı dize. Bu yüzden `aria-label` de kaldırıldı: aynı adı iki kanaldan vermek,
 * bir gün ikisinin ayrışmasına açık kapı bırakır.
 *
 * `id` `useId()` ile üretiliyor — elle yazılan bir id, aynı alan iki kez çizildiğinde (teklif
 * kalemleri) sessizce ikizlenirdi ve etiket yanlış kutuya bağlanırdı.
 *
 * `zorunlu` iki şey yapıyor: etiketin yanına görünür bir işaret koyuyor ve alana `required`
 * yazıyor. Ölçümde 45 alanın 45'inde `required` yoktu ve TC Kimlik No'nun zorunlu olduğu
 * hiçbir yerde yazmıyordu; hata ancak kaydettikten sonra geliyordu.
 */
export function Alan({
  className,
  etiket,
  ipucu,
  zorunlu,
  children,
}: {
  className?: string;
  etiket: string;
  ipucu?: string;
  zorunlu?: boolean;
  children: ReactElement<Record<string, unknown>>;
}) {
  const id = useId();
  const ipucuId = `${id}-ipucu`;
  return (
    <div className={className ? `alan ${className}` : 'alan'}>
      <label htmlFor={id}>
        {etiket}
        {zorunlu ? (
          <span className="alan-zorunlu" title={M.genel.zorunluAlan}>
            *
          </span>
        ) : null}
      </label>
      {cloneElement(children, {
        id,
        ...(ipucu ? { 'aria-describedby': ipucuId } : {}),
        ...(zorunlu ? { required: true } : {}),
      })}
      {ipucu ? (
        <small className="alan-ipucu" id={ipucuId}>
          {ipucu}
        </small>
      ) : null}
    </div>
  );
}

/** Bir satırda yan yana duran alanlar. Tek başına bir kutu değil, alanların ızgarası. */
export function AlanSatiri({ children }: { children: ReactNode }) {
  return <div className="alan-satiri">{children}</div>;
}

/**
 * Kopyalanabilir bir değer: IBAN, hesap numarası, ödeme linki.
 *
 * Panoya yazma her ortamda çalışmıyor (güvensiz kaynak, izin reddi, eski tarayıcı) ve
 * `navigator.clipboard` o durumlarda **sessizce** reddediliyor. Reddin görünmemesi, satışçının
 * kopyaladığını sanıp boş yapıştırması demek; o yüzden başarı da başarısızlık da yazılıyor.
 */
export function KopyalanirDeger({ deger, testId }: { deger: string; testId: string }) {
  const [durum, setDurum] = useState<'hazir' | 'kopyalandi' | 'hata'>('hazir');
  return (
    <span className="kopyalanir-deger">
      <code data-testid={testId}>{deger}</code>
      <button
        type="button"
        data-testid={`${testId}-kopyala`}
        onClick={() => {
          void navigator.clipboard
            ?.writeText(deger)
            .then(() => setDurum('kopyalandi'))
            .catch(() => setDurum('hata'));
          if (!navigator.clipboard) setDurum('hata');
        }}
      >
        {durum === 'kopyalandi' ? S.paymentCopied : durum === 'hata' ? S.paymentCopyFailed : S.paymentCopy}
      </button>
    </span>
  );
}

/** Şirketlerin tahsilat hesapları. Değerler `banka-hesaplari.ts` içinde, tek kopya. */
export function BankaHesaplari() {
  return (
    <div className="banka-hesaplari" data-testid="sales-bank-accounts">
      <p className="callout callout-warn" role="note" data-testid="sales-bank-currency-warning">
        {S.paymentCurrencyWarning}
      </p>
      {BANKA_HESAPLARI.map((hesap) => (
        <section key={hesap.anahtar} data-testid={`sales-bank-${hesap.anahtar}`}>
          <h5>{hesap.baslik}</h5>
          <p className="muted">{hesap.unvan}</p>
          <dl>
            {hesap.satirlar.map((satir) => (
              <div key={satir.etiket}>
                <dt>{satir.etiket}</dt>
                <dd>
                  {satir.kopyalanir ? (
                    <KopyalanirDeger deger={satir.deger} testId={`sales-bank-${hesap.anahtar}-${satir.etiket.replace(/[^A-Za-z]/gu, '').toLowerCase()}`} />
                  ) : (
                    satir.deger
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
    </div>
  );
}

/** Arama sonucunun kendi listesinde görünmeye başladığı harf sayısı. */
export const MUSTERI_ARAMA_ESIGI = 3;

/**
 * Satış ekranında seçilebilen para birimleri.
 *
 * Kaynak `prisma/seed.ts` içindeki `CURRENCIES`, artı `AED`: şirket BAE merkezli ve AED,
 * USD'ye 3,6725 sabit kuruyla bağlı. Liste uçtan çekilmiyor, çünkü `GET /config/reference`
 * `config:read` istiyor ve satış temsilcisinde o yetki yok; yetkisiz bir çağrı burayı
 * 403 ile boş bırakırdı. Kodun doğrulaması sunucuda kalıyor.
 */
export const SATIS_PARA_BIRIMLERI = ['USD', 'AED', 'EUR', 'TRY', 'GBP'] as const;

/**
 * Boş bırakılan fırsat başlığının karşılığı.
 *
 * Uç `title` için en az üç karakter istiyor (`CreateOpportunityDto`), o yüzden boş geçilemiyor.
 * Müşteri adı bilinmiyorsa tarih tek başına da üç karakteri geçiyor.
 */
export function firsatBasligi(yazilan: string, musteriAdi: string | undefined): string {
  const kirpilmis = yazilan.trim();
  if (kirpilmis.length >= 3) return kirpilmis.slice(0, 160);
  const tarih = new Date().toLocaleDateString('tr-TR');
  return (musteriAdi ? `${musteriAdi} · ${tarih}` : `Fırsat · ${tarih}`).slice(0, 160);
}

/**
 * Ekrana yazılan tutarı küçük birime çevirir: `1250,50` ve `1250.50` ikisi de 125050.
 *
 * Tam sayı aritmetiği: ondalık kısım metin olarak iki haneye tamamlanıyor, kayan noktayla
 * çarpma yapılmıyor. Boş alan 0 döndürüyor ve uç bunu kabul ediyor (`@Min(0)`).
 */
export function kucukBirime(yazilan: string): number {
  const temiz = yazilan.trim().replace(/\s/gu, '').replace(',', '.');
  if (temiz === '') return 0;
  const eslesme = /^(\d+)(?:\.(\d{0,2}))?$/u.exec(temiz);
  if (!eslesme) return 0;
  const kurus = (eslesme[2] ?? '').padEnd(2, '0');
  return Number.parseInt(eslesme[1] ?? '0', 10) * 100 + Number.parseInt(kurus, 10);
}

/**
 * Müşteri arama kutusu ve **kendi** sonuç listesi.
 *
 * Ürün Sahibi, 21.09.2026 CRM incelemesi: "Müşteri ara kısmında yazdığının sonucu kayıtlı
 * müşteri dropdown'ında görünüyor. Müşteri aranın altına dropdown ver, 3 harften sonra orada
 * göstersene." Eski davranış teknik olarak çalışıyordu ama okunmuyordu: yazılan metin listeyi
 * süzüyor, süzülmüş liste **başka bir alanın** açılırına düşüyordu. Yazdığı yerde sonuç görmeyen
 * satışçı aramanın bozuk olduğunu sanıyor.
 *
 * Liste yalnız `MUSTERI_ARAMA_ESIGI` harften sonra açılıyor: iki harfte 1.507 müşterinin
 * neredeyse tamamı eşleşiyor ve liste bilgi taşımıyor.
 */
export function MusteriArama({
  etiket,
  ipucu,
  sorgu,
  setSorgu,
  musteriler,
  sec,
  testId,
}: {
  etiket: string;
  ipucu?: string;
  sorgu: string;
  setSorgu: (deger: string) => void;
  musteriler: readonly CustomerListItem[];
  sec: (id: string) => void;
  testId: string;
}) {
  const [acik, setAcik] = useState(false);
  const yeterli = sorgu.trim().length >= MUSTERI_ARAMA_ESIGI;
  const goster = acik && yeterli;
  return (
    <div className="musteri-arama">
      <Alan etiket={etiket} ipucu={ipucu}>
        <input
          data-testid={testId}
          value={sorgu}
          maxLength={80}
          autoComplete="off"
          role="combobox"
          aria-expanded={goster}
          aria-controls={`${testId}-sonuclar`}
          onChange={(event) => {
            setSorgu(event.target.value);
            setAcik(true);
          }}
          onFocus={() => setAcik(true)}
          /* Tıklama listedeki düğmeye ulaşsın diye kapanış bir tur geciktiriliyor. */
          onBlur={() => window.setTimeout(() => setAcik(false), 150)}
        />
      </Alan>
      {goster ? (
        <ul className="musteri-arama-sonuc" id={`${testId}-sonuclar`} role="listbox" data-testid={`${testId}-sonuclar`}>
          {musteriler.length === 0 ? (
            <li className="musteri-arama-bos" role="presentation">{M.satis.musteriAramaSonucYok}</li>
          ) : (
            musteriler.map((musteri) => (
              <li key={musteri.id} role="option" aria-selected={false}>
                <button
                  type="button"
                  data-testid={`${testId}-sonuc-${musteri.id}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    sec(musteri.id);
                    setSorgu(musteri.displayName);
                    setAcik(false);
                  }}
                >
                  {musteri.displayName}
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
      {acik && !yeterli && sorgu.trim() !== '' ? (
        <p className="musteri-arama-ipucu" data-testid={`${testId}-esik`}>{M.satis.musteriAramaEsigi(MUSTERI_ARAMA_ESIGI)}</p>
      ) : null}
    </div>
  );
}

function Stat({ label, testId, children }: { label: string; testId: string; children: ReactNode }) {
  return (
    <div className="stat">
      <span className="stat-label">{label}</span>
      <p className="stat-value" data-testid={testId}>
        {children}
      </p>
    </div>
  );
}

/** Özel paketin fiyatı her teklifte elle girilir; boş ya da 0 fiyatla teklif kaydedilmez (04.10.2026). */
function assertOzelPaketFiyatli(items: QuoteItemInput[]): void {
  const satirlar = items.flatMap((item, index) => (item.salesProductId === OZEL_PAKET.id && !(item.unitPriceMinor > 0) ? [String(index + 1)] : []));
  if (satirlar.length) throw new Error(S.ozelPaketFiyatYok(satirlar.join(', ')));
}

/**
 * Fiyat alanı henüz metin hâlindeyken aynı ölçü: boş, okunamayan ya da 0 olan özel paket fiyatı.
 * Taslak toplamı bunu genel "tutar boş" uyarısından önce, özel paketin adıyla söylüyor.
 */
function ozelPaketFiyatsizSatirlar(items: QuoteItemInput[], metin: Record<string, string>): string[] {
  return items.flatMap((item, index) => {
    if (item.salesProductId !== OZEL_PAKET.id) return [];
    const yazi = metin[`${index}:unitPriceMinor`];
    let fiyat = item.unitPriceMinor;
    if (yazi !== undefined) {
      try { fiyat = paraAltBirime(yazi); } catch { fiyat = 0; }
    }
    return fiyat > 0 ? [] : [String(index + 1)];
  });
}

/** New quotes start empty; required item fields remain validated before sending. */
function assertKalemlerDolu(items: QuoteItemInput[]): void {
  if (!items.length) throw new Error(S.noItems);
  if (items.some((item) => item.description.trim().length < 2)) {
    throw new Error(M.satis.kalemEksikUyari);
  }
}
/** Teklif kaleminin para taşıyan dört alanı. Hepsi ekranda para biçiminde giriliyor. */
type ParaAlani = 'unitPriceMinor' | 'taxMinor' | 'feeMinor' | 'discountMinor' | 'governmentFeeMinor';

/**
 * Kalem türleri, **ucun kabul ettiği listenin tamamı**.
 *
 * `OTHER` ekranda yoktu ama `workflow.dto.ts` içindeki `ITEM_TYPES` onu kabul ediyordu: ekran
 * geçerli bir değeri gizliyordu. Katalogdan gelen ve bu listeye eşleşmeyen bir ürün türü artık
 * "Diğer" olarak düşüyor; eşleşmeyen türü rastgele bir kaleme benzetmek yanlış bir operasyon
 * görevi açardı.
 */
const ITEM_TYPES = ['HOTEL', 'TRANSFER', 'TOUR', 'ACTIVITY', 'VISA', 'HELICOPTER', 'YACHT', 'TICKET', 'ESIM', 'OTHER'] as const;
const TASK_STATUS_CODES = ['PENDING', 'IN_PROGRESS', 'BLOCKED', 'FULFILLED', 'FAILED', 'CANCELLED'] as const;
const PRIORITY_CODES = ['LOW', 'NORMAL', 'HIGH', 'URGENT'] as const;

// ---------------------------------------------------------------------------
// Satış
// ---------------------------------------------------------------------------

/**
 * Katalogdan teklif kalemi ekleme.
 *
 * Ölçülen kusur: iki kalemli bir teklifte 20 alan elle dolduruluyordu ve bunların içinde
 * **fiyat** da vardı. Alan sayısının çokluğu görünen kusurdu; asıl sebep, her kalemin sıfırdan
 * yazılmasıydı. Satışçı fiyatı ya ezberden yazıyor ya da ekranda hazır duran örnek veriyi
 * üstüne yazmadan gönderiyordu.
 *
 * **CRM ikinci bir katalog tutmuyor.** Liste, v3'ün web sitesinde gösterdiği ürün ağacının ta
 * kendisi: `GET /catalogue/products`, `commerce:catalogue_read` izniyle, ve CRM_SALES bu izni
 * zaten taşıyor (`ROLE_PERMISSIONS`, değiştirilmedi). Yayında olmayan ürün listede yok — bu bir
 * süzgeç değil, yayın kuralının kendisi: uç yayımlanmamış ürünü hiç göndermiyor. İkinci bir
 * katalog tutmak, bir gün iki fiyatın ayrışması demekti.
 *
 * **Ne dolduruluyor, ne doldurulmuyor.** Tür, açıklama ve birim fiyat katalogdan geliyor.
 * Tedarikçi gelmiyor, çünkü `V3Product` tedarikçi taşımıyor — tek istisna uçak ürünlerindeki
 * `airline`, ki o gerçekten tedarikçinin kendisi. Uydurulmuş bir tedarikçi adı, boş bir
 * alandan kötüdür: boş alan görünür, uydurulmuş olan görünmez.
 */
function KatalogSecici({ ekle, currency }: { ekle: (urun: Product) => void; currency: string | undefined }) {
  const [acik, setAcik] = useState(false);
  const [sayfa, setSayfa] = useState(1);
  const [eklenen, setEklenen] = useState('');
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => { if (acik) dialog.current?.showModal(); else dialog.current?.close(); }, [acik]);
  const [urunler, setUrunler] = useState<Product[] | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [arama, setArama] = useState('');
  const [tur, setTur] = useState('');
  const metin = M.satis.katalog;
  const [yukleniyor, setYukleniyor] = useState(false);
  const [yenidenDene, setYenidenDene] = useState(0);

  useEffect(() => {
    if (!acik) return;
    let iptal = false;
    setHata(null);
    setSayfa(1);
    setUrunler(null);
    const controller = new AbortController();
    // Sunucu v3'ü kendi içinde sayfalıyor ve önbelleğe alıyor; ilk okuma birkaç saniye, sonrakiler anında.
    const timeout = setTimeout(() => controller.abort(), 30_000);
    let products: Product[] = [], variants: Product[] = [];
    let remaining = 2;
    const errors: string[] = [];
    setYukleniyor(true);
    const publish = () => {
      if (!iptal) setUrunler([...products.filter(product => product.slug !== 'dubai-vizesi' || variants.length === 0), ...variants]);
    };
    const failed = (message: string) => {
      errors.push(message);
      if (!iptal) setHata(errors.join(' '));
    };
    const finished = () => {
      remaining -= 1;
      if (remaining === 0) { clearTimeout(timeout); if (!iptal) { publish(); setYukleniyor(false); } }
    };
    void api.catalogueSummary(currency ?? 'USD', controller.signal)
      .then(data => { products = data.products.map(product => ({ ...product, description: null, publishedAt: null })); publish(); })
      .catch(() => failed(metin.okunamadi)).finally(finished);
    void api.visaVariants(controller.signal).then(data => { variants = visaProducts(data); publish(); })
      .catch(() => failed(S.visaLoadError)).finally(finished);
    return () => { iptal = true; clearTimeout(timeout); controller.abort(); };
  }, [acik, currency, metin.okunamadi, yenidenDene]);

  const turler = useMemo(
    () => [...new Set((urunler ?? []).map((urun) => urun.kind))].sort(),
    [urunler],
  );

  // Bütün sayfalar yüklendikten sonra sözcükleri Türkçe harflerden bağımsız eşleştir.
  const suzulmus = useMemo(() => {
    const aranan = searchKey(arama).trim().split(/\s+/).filter(Boolean);
    return (urunler ?? []).filter((urun) => {
      if (tur && urun.kind !== tur) return false;
      return aranan.every(term => searchKey(`${urun.name} ${kalemTuru(urun.kind)}`).includes(term));
    });
  }, [urunler, arama, tur]);

  return (
    <><button type="button" className="birincil" data-testid="open-catalogue" onClick={() => setAcik(true)}>{S.catalogue}</button>
    <dialog ref={dialog} className="sales-catalogue-dialog" aria-label={S.catalogue} onClose={() => setAcik(false)}><header><h3>{S.catalogue}</h3><button type="button" data-testid="catalogue-close" onClick={() => setAcik(false)}>{S.close}</button></header>
    <section className="katalog-secici" data-testid="quote-catalogue">

      <p className="muted">{metin.aciklama}</p>
      {hata ? (
        <p className="error callout" role="alert" data-testid="quote-catalogue-error">
          {hata}
        </p>
      ) : null}
      {yukleniyor ? <p className="muted" role="status">{metin.yukleniyor}</p> : null}
      {hata && !yukleniyor ? <button type="button" onClick={() => setYenidenDene(value => value + 1)}>{S.catalogueRetry}</button> : null}
      {urunler ? (
        <>
          <AlanSatiri>
            <Alan etiket={metin.araEtiketi} ipucu={metin.araIpucu}>
              <input data-testid="catalogue-search" value={arama} maxLength={80} onChange={(event) => { setArama(event.target.value); setSayfa(1); }} />
            </Alan>
            <Alan etiket={metin.turSuzgeci}>
              <select data-testid="catalogue-kind" value={tur} onChange={(event) => { setTur(event.target.value); setSayfa(1); }}>
                <option value="">{metin.tumTurler}</option>
                {turler.map((kod) => (
                  <option key={kod} value={kod}>
                    {kalemTuru(kod)}
                  </option>
                ))}
              </select>
            </Alan>
          </AlanSatiri>
          {suzulmus.length === 0 ? (
            <div className="empty-state" data-testid="catalogue-empty">
              <strong>{metin.bosSonuc}</strong>
            </div>
          ) : (
            <div className="sales-product-grid" data-testid="catalogue-products">
              {suzulmus.slice((sayfa - 1) * 8, sayfa * 8).map((urun) => <article className="sales-product-card" key={urun.slug} data-testid="catalogue-product-row">
                <span>{kalemTuru(urun.kind)}</span><h4>{urun.name}</h4>
                <strong data-testid={`catalogue-price-${urun.slug}`}>{urun.fromPrice ? para(urun.fromPrice.amount, urun.fromPrice.currency) : TANIMSIZ}</strong>
                <button type="button" className="birincil" data-testid={`catalogue-add-${urun.slug}`} disabled={!!currency && !!urun.fromPrice && urun.fromPrice.currency !== currency} title={currency && urun.fromPrice && urun.fromPrice.currency !== currency ? S.currencyMismatch : undefined} onClick={() => { ekle(urun); setEklenen(urun.name); }}>{metin.ekle}</button>
              </article>)}
            </div>
          )}
          <div className="sales-catalogue-pagination"><span>{S.page(sayfa, suzulmus.length)}</span><button disabled={sayfa <= 1} onClick={() => setSayfa(sayfa - 1)}>{ORTAK.onceki}</button><button disabled={sayfa * 8 >= suzulmus.length} onClick={() => setSayfa(sayfa + 1)}>{ORTAK.sonraki}</button></div>
          <p className="sales-help">{S.catalogueLimit}</p>
          <p className="muted" data-testid="catalogue-count">
            {metin.sayac(tamSayi(suzulmus.length))}
          </p>
        </>
      ) : null}
      {eklenen ? <p className="sales-catalogue-added" role="status">{eklenen} · {S.catalogueAdded}</p> : null}
    </section></dialog></>
  );
}

/**
 * Fiyat önerisi: satış akışı vakası adım 4-5 (`docs/crm/satis-akisi-vaka.md`). Yalnız katalog
 * bağı olan kalemin altında görünüyor (`kalemKatalogRozeti`nin yanında) çünkü uç bir v3 slug'ı
 * istiyor — elle yazılmış bir kalemin karşılığı yok.
 *
 * **FR-25, 27.08.2026 kararıyla daraldı**: `GET`/`POST refresh` uçları `finance:read` yoksa
 * kısılmış (`toSalesView`) bir şekil gönderiyor, ama artık tedarikçi maliyetini (`amount`,
 * `currency`) de taşıyor — satışçı fiyatı savunabilsin diye (vaka adım 5: "Pax'ta 90 ...
 * tavsiye 98 dolar"). Kaynak, tazelik ayrıntısı ve marj kuralı hâlâ dönmüyor, bu bileşen de
 * onları göstermiyor. `tedarikciMaliyetiYaz` yalnız savunma amaçlı: `finance:read` taşıyan biri
 * bu ekranı açarsa uç genişletilmiş şekli (`staleReasonText`) gönderebilir; iki şeklin de tek
 * okunan alanı burada birleşiyor, ham `reason` kodu hiçbir dalda çıkmıyor.
 *
 * **Müşteriye giden hiçbir çıktı bu bileşene bağlı değil.** Teklif PDF'i ve web bağlantısı
 * (`/public/quotes/:token`) `QuotePdfService`in kendi Prisma sorgusundan geliyor; o sorgu
 * `netPriceMinor`/`marginBps` gibi iç alanları hiç seçmiyor. Bu ekranın maliyeti göstermesi
 * müşteri çıktısını etkilemiyor, çünkü iki kod yolu hiç kesişmiyor.
 *
 * **Tazele düğmesi yalnız `product.refreshable` true iken çiziliyor** — bu alan uçtaki tek doğru
 * kaynak (`kind === 'HOTEL' && supplierCode === 'paximum'`), ekran kendi hesabını yapmıyor. Tur
 * ve kendi ürünümüzde düğme hiç yok, "umut veren bir düğme" olmasın diye; yerine neden
 * çalışmadığını söyleyen bir cümle var.
 */
function FiyatOnerisiSatiri({ slug, serviceDate, checkOut: secilenCikis, customerId }: { slug: string; serviceDate?: string; checkOut?: string; customerId?: string }) {
  const { error, notice, run } = useNotice();
  const [oneri, setOneri] = useState<PriceRecommendationView | null>(null);
  const [acik, setAcik] = useState(false);
  const [checkIn, setCheckIn] = useState(serviceDate ? serviceDate.slice(0, 10) : '');
  const [checkOut, setCheckOut] = useState(secilenCikis ?? '');
  // Oda seçimi değişince önerinin tazeleme tarihleri de o konaklamayı göstersin.
  useEffect(() => { if (serviceDate) setCheckIn(serviceDate.slice(0, 10)); if (secilenCikis) setCheckOut(secilenCikis); }, [serviceDate, secilenCikis]);
  const [yetiskin, setYetiskin] = useState('2');
  const [uyruk, setUyruk] = useState('TR');
  const metin = M.satis.fiyatOnerisi;
  const testId = (parca: string) => `price-recommendation-${parca}-${slug}`;

  const getir = () =>
    void run(async () => {
      setAcik(true);
      const result = await api.priceRecommendation({ slug, serviceDate, customerId });
      setOneri(result);
    });

  const tazele = () =>
    void run(async () => {
      if (!checkIn || !checkOut) throw new Error(metin.tarihUyari);
      const adults = Number.parseInt(yetiskin, 10);
      if (!Number.isInteger(adults) || adults < 1) throw new Error(metin.yetiskinUyari);
      if (!/^[A-Za-z]{2}$/.test(uyruk.trim())) throw new Error(metin.uyrukUyari);
      const result = await api.refreshPriceRecommendation({
        slug,
        serviceDate,
        customerId,
        checkIn,
        checkOut,
        rooms: [{ adults }],
        nationality: uyruk.trim().toUpperCase(),
      });
      setOneri(result);
      return metin.tazelendi;
    });

  return (
    <div className="fiyat-onerisi" data-testid={testId('panel')}>
      <button type="button" className="ikincil" data-testid={testId(acik ? 'tekrar-getir' : 'getir')} onClick={getir}>
        {acik ? metin.tekrarGetir : metin.getir}
      </button>
      {acik ? (
        <>
          <Notices scope={testId('bildirim')} error={error} notice={notice} />
          {oneri ? (
            <div data-testid={testId('sonuc')}>
              <div className="stat-grid">
                <Stat label={metin.tedarikciMaliyetiBasligi} testId={testId('tedarikci-maliyeti')}>
                  {tedarikciMaliyetiYaz(oneri.supplierCost)}
                </Stat>
                <Stat label={metin.piyasaReferansiBasligi} testId={testId('piyasa-referansi')}>
                  {/*
                    Üç hâl, üç ayrı cevap: fiyat, bekleyiş, ya da kaynağın kendi sebebi. Bu satır
                    29.08.2026'ya kadar her zaman bir sebep cümlesi çiziyordu çünkü tip
                    `known: false` diye çiviliydi ve Booking fiyatı ekrana hiç çıkamıyordu.
                  */}
                  {oneri.marketReference.known
                    ? para(Number(oneri.marketReference.amountMinor), oneri.marketReference.currency)
                    : oneri.marketReference.pending
                      ? metin.piyasaBekliyor
                      : oneri.marketReference.reasonText}
                </Stat>
                <Stat label={metin.onerilenFiyatBasligi} testId={testId('oneri')}>
                  {oneri.recommendation.available
                    ? para(oneri.recommendation.suggestedUnitPriceMinor, oneri.recommendation.currencyCode)
                    : oneri.recommendation.reasonText}
                </Stat>
              </div>
              {/*
                Önerinin hangi yoldan geldiği. Çıplak bir sayı satışçıya savunacağı şeyi
                vermiyor: 4.817 ile 3.871 arasındaki fark, piyasadan mı marjdan mı geldiğidir.
              */}
              {oneri.recommendation.available ? (
                <p
                  className={
                    oneri.recommendation.basis === 'MARKET_SPREAD_BELOW_COST'
                      ? 'callout callout-warn'
                      : 'oneri-yolu'
                  }
                  role="status"
                  data-testid={testId('oneri-yolu')}
                  data-yol={oneri.recommendation.basis}
                >
                  {metin.oneriYolu[oneri.recommendation.basis]}
                </p>
              ) : null}

              {oneri.recommendation.available && oneri.recommendation.stale ? (
                <p className="callout callout-warn" role="status" data-testid={testId('bayat-uyari')}>
                  <strong>{metin.bayatBasligi}:</strong>{' '}
                  {oneri.supplierCost.known ? oneri.supplierCost.reasonText : null}
                </p>
              ) : null}

              {oneri.product.refreshable ? (
                <div data-testid={testId('tazele-alani')}>
                  <p className="muted">{metin.tazelemeAciklamasi}</p>
                  <AlanSatiri>
                    <Alan etiket={metin.konaklamaTarihleriEtiketi} zorunlu>
                      <TarihAraligiSecici testId={testId('tarihler')} giris={checkIn} cikis={checkOut} setAralik={(giris, cikis) => { setCheckIn(giris); setCheckOut(cikis); }} />
                    </Alan>
                    <Alan etiket={metin.yetiskinEtiketi} zorunlu>
                      <input inputMode="numeric" data-testid={testId('yetiskin')} value={yetiskin} onChange={(event) => setYetiskin(event.target.value)} />
                    </Alan>
                    <Alan etiket={metin.uyrukEtiketi} zorunlu>
                      <input maxLength={2} data-testid={testId('uyruk')} value={uyruk} onChange={(event) => setUyruk(event.target.value.toUpperCase())} />
                    </Alan>
                  </AlanSatiri>
                  <button type="button" className="ikincil" data-testid={testId('tazele')} onClick={tazele}>
                    {metin.tazele}
                  </button>
                  <p className="muted" data-testid={testId('sogutma-notu')}>
                    {metin.sogutmaNotu}
                  </p>
                </div>
              ) : (
                <p className="muted" data-testid={testId('tazelenemez')}>
                  {metin.tazelenemez}
                </p>
              )}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/**
 * 27.08.2026 kararı: satışçı tedarikçi maliyetini rakam olarak görür (`para(amount, currency)`),
 * bayat/bilinmeyen olduğunda olduğu gibi. Bayat uyarısının kendi metni ayrı bir `callout`ta
 * (aşağıda) gösteriliyor; burası yalnız tutarı yazıyor, ikinci bir bayat cümlesi tekrarlamıyor.
 */
function tedarikciMaliyetiYaz(supplierCost: PriceRecommendationView['supplierCost']): string {
  if (!supplierCost.known) return supplierCost.reasonText;
  return para(supplierCost.amount, supplierCost.currency);
}

// ---------------------------------------------------------------------------
// Teklif asistanı: talep alma (satış akışı vakası adım 2-5)
// ---------------------------------------------------------------------------

/** Bir talep alanının ekrandaki kipi: değer ile giriş kutusu arasındaki çeviriyi bu belirliyor. */
type TalepAlanKipi = 'metin' | 'tarih' | 'tamsayi' | 'para' | 'sayiListesi' | 'metinListesi';

/**
 * Ekranın **düzeltebildiği** alanlar, `UpdateQuoteRequestDto`nun kabul ettiği adlarla.
 *
 * Liste `QuoteRequestFieldPatch`in anahtarlarıyla tipleniyor, yani uçta olmayan bir alan adı
 * yazmak derleme hatası. Gerekçesi ölçülmüş bir kural: uç `forbidNonWhitelisted` ile çalışıyor
 * (`api/src/main.ts`), DTO'da olmayan bir anahtarı göndermek 400 döner ve satışçı çalışmayan bir
 * düğmeyle kalırdı.
 *
 * Uca yeni bir ayrıştırma alanı eklendiğinde (29.08.2026'da `hotelName` böyle eklendi) bu ekran
 * onu **zaten çiziyor**: alan listesi `fieldMeta`nın anahtarlarından okunuyor, buradan değil.
 * Buradaki liste yalnız "hangisi düzenlenebilir" sorusunun cevabı; DTO alanı kabul eder etmez
 * buraya bir satır eklemek yeterli.
 */
/**
 * Müşteriden alınması gereken bilginin üç hâli. `gerekmiyor`, `var` ile `eksik` arasında bir orta
 * yol değil: çocuk yaşı, çocuksuz bir talepte gerçekten istenmiyor ve onu "eksik" saymak
 * satışçıya var olmayan bir soru sordururdu.
 */
export type KontrolDurumu = 'var' | 'eksik' | 'gerekmiyor';

/**
 * Ürün Sahibi'nin 21.09.2026'da saydığı sekiz bilginin talepteki karşılığı.
 *
 * Listenin sırası onun cümlesindeki sıra: "adi soyadi, hangi tarihler kac kisi tatil konsepti ne
 * balayi mi aile tatili mi ultra lux aile tatili mi cocuklar kac yasinda varsa, butce ne
 * yaklasik olarak, istenen bolgeler var mi otel tavsiyesi icin, yada istenen oteller var mi."
 * Sıra keyfî değil: satışçı müşteriye bu sırayla soruyor.
 *
 * İki karar ölçüyle alındı:
 *
 * **Adı soyadı, talebin kendi alanı değil.** Eşleşen müşteri kaydında duruyor (`customerId`,
 * FR-05). Talebe ikinci bir isim alanı açmak, aynı kişinin iki yerde iki farklı yazımla durması
 * demek olurdu.
 *
 * **Çocuk yaşı, kişi sayısı bilinmeden sorulmuyor.** `childCount` boşsa çocuk olup olmadığı
 * bilinmiyor demektir ve bunu zaten `kisi` satırı işaretliyor; aynı eksiği iki satırda saymak
 * listeyi olduğundan kötü gösterirdi.
 */
export function musteriBilgiKontrolu(talep: QuoteRequest): readonly { anahtar: string; durum: KontrolDurumu }[] {
  const dolu = (deger: string | number | null): boolean => deger !== null && deger !== '';
  const cocukSayisi = talep.childCount;
  const yaslar = talep.childAges;
  const cocukYasiDurumu: KontrolDurumu =
    cocukSayisi === null || cocukSayisi === 0 ? 'gerekmiyor' : yaslar !== null && yaslar.length > 0 ? 'var' : 'eksik';
  return [
    { anahtar: 'ad', durum: talep.customerId !== null ? 'var' : 'eksik' },
    { anahtar: 'tarih', durum: dolu(talep.checkIn) && dolu(talep.checkOut) ? 'var' : 'eksik' },
    { anahtar: 'kisi', durum: dolu(talep.adultCount) && cocukSayisi !== null ? 'var' : 'eksik' },
    { anahtar: 'cocukYasi', durum: cocukYasiDurumu },
    { anahtar: 'konsept', durum: dolu(talep.holidayConcept) ? 'var' : 'eksik' },
    { anahtar: 'butce', durum: dolu(talep.budgetMinor) ? 'var' : 'eksik' },
    { anahtar: 'bolge', durum: dolu(talep.preferredRegion) ? 'var' : 'eksik' },
    { anahtar: 'otel', durum: dolu(talep.hotelName) ? 'var' : 'eksik' },
  ];
}

/** Kontrol listesinin çizimi. Eksik satırlar ayrı bir sınıf taşıyor, yalnız ayrı bir kelime değil. */
export function MusteriBilgiKontrolu({ talep }: { talep: QuoteRequest }) {
  const metin = M.satis.talep;
  const satirlar = musteriBilgiKontrolu(talep);
  const eksik = satirlar.filter((satir) => satir.durum === 'eksik').length;
  return (
    <section className="talep-kontrol" data-testid="sales-request-checklist" data-eksik={eksik}>
      <h4>{metin.kontrolBasligi}</h4>
      <p className="muted" data-testid="sales-request-checklist-summary">{metin.kontrolAciklamasi(eksik)}</p>
      <ul>
        {satirlar.map((satir) => (
          <li key={satir.anahtar} data-testid={`sales-request-checklist-${satir.anahtar}`} data-durum={satir.durum}>
            <span>{metin.kontrolSatirlari[satir.anahtar] ?? satir.anahtar}</span>
            <span className={`talep-kontrol-durum talep-kontrol-${satir.durum}`}>
              {satir.durum === 'var' ? metin.kontrolVar : satir.durum === 'eksik' ? metin.kontrolEksik : metin.kontrolGerekmiyor}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

const TALEP_ALANLARI: readonly { anahtar: keyof QuoteRequestFieldPatch; kip: TalepAlanKipi }[] = [
  { anahtar: 'destination', kip: 'metin' },
  { anahtar: 'hotelName', kip: 'metin' },
  { anahtar: 'checkIn', kip: 'tarih' },
  { anahtar: 'checkOut', kip: 'tarih' },
  { anahtar: 'nights', kip: 'tamsayi' },
  { anahtar: 'adultCount', kip: 'tamsayi' },
  { anahtar: 'childCount', kip: 'tamsayi' },
  { anahtar: 'childAges', kip: 'sayiListesi' },
  { anahtar: 'budgetMinor', kip: 'para' },
  { anahtar: 'budgetCurrencyCode', kip: 'metin' },
  { anahtar: 'hotelClass', kip: 'metin' },
  { anahtar: 'holidayConcept', kip: 'metin' },
  { anahtar: 'preferredRegion', kip: 'metin' },
  { anahtar: 'visaType', kip: 'metin' },
  /**
   * Gün sayısı, tam sayı ve **boş bırakılabilir**. "1 aylık vize" diyen bir talepte alan bilerek
   * boş kalıyor (bir ay sabit bir gün sayısı değil); boş bir kutuyu uca göndermiyoruz, çünkü uç
   * dizeyi 400 ile reddediyor ve boş bir değer zaten bir düzeltme değil.
   */
  { anahtar: 'visaDurationDays', kip: 'tamsayi' },
  { anahtar: 'preferredServices', kip: 'metinListesi' },
];

const TALEP_ALAN_ANAHTARLARI: ReadonlySet<string> = new Set(TALEP_ALANLARI.map((alan) => alan.anahtar));

/**
 * Talebin bir alanının ham değeri.
 *
 * Uç `QuoteRequest` satırını olduğu gibi gönderiyor ve ayrıştırma alan kümesi büyüyor; tipte
 * henüz adı geçmeyen bir alan da (`fieldMeta`da var, `QuoteRequest`te yok) okunabilmeli. Tür
 * gevşetmesi **tek** yerde, burada; çağıranlar dizeye ya da sayıya kendi kipleriyle çeviriyor.
 */
function talepDegeri(talep: QuoteRequest, anahtar: string): unknown {
  return (talep as unknown as Record<string, unknown>)[anahtar];
}

/** Değerin giriş kutusunda yazılı hâli. Para alt birimden tam sayı aritmetiğiyle çevriliyor. */
function talepAlanYazisi(talep: QuoteRequest, anahtar: string, kip: TalepAlanKipi): string {
  const deger = talepDegeri(talep, anahtar);
  if (deger === null || deger === undefined) return '';
  switch (kip) {
    // Uç tam ISO damgası gönderiyor (`2026-10-20T00:00:00.000Z`); `<input type="date">` günü ister.
    case 'tarih':
      return String(deger).slice(0, 10);
    case 'para':
      return typeof deger === 'number' ? altBirimYazi(deger) : '';
    case 'sayiListesi':
    case 'metinListesi':
      return Array.isArray(deger) ? deger.join(', ') : '';
    default:
      return String(deger);
  }
}

/** Düzeltilemeyen alanın okunur hâli. Boş değer uydurulmuyor, ortak "yok" işareti yazılıyor. */
function talepOkunurYazi(talep: QuoteRequest, anahtar: string): string {
  const deger = talepDegeri(talep, anahtar);
  if (deger === null || deger === undefined) return TANIMSIZ;
  if (Array.isArray(deger)) return deger.length === 0 ? TANIMSIZ : deger.join(', ');
  return String(deger);
}

/**
 * FR-04'ün cümlesi: alanın kaynağı ve güveni.
 *
 * Eşik hesabı burada **yok**: `lowConfidence` uçtan geliyor (`request-parsing.provider.ts`,
 * sağlayıcı eşiği kendi uyguluyor). Tarayıcıda ikinci bir eşik, bir gün ikisinin ayrışması
 * demekti.
 */
function talepGuvenCumlesi(meta: QuoteRequestFieldMeta | undefined): string | undefined {
  if (!meta) return undefined;
  const metin = M.satis.talep;
  if (meta.source === 'MANUAL') return meta.confidence >= 1 ? metin.guvenElle : metin.guvenAyristirilmadi;
  const yuzde = M.genel.yuzde(tamSayi(Math.round(meta.confidence * 100)));
  return meta.lowConfidence ? metin.guvenDusuk(yuzde) : metin.guvenTahmin(yuzde);
}

/**
 * Fiyat kutusunun tek hücresi. Dört hâlin üçünde tutar yok ve **nedeni uçtan geliyor**; ekran o
 * cümleyi olduğu gibi yazıyor. Boş bir hücre de uydurulmuş bir rakam da aynı yalanı söylerdi.
 */
function talepFiyatYazisi(gorunum: PriceLookupProviderView): string {
  if (gorunum.status === 'READY') return para(gorunum.amountMinor, gorunum.currencyCode);
  if (gorunum.status === 'PENDING') return M.satis.talep.fiyatBekliyor;
  return gorunum.reasonText;
}

/** Hangi ürüne, ne zaman sorulduğu. Sorulan ürün belli değilse satır hiç çizilmiyor. */
function talepFiyatKaynagi(gorunum: PriceLookupProviderView): string | null {
  if (gorunum.status === 'READY') return M.satis.talep.fiyatKaynagi(gorunum.productSlug, tarihSaat(gorunum.asOf));
  return gorunum.productSlug ? M.satis.talep.fiyatUrunu(gorunum.productSlug) : null;
}

/**
 * Kalem önerisini katalog ürününe çevirir.
 *
 * Teklife ekleme yolu **tek**: `katalogdanEkle`. İkinci bir ekleme yolu yazmak, katalog bağının
 * (`productSlug`) bir yoldan yazılıp öbüründen yazılmaması demekti — ve o bağ yazılmazsa "hangi
 * ürün ne kadar satıldı" sorusunun cevabı sonradan kurulamıyor. `itemType` uçta zaten kalem
 * türüne çevrilmiş (`mapV3KindToItemType`), yani `katalogdanEkle`nin tür eşlemesinden değişmeden
 * geçiyor.
 */
function oneriUrunu(oneri: ItemSuggestion): Product {
  return {
    slug: oneri.productSlug,
    kind: oneri.itemType,
    name: oneri.description,
    description: null,
    publishedAt: null,
    fromPrice:
      oneri.referenceSalePriceMinor === null
        ? null
        : { amount: oneri.referenceSalePriceMinor, currency: oneri.referenceCurrencyCode },
    airline: null,
  };
}

/** `ItemSuggestionQueryDto` üç para birimi kabul ediyor; talebinki bunlardan biri değilse EUR. */
const ONERI_PARA_BIRIMLERI = ['EUR', 'USD', 'AED'] as const;

/**
 * Satış akışı vakası adım 2-5'in ekranı.
 *
 * **Ölçülen kusur: uçlar vardı, ekranı yoktu.** `POST /sales/requests`, alan düzeltme, onay,
 * fiyat kutusu ve kalem önerisi 27.08.2026'da açıldı ve hiçbir ekrandan çağrılmıyordu; satışçının
 * müşteri mesajını yapıştıracağı bir kutu yoktu, yani vakanın 2. ve 3. maddesi yalnız API'den
 * yürüyordu.
 *
 * **Ayrıştırma bir tahmin, ve ekran bunu saklamıyor.** Alanlar düzenlenebilir çiziliyor, düşük
 * güvenli olanlar işaretli (FR-04), ve ayrıştırmanın dört hâli (`NOT_CONFIGURED`, `FAILED`,
 * `PARSED_EMPTY`, `PARSED`) birbirinden ayrı görünüyor — dördü de aynı boş forma benzediğinde
 * satışçı "sistem çalışmadı" ile "müşteri yazmamış" arasındaki farkı bilemiyordu (ölçüldü
 * 27.08.2026, `api/test/integration/talep-ayristirma-hata-gorunurlugu.spec.ts` dördünü de
 * gerçek HTTP üstünden üretiyor).
 *
 * **Müşteri seçimi paylaşılan durumdan.** `customerId` paneldeki müşteri listesiyle aynı durum:
 * talebin onaylandığı müşteri, o talepten açılacak fırsatın müşterisiyle **aynı** kayıt
 * (`createOpportunityFromRequest` talebin `customerId`sini kullanıyor). İkinci bir seçim durumu,
 * iki ekranın iki ayrı müşteriyi göstermesi demekti.
 *
 * FR-25 bu bileşende iki yerde geçiyor ve ikisinde de kural aynı: **uç ne gönderdiyse o**.
 * Kalem önerisinde maliyet alanları `finance:read` yoksa hiç dolmuyor (uç v3'ün maliyet ucunu o
 * aktör için çağırmıyor bile), o yüzden buradaki çizim onların dolu olduğunu hiç varsaymıyor.
 */
/** Bekleyen ayrıştırmayı yoklama aralığı ve üst sınırı: 1,5 sn x 60 = 90 sn, sunucunun eskiyen bekleyiş sınırıyla aynı. */
const TALEP_YOKLAMA_ARALIGI_MS = 1_500;
const TALEP_YOKLAMA_UST_SINIRI = 60;

function TalepAlmaBolumu({
  me,
  customers,
  customerId,
  setCustomerId,
  ekle,
  firsataBagla,
  currency,
}: {
  me: Me;
  customers: CustomerListItem[];
  customerId: string;
  setCustomerId: (id: string) => void;
  ekle: (urun: Product) => void;
  firsataBagla: (opportunityId: string, currencyCode: string) => Promise<void>;
  currency: string | undefined;
}) {
  const { error, notice, run } = useNotice();
  const [hamMetin, setHamMetin] = useState('');
  const [talep, setTalep] = useState<QuoteRequest | null>(null);
  /**
   * Düzeltmelerin **yazılı hâli**, alan adına göre. Kalem fiyatlarındaki `kalemMetni` ile aynı
   * gerekçe: her tuşta sayıya çevirip geri yazmak yarım yazılmış bir değeri (`250,`) imkânsız
   * kılardı. Ayrıca bu sözlük "hangi alana dokunuldu" sorusunun da cevabı — uca yalnız burada
   * duran anahtarlar gidiyor, çünkü verilmeyen alan uçta dokunulmamış sayılıyor.
   */
  const [duzeltmeler, setDuzeltmeler] = useState<Record<string, string>>({});
  const [fiyat, setFiyat] = useState<PriceLookupView | null>(null);
  const [oneriler, setOneriler] = useState<ItemSuggestion[] | null>(null);
  const metin = M.satis.talep;
  /**
   * Ayrıştırma arka planda sürüyor (`parsedAt` boş). Uç talebi hemen yazar; alanlar sonradan
   * dolar. Bu sürede form kilitli: dolmakta olan alanı düzeltmek, gelen sonucun üstüne yazmak
   * demekti (uç da 409 `PARSING_IN_PROGRESS` ile reddediyor).
   */
  const bekliyor = talep !== null && talep.parsedAt === null;
  const talepNo = talep?.id ?? null;

  useEffect(() => {
    if (!bekliyor || talepNo === null) return;
    let iptal = false;
    let deneme = 0;
    const zamanlayici = window.setInterval(() => {
      deneme += 1;
      void api
        .salesRequest(talepNo)
        .then(async (guncel) => {
          if (iptal) return;
          if (guncel.parsedAt === null && deneme < TALEP_YOKLAMA_UST_SINIRI) return;
          window.clearInterval(zamanlayici);
          setTalep(guncel);
          if (guncel.parsedAt !== null) setFiyat(await api.salesRequestPriceLookup(guncel.id));
        })
        .catch(() => {
          // Bir yoklama düşerse bir sonraki denenir; sınırı aşınca sunucunun eskiyen bekleyişi
          // kapatması (`GET`te) zaten sonucu getirir.
        });
    }, TALEP_YOKLAMA_ARALIGI_MS);
    return () => {
      iptal = true;
      window.clearInterval(zamanlayici);
    };
  }, [bekliyor, talepNo]);

  /** Talep her değiştiğinde fiyat kutusu da yeniden okunuyor: uç anında dönüyor, tedarikçiye gitmiyor. */
  const fiyatiOku = async (id: string) => {
    setFiyat(await api.salesRequestPriceLookup(id));
  };

  /** Yeni talebi panele alır: bayat düzeltme ve öneri temizlenir, fiyat kutusu yeniden okunur. */
  const talebiPaneleAl = async (olusan: QuoteRequest) => {
    setTalep(olusan);
    setDuzeltmeler({});
    setOneriler(null);
    // Eşleşme tekse uç `customerId`yi kendi yazıyor (FR-05); ekranın seçimi onu izliyor.
    if (olusan.customerId) setCustomerId(olusan.customerId);
    await fiyatiOku(olusan.id);
  };

  /** Çift tıklama kilidi ve "POST başarılı, sonrası düştü" durumunda aynı kaydı yeniden kullanma. */
  const talepKilidi = useRef(false);
  const [talepSuruyor, setTalepSuruyor] = useState(false);
  const sonKayit = useRef<{ text: string; record: QuoteRequest } | null>(null);

  const talebiAl = () => {
    if (talepKilidi.current) return;
    talepKilidi.current = true;
    setTalepSuruyor(true);
    void talebiAlIc().finally(() => {
      talepKilidi.current = false;
      setTalepSuruyor(false);
    });
  };

  const talebiAlIc = () =>
    run(async () => {
      if (hamMetin.trim().length < 10) throw new Error(metin.metinBosUyari);
      if (isSingleVisaRequest(hamMetin)) {
        const product = matchVisaRequest(hamMetin, visaProducts(await api.visaVariants()));
        if (!product) throw new Error(S.visaAmbiguous);
        if (currency && product.fromPrice && product.fromPrice.currency !== currency) throw new Error(S.currencyMismatch);
        const alanlar = visaRequestFields(hamMetin);
        if (!alanlar) throw new Error(S.visaAmbiguous);
        // Talep kaydı önce: yazılamazsa ürün de eklenmez, sessiz yarım kalan satış olmaz.
        // Panel ve müşteri eşleşmesi, normal yolla aynı yardımcıdan geçer (açık fırsata dokunmaz).
        const kayit = await reuseOrCreate(sonKayit, hamMetin, () => api.createVisaSalesRequest({ rawText: hamMetin, ...alanlar }));
        await talebiPaneleAl(kayit);
        sonKayit.current = null;
        ekle(product);
        return visaMatchedNotice(product);
      }

      const olusan = await reuseOrCreate(sonKayit, hamMetin, () => api.createSalesRequest({ rawText: hamMetin }));
      await talebiPaneleAl(olusan);
      sonKayit.current = null;
      return olusan.parsedAt === null ? metin.alindiAyristiriliyor : metin.alindi;
    });

  const duzeltmeleriKaydet = () =>
    void run(async () => {
      if (!talep) throw new Error(metin.metinBosUyari);
      const govde: Record<string, unknown> = { expectedVersion: talep.version };
      let sayac = 0;
      let bosBirakilan = false;
      for (const alan of TALEP_ALANLARI) {
        const ham = duzeltmeler[alan.anahtar];
        if (ham === undefined) continue;
        const temiz = ham.trim();
        // Boş bırakmak bir düzeltme değil: DTO verilmeyen alanı dokunulmamış sayıyor, silmiyor.
        if (temiz === '') {
          bosBirakilan = true;
          continue;
        }
        sayac += 1;
        switch (alan.kip) {
          case 'tamsayi':
            govde[alan.anahtar] = parseTamSayi(temiz);
            break;
          case 'para':
            govde[alan.anahtar] = paraAltBirime(temiz);
            break;
          case 'sayiListesi':
            govde[alan.anahtar] = temiz.split(',').map((parca) => parseTamSayi(parca));
            break;
          case 'metinListesi':
            govde[alan.anahtar] = temiz
              .split(',')
              .map((parca) => parca.trim())
              .filter((parca) => parca !== '');
            break;
          default:
            govde[alan.anahtar] = temiz;
        }
      }
      if (sayac === 0) throw new Error(bosBirakilan ? metin.bosDuzeltmeUyari : metin.duzeltmeYokUyari);
      const guncel = await api.updateSalesRequestFields(talep.id, govde as unknown as QuoteRequestFieldPatch);
      setTalep(guncel);
      setDuzeltmeler({});
      // Alanlar tamamlanmış olabilir; tetikleme eşiği uçta bu istekte yeniden değerlendiriliyor.
      await fiyatiOku(guncel.id);
      return metin.alanlarKaydedildi(tamSayi(sayac));
    });

  const onayla = () =>
    void run(async () => {
      if (!talep) throw new Error(metin.metinBosUyari);
      const secilen = talep.customerId ?? (customerId || '');
      if (!secilen) throw new Error(metin.musteriGerekliUyari);
      const guncel = await api.confirmSalesRequest(talep.id, { expectedVersion: talep.version, customerId: secilen });
      setTalep(guncel);
      return metin.onaylandi;
    });

  const firsatAc = () =>
    void run(async () => {
      if (!talep) throw new Error(metin.metinBosUyari);
      const firsat = await api.createOpportunityFromSalesRequest(talep.id, { expectedVersion: talep.version, ownerId: me.userId });
      // Talep artık `LINKED`; sürüm de değişti, ekranın elindeki kopya tazeleniyor.
      setTalep(await api.salesRequest(talep.id));
      await firsataBagla(firsat.id, firsat.currencyCode);
      return metin.firsatAcildi(firsat.code);
    });

  const onerileriGetir = () =>
    void run(async () => {
      if (!talep) throw new Error(metin.metinBosUyari);
      const arama = (talep.hotelName ?? '').trim() || (talep.destination ?? '').trim();
      if (arama.length < 2) throw new Error(metin.oneriAramaYokUyari);
      const paraBirimi = (ONERI_PARA_BIRIMLERI as readonly string[]).includes(talep.budgetCurrencyCode ?? '')
        ? talep.budgetCurrencyCode!
        : 'EUR';
      setOneriler(await api.itemSuggestions({ q: arama, language: 'tr', currency: paraBirimi, limit: 10 }));
    });

  /**
   * Çizilecek alanlar: önce düzenlenebilir olanlar (`TALEP_ALANLARI` sırasıyla), sonra `fieldMeta`
   * içinde olup bu ekranın düzeltemediği her alan. İkincisi **gizlenmiyor**: ayrıştırıcının
   * doldurduğu bir alanı satışçıdan saklamak, tam da bu ekranın düzeltmeye çalıştığı kusur.
   */
  const duzenlenebilirAlanlar = talep ? TALEP_ALANLARI.filter((alan) => alan.anahtar in talep.fieldMeta) : [];
  const girisCikisAraligi = duzenlenebilirAlanlar.map((alan) => alan.anahtar as string);
  const okunurAlanlar = talep ? Object.keys(talep.fieldMeta).filter((anahtar) => !TALEP_ALAN_ANAHTARLARI.has(anahtar)) : [];
  const eslesmeler = talep?.customerMatchSuggestions ?? [];

  return (
    <section className="talep-alma" data-testid="sales-request">
      <h3>{metin.baslik}</h3>
      <p className="muted">{metin.aciklama}</p>
      <Notices scope="sales-request" error={error} notice={notice} />

      <Alan etiket={metin.metinEtiketi} ipucu={metin.metinIpucu} zorunlu>
        <textarea
          data-testid="sales-request-raw"
          value={hamMetin}
          maxLength={4000}
          onChange={(event) => setHamMetin(event.target.value)}
        />
      </Alan>
      <button type="button" className="birincil" data-testid="sales-request-submit" disabled={talepSuruyor} onClick={talebiAl}>
        {metin.ayristir}
      </button>

      {talep ? (
        <>
          <p data-testid="sales-request-state">{metin.talepDurumu(talep.id.slice(0, 8), durumAdi(talep.status))}</p>

          {/*
            Ayrıştırmanın hâli. Arıza olan iki hâl (`FAILED`, `NOT_CONFIGURED`) uyarı kutusunda;
            `PARSED_EMPTY` bir arıza değil (müşteri gerçekten yazmamış) ama sebebi görünmek
            zorunda, `PARSED` ise onay kutusunda. Sebebin cümlesi uçtan geliyor, burada ikinci
            bir cümle uydurulmuyor.
          */}
          <p
            className={talep.parsingStatus === 'PARSED' ? 'callout callout-ok' : 'callout callout-warn'}
            role="status"
            data-testid="sales-request-parsing"
            data-durum={bekliyor ? 'PENDING' : talep.parsingStatus}
          >
            {bekliyor ? (
              <>
                <strong>{metin.ayristirmaDurumu.PENDING}</strong> {metin.ayristiriliyorAciklama}
              </>
            ) : (
              <>
                <strong>{metin.ayristirmaDurumu[talep.parsingStatus] ?? talep.parsingStatus}</strong>
                {talep.parsingReasonText ? ` ${talep.parsingReasonText}` : ''}
              </>
            )}
          </p>
          <p className="muted" data-testid="sales-request-parsing-meta">
            {metin.ayristirmaKunyesi(talep.parsingProvider, tamSayi(talep.parsingAttempts))}
            {talep.parsingErrorCode ? ` ${metin.ayristirmaHataKodu(talep.parsingErrorCode)}` : ''}
          </p>

          <MusteriBilgiKontrolu talep={talep} />

          <fieldset disabled={bekliyor} className="talep-bekleme-kilidi" data-testid="sales-request-fieldset">
          <h4>{metin.alanlarBasligi}</h4>
          <p className="muted">{metin.alanlarAciklamasi}</p>
          <div className="talep-alanlari">
            {duzenlenebilirAlanlar.map((alan) => {
              // Giriş ve çıkış bir aralık: iki ayrı tarih kutusu yerine tek aralık seçici, giriş
              // alanının yerinde. Biri talepte yoksa o alan tek başına eski kutusuyla kalıyor.
              const aralik = girisCikisAraligi.includes('checkIn') && girisCikisAraligi.includes('checkOut');
              if (aralik && alan.anahtar === 'checkOut') return null;
              if (aralik && alan.anahtar === 'checkIn') {
                const girisYazi = duzeltmeler.checkIn ?? talepAlanYazisi(talep, 'checkIn', 'tarih');
                const cikisYazi = duzeltmeler.checkOut ?? talepAlanYazisi(talep, 'checkOut', 'tarih');
                const dusukGiris = talep.fieldMeta.checkIn?.lowConfidence === true;
                const dusukCikis = talep.fieldMeta.checkOut?.lowConfidence === true;
                return (
                  <div
                    key="checkIn-checkOut"
                    className={dusukGiris || dusukCikis ? 'talep-alani talep-alani-dusuk' : 'talep-alani'}
                    data-testid="sales-request-field-checkIn"
                  >
                    <Alan etiket={metin.girisCikisEtiketi} ipucu={[talepGuvenCumlesi(talep.fieldMeta.checkIn), talepGuvenCumlesi(talep.fieldMeta.checkOut)].filter((parca): parca is string => parca !== undefined).join(' ') || undefined}>
                      <TarihAraligiSecici
                        testId="sales-request-input-dates"
                        giris={girisYazi}
                        cikis={cikisYazi}
                        setAralik={(giris, cikis) => setDuzeltmeler((mevcut) => ({ ...mevcut, checkIn: giris, checkOut: cikis }))}
                      />
                    </Alan>
                    {dusukGiris || dusukCikis ? (
                      <span className="state talep-dusuk" data-testid="sales-request-lowconfidence-checkIn">
                        {metin.dusukGuven}
                      </span>
                    ) : null}
                  </div>
                );
              }
              const meta = talep.fieldMeta[alan.anahtar];
              const dusuk = meta?.lowConfidence === true;
              const yazi = duzeltmeler[alan.anahtar] ?? talepAlanYazisi(talep, alan.anahtar, alan.kip);
              const yaz = (deger: string) => setDuzeltmeler((mevcut) => ({ ...mevcut, [alan.anahtar]: deger }));
              const ipucu = [
                talepGuvenCumlesi(meta),
                alan.kip === 'para' ? M.genel.tutarIpucu : undefined,
                alan.kip === 'sayiListesi' || alan.kip === 'metinListesi' ? metin.listeIpucu : undefined,
                alan.anahtar === 'budgetCurrencyCode' ? metin.paraBirimiIpucu : undefined,
                // Fiyat kutusunun eşiği bu alanda; boş bırakıldığında kutunun neden boş kaldığı
                // ancak burada yazılıysa anlaşılıyor.
                alan.anahtar === 'hotelName' ? metin.otelAdiIpucu : undefined,
                alan.anahtar === 'visaDurationDays' ? metin.visaSuresiIpucu : undefined,
              ]
                .filter((parca): parca is string => parca !== undefined)
                .join(' ');
              return (
                <div
                  key={alan.anahtar}
                  className={dusuk ? 'talep-alani talep-alani-dusuk' : 'talep-alani'}
                  data-testid={`sales-request-field-${alan.anahtar}`}
                >
                  <Alan etiket={metin.alanAdi[alan.anahtar] ?? alan.anahtar} ipucu={ipucu}>
                    {alan.kip === 'tarih' ? (
                      <input
                        type="date"
                        data-testid={`sales-request-input-${alan.anahtar}`}
                        value={yazi}
                        onChange={(event) => yaz(event.target.value)}
                      />
                    ) : (
                      <input
                        inputMode={alan.kip === 'tamsayi' ? 'numeric' : alan.kip === 'para' ? 'decimal' : 'text'}
                        maxLength={200}
                        data-testid={`sales-request-input-${alan.anahtar}`}
                        value={yazi}
                        onChange={(event) => yaz(event.target.value)}
                      />
                    )}
                  </Alan>
                  {dusuk ? (
                    <span className="state talep-dusuk" data-testid={`sales-request-lowconfidence-${alan.anahtar}`}>
                      {metin.dusukGuven}
                    </span>
                  ) : null}
                </div>
              );
            })}
            {okunurAlanlar.map((anahtar) => {
              const meta = talep.fieldMeta[anahtar];
              const dusuk = meta?.lowConfidence === true;
              return (
                <div
                  key={anahtar}
                  className={dusuk ? 'talep-alani talep-alani-dusuk' : 'talep-alani'}
                  data-testid={`sales-request-field-${anahtar}`}
                >
                  <span className="stat-label">{metin.alanAdi[anahtar] ?? anahtar}</span>
                  <p className="stat-value" data-testid={`sales-request-value-${anahtar}`}>
                    {talepOkunurYazi(talep, anahtar)}
                  </p>
                  <small className="alan-ipucu">{metin.duzenlenemez}</small>
                  {dusuk ? (
                    <span className="state talep-dusuk" data-testid={`sales-request-lowconfidence-${anahtar}`}>
                      {metin.dusukGuven}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
          <button type="button" className="ikincil" data-testid="sales-request-save-fields" onClick={duzeltmeleriKaydet}>
            {metin.alanlariKaydet}
          </button>

          <h4>{metin.fiyatBasligi}</h4>
          <p className="muted">{metin.fiyatAciklamasi}</p>
          {fiyat ? (
            <div className="stat-grid" data-testid="sales-request-price">
              {(
                [
                  ['paximum', metin.paximumBasligi, fiyat.paximum],
                  ['roompilot', metin.roompilotBasligi, fiyat.roompilot],
                ] as const
              ).map(([anahtar, baslik, gorunum]) => {
                const kaynak = talepFiyatKaynagi(gorunum);
                return (
                  <div className="stat" key={anahtar} data-durum={gorunum.status}>
                    <span className="stat-label">{baslik}</span>
                    <p className="stat-value" data-testid={`sales-request-price-${anahtar}`}>
                      {talepFiyatYazisi(gorunum)}
                    </p>
                    {kaynak ? (
                      <small className="alan-ipucu" data-testid={`sales-request-price-source-${anahtar}`}>
                        {kaynak}
                      </small>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : null}
          <button
            type="button"
            className="ikincil"
            data-testid="sales-request-price-refresh"
            onClick={() => void run(() => fiyatiOku(talep.id))}
          >
            {metin.fiyatiGetir}
          </button>

          <h4>{metin.onayBasligi}</h4>
          <p className="muted">{metin.onayAciklamasi}</p>
          {talep.customerId ? (
            <p data-testid="sales-request-matched-customer">
              {metin.eslesenMusteri(
                eslesmeler.find((aday) => aday.customerId === talep.customerId)?.displayName ??
                  customers.find((musteri) => musteri.id === talep.customerId)?.displayName ??
                  talep.customerId,
                metin.eslesmeKanali[eslesmeler.find((aday) => aday.customerId === talep.customerId)?.matchedOn ?? ''] ?? TANIMSIZ,
              )}
            </p>
          ) : (
            <>
              {eslesmeler.length === 0 ? (
                <p className="muted" data-testid="sales-request-no-match">
                  {metin.eslesmeYok}
                </p>
              ) : (
                <ul data-testid="sales-request-matches">
                  {eslesmeler.map((aday) => (
                    <li key={aday.customerId}>
                      <span>{metin.eslesenMusteri(aday.displayName, metin.eslesmeKanali[aday.matchedOn] ?? aday.matchedOn)}</span>
                      <button type="button" className="ikincil" onClick={() => setCustomerId(aday.customerId)}>
                        {metin.eslesmeSec}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <Alan etiket={ORTAK.musteri} zorunlu>
                <select
                  data-testid="sales-request-customer"
                  value={customerId}
                  onChange={(event) => setCustomerId(event.target.value)}
                >
                  <option value="">{M.satis.musteriSecin}</option>
                  {customers.map((musteri) => (
                    <option key={musteri.id} value={musteri.id}>
                      {musteri.displayName}
                    </option>
                  ))}
                </select>
              </Alan>
            </>
          )}
          <button
            type="button"
            className="birincil"
            data-testid="sales-request-confirm"
            disabled={talep.status !== 'DRAFT'}
            onClick={onayla}
          >
            {metin.onayla}
          </button>
          <button
            type="button"
            className="ikincil"
            data-testid="sales-request-create-opportunity"
            disabled={talep.status !== 'CONFIRMED'}
            onClick={firsatAc}
          >
            {metin.firsatOlustur}
          </button>

          <h4>{metin.oneriBasligi}</h4>
          <p className="muted">{metin.oneriAciklamasi}</p>
          <button type="button" className="ikincil" data-testid="sales-request-suggestions" onClick={onerileriGetir}>
            {metin.onerileriGetir}
          </button>
          {oneriler && oneriler.length === 0 ? (
            <div className="empty-state" data-testid="sales-request-suggestions-empty">
              <strong>{metin.oneriYok}</strong>
            </div>
          ) : null}
          {oneriler && oneriler.length > 0 ? (
            <>
              <div className="table-wrap">
                <table data-testid="sales-request-suggestion-rows">
                  <thead>
                    <tr>
                      <th>{metin.oneriSutun.ad}</th>
                      <th>{metin.oneriSutun.tur}</th>
                      <th>{metin.oneriSutun.fiyat}</th>
                      <th>{metin.oneriSutun.eylem}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {oneriler.map((oneri) => (
                      <tr key={oneri.productSlug} data-testid="sales-request-suggestion-row">
                        <td>{oneri.description}</td>
                        <td>{kalemTuru(oneri.itemType)}</td>
                        <td data-testid={`sales-request-suggestion-price-${oneri.productSlug}`}>
                          {para(oneri.referenceSalePriceMinor, oneri.referenceCurrencyCode)}
                        </td>
                        <td>
                          {/*
                            FR-25: maliyet satırı yalnız uç gerçekten gönderdiğinde çiziliyor.
                            `finance:read` taşımayan aktörde bu alanlar hiç dolmuyor ve burada
                            hiçbir şey yazılmıyor; "bilinmiyor" demek bile olmayan bir veriyi
                            varsaymak olurdu.
                          */}
                          {!oneri.costUnavailable && oneri.netPriceMinor !== null ? (
                            <small className="alan-ipucu" data-testid={`sales-request-suggestion-cost-${oneri.productSlug}`}>
                              {metin.oneriMaliyet(para(oneri.netPriceMinor, oneri.netCurrencyCode))}
                              {oneri.costMayBeStale ? ` ${metin.oneriMaliyetBayat}` : ''}
                            </small>
                          ) : null}
                          <button
                            type="button"
                            className="birincil"
                            data-testid={`sales-request-suggestion-add-${oneri.productSlug}`}
                            onClick={() => ekle(oneriUrunu(oneri))}
                          >
                            {M.satis.katalog.ekle}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="muted" data-testid="sales-request-suggestion-note">
                {metin.oneriMusaitlikNotu}
              </p>
            </>
          ) : null}
          </fieldset>
        </>
      ) : null}
    </section>
  );
}

function SalesPanel({ me, view, onView }: { me: Me; view: SalesView; onView: (view: SalesView) => void }) {
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const { error, notice, run } = useNotice();
  const { error: customerError, notice: customerNotice, run: runCustomer } = useNotice();
  const [customers, setCustomers] = useState<CustomerListItem[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [customerId, setCustomerId] = useState('');
  const [opportunityId, setOpportunityId] = useState('');
  const [, setQuoteId] = useState('');
  const [quote, setQuote] = useState<Quote | null>(null);
  const [order, setOrder] = useState<{ code: string; id: string; replay: boolean } | null>(null);
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [, setClosureId] = useState('');
  const [closure, setClosure] = useState<Order | null>(null);
  /**
   * Müşteri türü: CRM-A18 talep denetimi #45'in gerçek uçtaki karşılığı. `INDIVIDUAL` TC
   * Kimlik No, `COMPANY` vergi/ticaret sicil no istiyor — ikisi karışmasın diye tür seçimi
   * hangi alanların görüneceğini de belirliyor (`assertIdentity`, `customers.service.ts`).
   */
  const [customerType, setCustomerType] = useState<'INDIVIDUAL' | 'COMPANY'>('INDIVIDUAL');
  const [fullName, setFullName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [taxNumber, setTaxNumber] = useState('');
  const [nationalId, setNationalId] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [customerQuery, setCustomerQuery] = useState('');
  /** Seçili müşteri ilk 50 / arama listesinde değilse (yeni oluşturulan kişi) seçeneklere eklenir. */
  const [ekMusteri, setEkMusteri] = useState<CustomerListItem | null>(null);
  /**
   * 21.09.2026 incelemesi: ikisi de `createLead` gövdesinde sabitti. `EUR` sabitinin bedeli
   * olculdu: uretimde `Currency` tablosu bos oldugu icin her aday olusturma denemesi
   * "Unknown or inactive currency: EUR" ile bitiyordu ve ekranda secilecek bir alternatif yoktu.
   * Varsayilan USD: sirketin islem para birimi o, ve AED ona sabit kurla bagli.
   */
  const [leadCurrency, setLeadCurrency] = useState('USD');
  const [pipelineQuery, setPipelineQuery] = useState('');
  const [pipelineStage, setPipelineStage] = useState('');
  const [discountMinor, setDiscountMinor] = useState('0');
  const [discountMode, setDiscountMode] = useState<DiscountMode>('AMOUNT');
  const [taxChoice, setTaxChoice] = useState<TaxChoice>(VARSAYILAN_VERGI_KIPI);
  const [taxRate, setTaxRate] = useState(VARSAYILAN_VERGI_ORANI);
  const [validUntil, setValidUntil] = useState('');
  // Düzenlemede kaynak sürümden taşınan tam damga: kullanıcı günü değiştirmediyse aynen gönderilir.
  const [validUntilOrijinal, setValidUntilOrijinal] = useState<string | null>(null);
  const [internalNotes, setInternalNotes] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  const [items, setItems] = useState<QuoteItemInput[]>([]);
  const [suppliers, setSuppliers] = useState<{ id: string; code: string; name: string; kind: string }[]>([]);
  const [supplierError, setSupplierError] = useState(false);
  const loadSuppliers = useCallback(() => {
    setSupplierError(false);
    void api.supplierOptions().then(result => setSuppliers(result.items)).catch(() => setSupplierError(true));
  }, []);
  useEffect(loadSuppliers, [loadSuppliers]);
  const [creatingQuote, setCreatingQuote] = useState(false);
  const quoteInFlight = useRef(false);
  const [acceptVersion, setAcceptVersion] = useState('');
  /** Teklif reddi: form kapalı doğuyor, onay kutusu işaretlenmeden gönderilemiyor. */
  const [teklifRetAcik, setTeklifRetAcik] = useState(false);
  const [teklifRetGerekcesi, setTeklifRetGerekcesi] = useState('');
  const [teklifRetOnayi, setTeklifRetOnayi] = useState(false);
  /** Satış akışı vakası madde 13: yenilemenin nedeni verdiği okunur uyarılar (elle yazılmış/yenilenemeyen kalemler). */
  const [renewalWarnings, setRenewalWarnings] = useState<string[] | null>(null);
  /** Yenileme öncesi/sonrası toplam: eski fiyat taşınmıyor, satışçı neyin değiştiğini burada görüyor. */
  const [renewalDelta, setRenewalDelta] = useState<{ previousTotalMinor: number; totalMinor: number; currencyCode: string } | null>(null);
  const [paymentLink, setPaymentLink] = useState<PaymentLinkResult | null>(null);
  const [paymentBusy, setPaymentBusy] = useState(false);
  const paymentInFlight = useRef(false);
  /**
   * Vaka madde 9'un gönderim denemesi. `null` "henüz denenmedi" demek; bir sonuç geldiğinde
   * **her zaman** çiziliyor, `ok` false olsa bile. Reddin görünmemesi, sahte bir başarıdan
   * daha sessiz bir yalan olurdu.
   */
  const [quoteWhatsApp, setQuoteWhatsApp] = useState<QuoteWhatsAppResult | null>(null);
  /** Vaka madde 6-7: tek mesaj (fotoğraf, aktivite listesi), ödeme bağlantısı üretmeden. */
  const [whatsappTelefon, setWhatsappTelefon] = useState('');
  /**
   * Açık teklifin müşterisinin kayıtlı telefonu, kendi ucundan. Paneldeki `customers` listesi en yeni
   * 50 kayıtla sınırlı; müşteri sayısı büyüyünce (11.09.2026, PayPartner aktarımı) teklifin müşterisi
   * o listede olmayabiliyor ve WhatsApp numarası boş kalıyordu.
   */
  const [teklifMusteriTelefonu, setTeklifMusteriTelefonu] = useState<string | null>(null);
  const [whatsappAmac, setWhatsappAmac] = useState<WhatsAppAmaci>('ACTIVITIES_PDF');
  const [whatsappMetin, setWhatsappMetin] = useState('');
  const [whatsappSonuc, setWhatsappSonuc] = useState<WhatsAppSendResult | null>(null);

  const idempotencyKey = useMemo(() => `a2-${Math.random().toString(36).slice(2)}${Date.now()}`, []);

  const musteriSecenek = musteriSecenekleri(customers, ekMusteri, customerId);
  useEffect(() => {
    if (!customerId || customers.some(musteri => musteri.id === customerId) || ekMusteri?.id === customerId) return;
    let iptal = false;
    void api.customer(customerId).then(detay => {
      if (!iptal) setEkMusteri({ id: detay.id, displayName: detay.displayName, type: detay.type, statusCode: detay.statusCode, email: detay.email, phone: detay.phone, countryCode: detay.countryCode });
    }).catch(() => undefined);
    return () => { iptal = true; };
  }, [customerId, customers, ekMusteri]);

  const reloadSequence = useRef(0);
  const reload = useCallback(async () => {
    const sequence = ++reloadSequence.current;
    setLoading(true);
    try {
    // Aranmıyorsa en yeni 50 müşteri; aranıyorsa sunucu tarafında filtrelenmiş 20 sonuç.
    // 5.631 müşterinin tamamını tarayıcıya indirip orada süzmek bu ekranı kilitlerdi.
    const [customerPage, opportunityPage, quotePage, orderPage] = await Promise.all([
      musteriAra(customerQuery, { liste: api.customers, ara: api.searchCustomers }),
      api.opportunities({ q: pipelineQuery, stage: pipelineStage }),
      api.quotes(),
      api.orders(),
    ]);
    if (sequence !== reloadSequence.current) return;
    setCustomers(customerPage.items);
    setOpportunities(opportunityPage.items);
    setQuotes(quotePage.items);
    setOrders(orderPage.items);
    setLoadFailed(false);
    } catch (caught) { if (sequence === reloadSequence.current) setLoadFailed(true); throw caught;
    } finally { if (sequence === reloadSequence.current) setLoading(false); }
  }, [customerQuery, pipelineQuery, pipelineStage]);

  useEffect(() => {
    const timer = window.setTimeout(() => void run(() => reload()), 180);
    return () => window.clearTimeout(timer);
  }, [reload, run]);

  const refreshQuote = useCallback(
    async (id: string) => {
      const loaded = await api.quote(id);
      setQuote(loaded);
      setQuoteId(loaded.id);
      setTeklifMusteriTelefonu(null);
      void api.customer(loaded.customer.id).then((musteri) => setTeklifMusteriTelefonu(musteri.phone ?? '')).catch(() => setTeklifMusteriTelefonu(null));
      setAcceptVersion(String(loaded.currentVersionNo));
      // Başka bir teklife geçmek ya da aynı tekliften yeniden okumak, önceki yenileme/ödeme
      // bağlantısı sonucunu geride bırakıyor: ekranda eski bir sürümün uyarısı asılı kalmasın.
      setRenewalWarnings(null);
      setRenewalDelta(null);
      setPaymentLink(null);
      // Gönderim sonuçları da geride bırakılıyor: başka bir teklifin reddi ya da başarısı
      // ekranda asılı kalırsa satışçı onu seçili teklifin sonucu sanır.
      setQuoteWhatsApp(null);
      setWhatsappSonuc(null);
      setWhatsappTelefon('');
      setWhatsappMetin('');
      // Ret formu da kapanıyor: bir teklif için işaretlenmiş onay kutusu, başka bir teklif
      // seçildiğinde işaretli kalırsa onay verilmemiş bir ret bir tık uzağa düşerdi.
      setTeklifRetAcik(false);
      setTeklifRetGerekcesi('');
      setTeklifRetOnayi(false);
      await reload();
      return loaded;
    },
    [reload],
  );

  // Sipariş satırı her kapanış adımından sonra yeniden okunuyor: düğme, istediği geçişin uçta
  // gerçekten olduğu durum olduğunu varsaymıyor.
  const refreshClosure = useCallback(async (id: string) => {
    setClosureId(id);
    if (!id) {
      setClosure(null);
      return null;
    }
    const loaded = await api.order(id);
    setClosure(loaded);
    return loaded;
  }, []);

  const closureStep = (status: string) =>
    void run(async () => {
      if (!closure) throw new Error(M.genel.siparisSecinUyari);
      const updated = await api.transitionOrder(closure.id, { status, expectedVersion: closure.version });
      await refreshClosure(closure.id);
      await reload();
      return M.satis.siparisGecti(updated.code, durumAdi(updated.status));
    });

  const setItem = (index: number, patch: Partial<QuoteItemInput>) =>
    setItems((current) => {
      const next = current.map((item, position) => (position === index ? { ...item, ...patch } : item));
      // Kişi sayısı değişince ekspres adedi eşitlenir; ekspres satırına elle yazılan adet ezilmez.
      const kisiyiEtkiler = patch.quantity !== undefined || patch.itemType !== undefined || patch.description !== undefined;
      const degisen = next[index];
      return kisiyiEtkiler && degisen && !isExpressLine(degisen) ? syncExpressQuantities(next) : next;
    });

  /**
   * Kalem para alanlarının **yazıldığı hâli**.
   *
   * Sayı ile metin ayrı tutuluyor, çünkü her tuşta alt birime çevirip geri yazmak `250,`
   * yazmayı imkânsız kılardı: virgül basıldığı anda `250,00`a dönüşür ve imleç kaybolurdu.
   * Metin ekranda duruyor, sayıya çevrilme anı **gönderim anı** (`fiyatlandir`), ve o an
   * geçersiz bir tutar sessizce sıfırlanmıyor, gönderimi durduruyor.
   */
  const [kalemMetni, setKalemMetni] = useState<Record<string, string>>({});
  const [ozelPaketNotu, setOzelPaketNotu] = useState<string | null>(null);
  const [revision, setRevision] = useState<{ quote: Quote; sourceId: string; restore: () => void } | null>(null);
  useEffect(() => {
    if (revision && view !== 'new') { revision.restore(); setRevision(null); }
  }, [view, revision]);
  const editQuote = (selected: Quote | null = quote) => {
    if (!selected || ['ACCEPTED', 'CANCELLED'].includes(selected.status)) return;
    const source = selected.versions.find(version => version.versionNo === selected.currentVersionNo);
    if (!source) return;
    const restore = () => {
      setCustomerId(customerId); setOpportunityId(opportunityId); setLeadCurrency(leadCurrency);
      setItems(items); setKalemMetni(kalemMetni); setDiscountMinor(discountMinor); setDiscountMode(discountMode);
      setValidUntil(validUntil); setValidUntilOrijinal(null); setInternalNotes(internalNotes); setCustomerNotes(customerNotes);
      setTaxChoice(taxChoice); setTaxRate(taxRate);
    };
    const loadedItems: QuoteItemInput[] = source.items.map(item => ({
      itemType: item.itemType, description: item.description, supplierName: item.supplierName,
      hotelSelection: item.hotelSelection ?? undefined, productSlug: item.productSlug ?? undefined, salesProductId: item.salesProductId ?? undefined, quantity: item.quantity, unitPriceMinor: item.unitPriceMinor,
      travellerName: item.travellerName ?? undefined, serviceDate: item.serviceDate?.slice(0, 10),
      taxMinor: item.taxMinor ?? 0, feeMinor: item.feeMinor ?? 0, discountMinor: item.discountMinor ?? 0, governmentFeeMinor: item.governmentFeeMinor ?? 0,
    }));
    setRevision({ quote: selected, sourceId: source.id, restore });
    setCustomerId(selected.customer.id); setOpportunityId(''); setLeadCurrency(selected.currencyCode);
    setItems(loadedItems); setKalemMetni({ ...revisionTaxFields(loadedItems, source.discountMinor), ...kayitliKalemVergiAlanlari(source.items, bps => altBirimYazi(bps)) }); setDiscountMinor(altBirimYazi(source.discountMinor)); setDiscountMode('AMOUNT');
    // Sürüm düzeyi vergi modu varsa aynen yüklenir; yoksa eski kalem bazlı yol.
    setTaxChoice(kayitliVergiKipi(source.taxMode)); setTaxRate(String((source.taxRateBps ?? DEFAULT_TAX_RATE_BPS) / 100));
    // Eski sürümün tarihi yalnız hâlâ gelecekteyse taşınır; geçmişteyse boş kalır, yeni sürüm 10 gün alır.
    { const tasinan = tasinacakGecerlilik(source.validUntil, source.validUntilManual); setValidUntilOrijinal(tasinan); setValidUntil(tasinan ? dubaiGunu(tasinan) : ''); }
    const notes = /^(?:İÇ NOT: )([\s\S]*?)(?:\\n|\n)MÜŞTERİ NOTU: ([\s\S]*)$/.exec(source.notes ?? '');
    setInternalNotes(notes ? notes[1]! : source.notes ?? ''); setCustomerNotes(notes?.[2] ?? '');
    setQuote(null); onView('new');
  };

  const fiyatAnahtari = (index: number, alan: ParaAlani) => `${index}:${alan}`;
  const fiyatMetni = (index: number, minor: number, alan: ParaAlani = 'unitPriceMinor') =>
    kalemMetni[fiyatAnahtari(index, alan)] ?? altBirimYazi(minor);
  const setFiyat = (index: number, alan: ParaAlani, ham: string) => {
    setKalemMetni((current) => ({ ...current, [fiyatAnahtari(index, alan)]: ham }));
    try {
      setItem(index, { [alan]: ham.trim() === '' ? 0 : paraAltBirime(ham) });
    } catch {
      // Yarım yazılmış bir tutar (`250,`) hata değil; sayı yalnız okunabilir olduğunda yazılıyor.
    }
  };
  /**
   * Gönderim anında bütün kalem tutarları **yazılan metinden** yeniden okunuyor.
   *
   * Ekrandaki metin ile gönderilen sayının ayrışabileceği tek an bu; ayrışmasına izin vermek
   * yerine metin tek yetkili kaynak yapıldı. Okunamayan bir tutar burada hata fırlatıyor ve
   * `run()` onu ekrana basıyor: yanlış bir fiyatla oluşmuş bir teklifi geri almak, bir alanı
   * yeniden yazmaktan çok daha pahalı.
   */
  const changeQuoteCurrency = (currency: string) => {
    if (currency === leadCurrency || opportunityId || revision) return;
    setLeadCurrency(currency);
    setDiscountMinor('0');
    setItems(current => current.map(item => ({ ...item, hotelSelection: undefined, unitPriceMinor: 0, taxMinor: 0, feeMinor: 0, discountMinor: 0 })));
    setKalemMetni(current => Object.fromEntries(items.flatMap((_item, index) => [
      [`${index}:unitPriceMinor`, ''], [`${index}:feeMinor`, '0'], [`${index}:discountMinor`, '0'],
      [`${index}:taxRate`, current[`${index}:taxRate`] ?? '0'], [`${index}:discountMode`, current[`${index}:discountMode`] ?? 'AMOUNT'],
    ])));
  };

  const fiyatlandir = () => priceQuoteDraft(items, kalemMetni, {
    discount: discountMinor, discountMode, fee: '0',
  }, { choice: taxChoice, rate: taxRate });
  /** Sunucuya giden vergi alanları; eski yolda hiçbiri gönderilmez. */
  const vergiAlanlari = (pricing: ReturnType<typeof fiyatlandir>) => pricing.tax ? { taxMode: pricing.tax.taxMode, taxRateBps: pricing.tax.taxRateBps } : {};

  let draftKirilim: ReturnType<typeof taslakKirilimi> | null = null;
  let draftPricingError = '';
  const ozelPaketEksik = ozelPaketFiyatsizSatirlar(items, kalemMetni);
  if (items.length && ozelPaketEksik.length) {
    draftPricingError = S.ozelPaketFiyatYok(ozelPaketEksik.join(', '));
  } else if (items.length) {
    try { draftKirilim = taslakKirilimi(fiyatlandir()); } catch (caught) { draftPricingError = caught instanceof Error ? caught.message : S.invalidAdjustment; }
  }

  // Ürün kartının fiyat bloğundaki KDV bilgisi, belgedeki KDV ile aynı hesaptan (itemTaxInfos).
  let kalemKdv: ItemTaxInfo[] = [];
  if (items.length && !ozelPaketEksik.length) {
    try { kalemKdv = itemTaxInfos(items, kalemMetni, { discount: discountMinor, discountMode, fee: '0' }, { choice: taxChoice, rate: taxRate }); } catch { kalemKdv = []; }
  }

  /**
   * Katalogdan gelen ürünü teklif kalemine çevirir.
   *
   * Kalem **sona** ekleniyor ve fiyatın yazılı hâli (`kalemMetni`) de aynı anda tohumlanıyor:
   * ikisi ayrı düşerse ekranda katalog fiyatı, gönderimde sıfır olurdu.
   *
   * `kind` eşleşmiyorsa tür `OTHER` oluyor ve ekranda "Diğer" yazıyor. Eşleşmeyen bir türü
   * rastgele bir kaleme benzetmek, yanlış bir kalem türüyle açılan bir operasyon görevi demek;
   * "Diğer" görünür bir eksiklik, uydurulmuş bir tür görünmez bir yanlış.
   */
  const katalogdanEkle = (urun: Product) => {
    if (!revision && items.length === 0 && !opportunityId && urun.fromPrice) setLeadCurrency(urun.fromPrice.currency);
    if ((revision || items.length > 0 || opportunityId) && urun.fromPrice && urun.fromPrice.currency !== leadCurrency) {
      void run(async () => { throw new Error(S.currencyMismatch); });
      return;
    }
    const tur = (ITEM_TYPES as readonly string[]).includes(urun.kind) ? urun.kind : 'OTHER';
    const birimFiyat = urun.fromPrice?.amount ?? 0;
    const sira = items.length;
    setItems((current) => syncExpressQuantities([
      ...current,
      {
        itemType: tur,
        description: urun.name,
        // Tedarikçi yalnız gerçekten bilindiğinde yazılıyor: uçak ürününde havayolu tedarikçidir.
        supplierName: urun.airline ?? '',
        /**
         * Katalog bağı. Ürünün adı ve fiyatı kopyalanıyordu, **kimliği** kopyalanmıyordu; o
         * yüzden katalogdan gelen bir kalem veritabanında elle yazılmıştan ayırt edilemiyordu.
         * Bağ o an yazılmazsa bir daha bulunamaz: "hangi ürün ne kadar satıldı" sorusunun
         * cevabı sonradan kurulamaz.
         */
        productSlug: urun.sourceProductSlug ?? urun.slug,
        quantity: 1,
        unitPriceMinor: birimFiyat,
      },
    ]));
    setKalemMetni((current) => ({ ...current, [`${sira}:unitPriceMinor`]: altBirimYazi(birimFiyat) }));
    void run(async () =>
      urun.airline
        ? M.satis.katalog.eklendiTedarikciliyle(urun.name, urun.airline)
        : urun.fromPrice
          ? M.satis.katalog.eklendi(urun.name)
          : `${M.satis.katalog.eklendi(urun.name)} ${M.satis.katalog.fiyatYok}`,
    );
  };

  /**
   * Özel paket (Ürün Sahibi, 04.10.2026): "ürün gelsin, özel fiyatı olsun, her seferinde fiyat girsin."
   * Kalem CRM'deki sabit ürüne bağlı gelir; fiyat alanı **boş** gelir ve kayıttan önce doldurulmak
   * zorunda (`assertOzelPaketFiyatli`). Açıklama düzenlenebilir ve faturaya o açıklama gider.
   * Aynı teklife birden fazla eklenebilir.
   */
  const ozelPaketEkle = () => {
    const index = items.length;
    setItems(current => [...current, { itemType: OZEL_PAKET.itemType, description: OZEL_PAKET.varsayilanAciklama, supplierName: '', quantity: 1, unitPriceMinor: 0, salesProductId: OZEL_PAKET.id }]);
    setKalemMetni(current => ({ ...current, [`${index}:unitPriceMinor`]: '' }));
    setOzelPaketNotu(S.ozelPaketEklendi);
  };

  const savedProductAdd = (product: SalesProduct) => {
    // Özel paket listeden seçilse de düğmeyle aynı: kayıtlı fiyatı ve para birimi kullanılmıyor.
    if (product.id === OZEL_PAKET.id) { ozelPaketEkle(); return; }
    const index = items.length;
    // Para birimi farklıysa fiyat dönüştürülmez (kur uydurulmaz): fiyat alanı boş gelir, satışçı elle girer.
    const ayniPara = product.currencyCode === leadCurrency;
    setItems(current => syncExpressQuantities([...current, { itemType: product.itemType, description: product.description ? `${product.name} · ${product.description}` : product.name, supplierName: product.supplierName ?? '', quantity: 1, unitPriceMinor: ayniPara ? product.unitPriceMinor : 0, salesProductId: product.id }]));
    setKalemMetni(current => ({ ...current, [`${index}:unitPriceMinor`]: ayniPara ? altBirimYazi(product.unitPriceMinor) : '', [`${index}:taxRate`]: altBirimYazi(product.taxRateBps) }));
  };

  /**
   * Ödeme bağlantısı düğmesinin **öngörü** kısmı. Uç TRY'yi ve yayımlanmamış sürümü 409/kural
   * hatasıyla reddediyor (`payment-link.service.ts`); satışçının düğmeye basıp sessiz bir hatayla
   * karşılaşmaması için o iki kuralın okunur hâli burada, basmadan önce görünüyor. Karma sepet
   * artık bir ret değil: Ürün Sahibi kararı (25.08.2026) doğrudan tahsil, bu yüzden karma sepet
   * bilgilendirici bir not olarak gösteriliyor, "üretilemeyecek" demiyor. Uçtaki karar tek doğru
   * kaynak kalıyor — hesaplama karma sepet/otel ayrımı gibi kararı verilmiş, sabit bir kuralın
   * tekrarı (`classifyBasket`, `payment-link.service.ts`), tahmini değil.
   */
  const guncelSurum = quote?.versions.find((entry) => entry.versionNo === quote.currentVersionNo) ?? null;
  const guncelKalemTurleri = guncelSurum?.items.map((entry) => entry.itemType) ?? [];
  const karmaSepet = guncelKalemTurleri.includes('HOTEL') && guncelKalemTurleri.some((tur) => tur !== 'HOTEL');
  const tryReddi = quote?.currencyCode === 'TRY';
  const odemeBaglantisiIcinYayimGerekli = guncelSurum ? guncelSurum.status !== 'PUBLISHED' : false;

  const preparePaymentLink = async () => {
    if (!quote || paymentInFlight.current) return;
    paymentInFlight.current = true;
    setPaymentBusy(true);
    try {
      const result = await prepareQuotePayment(quote, api, async (published) => {
        setQuote(published);
        await refreshQuote(published.id);
      });
      setPaymentLink(result);
      return result.alreadyExisted ? M.satis.baglantiZatenVardi : M.satis.baglantiUretildi;
    } finally {
      paymentInFlight.current = false;
      setPaymentBusy(false);
    }
  };

  /**
   * Müşteriye giden adres.
   *
   * Jeton yalnız yayımlama anında yazılıyor, yani taslakta yok, ve o durumda ölü bir bağlantı
   * çizilmiyor, sebebi yazılıyor. WhatsApp cevabı geldiyse **sunucunun** ürettiği mutlak adres
   * (`proforma.url`) panelin türettiğinin yerine geçiyor: müşteriye gidecek kökeni sunucunun
   * `PUBLIC_QUOTE_BASE_URL`i belirliyor ve panel o değişkeni hiçbir uçtan göremiyor.
   */
  const musteriBaglantisi = publicQuoteLinks(guncelSurum?.publicViewToken);
  const sunucununProformaAdresi = quoteWhatsApp?.proforma.attached === true ? quoteWhatsApp.proforma.url : null;
  const gosterilecekPdfAdresi = sunucununProformaAdresi ?? musteriBaglantisi?.pdfUrl ?? null;

  /**
   * Serbest gönderim numarası. `customers` zaten yüklü olduğu için ikinci bir istek açılmıyor;
   * müşteri listede yoksa (arama listeyi daraltmış olabilir) alan boş kalıyor ve satışçı kendi
   * yazıyor. Bir numara **uydurulmuyor**.
   *
   * Vaka madde 9'un tuşu bu alanı okumuyor: orada numarayı sunucu müşteri kaydından alıyor
   * (`quote-whatsapp.service.ts`), ve kayıtta numara yoksa `CUSTOMER_PHONE_MISSING` diyor.
   */
  const musteriKayitliTelefon = quote ? (teklifMusteriTelefonu ?? customers.find((entry) => entry.id === quote.customer.id)?.phone ?? '') : '';
  const whatsappNumarasi = whatsappTelefon === '' ? musteriKayitliTelefon : whatsappTelefon;

  /** Sunucu bir sürümü yalnız `PUBLISHED`/`ACCEPTED` iken müşteriye çiziyor (`loadCustomerSafeVersion`). */
  const musteriyeAcik = (durum: string) => durum === 'PUBLISHED' || durum === 'ACCEPTED';

  /** Yayımla düğmesi yalnız geçerli sürüm taslakken açık; pasifken nedeni ekranda görünür yazılır. */
  const yayimDurumu = quote?.versions.find((entry) => entry.versionNo === quote.currentVersionNo)?.status ?? '';

  return (
    <section id="sales-workflow" data-testid="sales-panel">

      <Notices scope="sales" error={error} notice={notice} />

      {quote ? <details className="sales-disclosure sales-odeme" data-testid="sales-payment">
        <summary>{S.paymentLinkTitle}</summary>
            {odemeBaglantisiIcinYayimGerekli ? (
              <p className="callout callout-warn" role="status" data-testid="sales-payment-unpublished">
                {M.satis.yalnizYayimliSurumUyarisi}
              </p>
            ) : null}
            <button
              type="button"
              data-testid="sales-payment-create-link"
              disabled={paymentBusy || tryReddi || !guncelSurum || !['DRAFT', 'PUBLISHED'].includes(guncelSurum.status)}
              onClick={() => void run(preparePaymentLink)}
            >
              {guncelSurum?.status === 'DRAFT' ? S.publishAndPayment : M.satis.odemeBaglantisiUret}
            </button>
            {paymentLink ? (
              <p data-testid="sales-payment-link-result">
                {paymentLink.alreadyExisted ? <span className="state">{M.satis.baglantiYinelemeRozeti}</span> : null}
                <KopyalanirDeger deger={paymentLink.paymentUrl} testId="sales-payment-link-url" />
              </p>
            ) : null}
      </details> : null}

      {view === 'overview' ? <SalesBoard opportunities={opportunities} quotes={quotes} query={pipelineQuery} stage={pipelineStage} setQuery={setPipelineQuery} setStage={setPipelineStage} open={(entry) => { setOpportunityId(entry.id); setCustomerId(entry.customer.id); setLeadCurrency(entry.currencyCode); onView('new'); }} reload={() => void run(reload)} loading={loading} failed={loadFailed} /> : null}
      <div hidden={view !== 'new'} className="sales-builder sales-composer">
        {revision ? <div className="callout" data-testid="quote-editing"><strong>{S.editingQuote(revision.quote.code)}</strong><p>{S.revisionHint}</p><button type="button" onClick={() => { const original = revision.quote; revision.restore(); setRevision(null); setQuote(original); onView('quotes'); }}>{S.cancelEdit}</button></div> : null}
        <div className="sales-builder-layout sales-composer-layout"><div className="sales-composer-main">
          <div className="sales-composer-section" data-testid="sales-customer-section"><h3>{S.steps[0]}</h3>
            <div className="alan-satiri" hidden={!!revision}>
        <MusteriArama
          etiket={S.customerSearch}
          ipucu={M.satis.musteriAramaIpucu}
          sorgu={customerQuery}
          setSorgu={setCustomerQuery}
          musteriler={customers}
          sec={(id) => { if (!revision) { setCustomerId(id); setOpportunityId(''); } }}
          testId="sales-customer-search"
        />
        <Alan etiket={S.existing} zorunlu><select disabled={!!revision} data-testid="sales-customer" value={customerId} onChange={(e) => { setCustomerId(e.target.value); setOpportunityId(''); }}><option value="">{M.satis.musteriSecin}</option>{musteriSecenek.map((c) => <option key={c.id} value={c.id}>{c.displayName}</option>)}</select></Alan>
      </div>
            {revision ? <p>{revision.quote.customer.displayName}</p> : null}<details hidden={!!revision} className="sales-disclosure sales-customer-form" data-testid="sales-new-customer"><summary>{S.newCustomer}</summary>
      <AlanSatiri>
        <Alan etiket={M.satis.turEtiketi} zorunlu>
          <select
            data-testid="customer-type"
            value={customerType}
            onChange={(event) => setCustomerType(event.target.value as 'INDIVIDUAL' | 'COMPANY')}
          >
            <option value="INDIVIDUAL">{musteriTuru('INDIVIDUAL')}</option>
            <option value="COMPANY">{musteriTuru('COMPANY')}</option>
          </select>
        </Alan>
        {customerType === 'INDIVIDUAL' ? (
          <>
            <Alan etiket={S.fullName} ipucu={S.fullNameHint} zorunlu>
              <input data-testid="customer-full-name" autoComplete="name" value={fullName} maxLength={161} onChange={(event) => setFullName(event.target.value)} />
            </Alan>
            {/*
              CRM-A18 bireysel müşteride TC Kimlik No'yu tanımlıyordu; satış akışı vakası
              (26.08.2026, Ürün Sahibi: "TC numarası vermeyebilir, zorunlu yapmasın") kaydı
              kayıt anında zorunlu olmaktan çıkardı. Kural silinmedi, B2C fatura kesimine
              taşındı (`finance/invoice.readiness.ts`) — `assertIdentity`
              (`customers.service.ts`) artık bunu istemiyor, o yüzden `zorunlu` işareti kalktı.
            */}
          </>
        ) : (
          <>
            <Alan etiket={M.satis.kurumsalAdEtiketi} zorunlu>
              <input data-testid="customer-company-name" value={companyName} maxLength={160} onChange={(event) => setCompanyName(event.target.value)} />
            </Alan>
            <Alan etiket={M.satis.vergiNoEtiketi} zorunlu>
              <input data-testid="customer-tax-number" value={taxNumber} maxLength={40} onChange={(event) => setTaxNumber(event.target.value)} />
            </Alan>
          </>
        )}
        <Alan etiket={M.satis.epostaEtiketi}>
          <input data-testid="customer-email" type="email" autoComplete="email" value={email} maxLength={160} onChange={(event) => setEmail(event.target.value)} />
        </Alan>
        <Alan etiket={M.satis.telefonEtiketi}>
          <input data-testid="customer-phone" type="tel" autoComplete="tel" value={phone} maxLength={32} onChange={(event) => setPhone(event.target.value)} />
        </Alan>
        <Alan etiket={M.satis.adresEtiketi} ipucu={M.satis.adresIpucu}>
          <input data-testid="customer-billing-address" autoComplete="street-address" value={billingAddress} maxLength={140} onChange={(event) => setBillingAddress(event.target.value)} />
        </Alan>
      </AlanSatiri>
      {customerType === 'INDIVIDUAL' ? <details className="sales-identity-optional"><summary>{S.identityOptional}</summary><Alan etiket={M.satis.tcKimlikEtiketi} ipucu={M.satis.tcKimlikIpucu}><input data-testid="customer-national-id" value={nationalId} maxLength={11} inputMode="numeric" onChange={(event) => setNationalId(event.target.value.replace(/\D/g, ''))} /></Alan></details> : null}
      <Notices scope="sales-customer-form" error={customerError} notice={customerNotice} />
      <button
        type="button"
        data-testid="create-customer"
        className="birincil"
        onClick={() =>
          void runCustomer(async () => {
            const parts = fullName.trim().split(/\s+/u);
            const lastName = parts.length > 1 ? parts.pop()! : '';
            const firstName = parts.join(' ');
            if (customerType === 'INDIVIDUAL' && (!firstName || !lastName || firstName.length > 80 || lastName.length > 80)) throw new Error(S.fullNameError);
            const created = await api.createCustomer({
              type: customerType,
              ...(customerType === 'INDIVIDUAL' ? { firstName, lastName, nationalId: nationalId || undefined } : { companyName, taxNumber }),
              email: email || undefined,
              phone: phone || undefined,
              billingAddress: billingAddress.trim() || undefined,
              // 04.10.2026: sabit 'ACTIVE' üretimde tabloda yoktu ve form düşüyordu. Kod gönderilmiyor;
              // sunucu tablodaki varsayılan durumu (isDefault) kullanıyor.
            });
            await reload();
            // Yeni kişi ilk 50 kayıtta olmayabilir: seçenek ve arama kutusu da doldurulur, seçim boş görünmez.
            setEkMusteri(created);
            setCustomerId(created.id);
            setCustomerQuery(created.displayName);
            setOpportunityId('');
            // Tek yolcu satırı varsa (adet 1) yolcu adı boşsa yeni kişiyle dolar; çoklu satırlara dokunulmaz.
            setItems(current => {
              const tekKisi = current.filter(item => item.itemType === 'VISA' && !isExpressLine(item));
              const [aday] = tekKisi;
              return tekKisi.length === 1 && aday && aday.quantity === 1 && !aday.travellerName?.trim()
                ? current.map(item => (item === aday ? { ...item, travellerName: created.displayName } : item))
                : current;
            });
            return M.satis.musteriOlusturuldu(created.displayName);
          })
        }
      >
        {M.satis.musteriOlustur}
      </button>

</details>
            <details hidden={!!revision} className="sales-disclosure" data-testid="sales-intake"><summary>{S.intake}</summary>      {/*
        Vakanın 2-5. maddeleri, kayıt ile fırsat arasında: müşteri kaydedildikten sonra talebi
        almak, ondan sonra fırsat açmak. Bileşen paneldeki durumu paylaşıyor (müşteri listesi,
        seçili müşteri, kalem ekleme yolu) — kendi kopyalarını tutsaydı iki bölüm iki ayrı
        müşteriyi ve iki ayrı teklifi gösterebilirdi.
      */}
      <TalepAlmaBolumu
        currency={revision || items.length || opportunityId ? leadCurrency : undefined}
        me={me}
        customers={customers}
        customerId={customerId}
        setCustomerId={(id) => {
          // Talepten ya da öneriden gelen müşteri değişimi açık fırsatı bırakır (fırsat müşteriye aittir).
          if (revision) return;
          setOpportunityId(opportunityAfterCustomerChange(customerId, id, opportunityId));
          setCustomerId(id);
        }}
        ekle={katalogdanEkle}
        firsataBagla={async (id, currencyCode) => {
          setLeadCurrency(currencyCode);
          setOpportunityId(id);
          await reload();
        }}
      />

</details>
          </div>
          <div className="sales-composer-section" data-testid="sales-items-section"><h3>{S.steps[1]}</h3><div className="sales-composer-toolbar"><KatalogSecici ekle={katalogdanEkle} currency={revision || items.length || opportunityId ? leadCurrency : undefined} /><SavedProductPicker currency={leadCurrency} onAdd={savedProductAdd} /><button type="button" className="ikincil" data-testid="add-ozel-paket" onClick={ozelPaketEkle}>{S.ozelPaket}</button></div>{ozelPaketNotu ? <p className="muted" role="status" data-testid="ozel-paket-notu">{ozelPaketNotu}</p> : null}{supplierError ? <p role="alert">{S.supplierLoadError}<button type="button" onClick={loadSuppliers}>{S.refresh}</button></p> : null}
      {items.length === 0 ? <div className="sales-empty"><strong>{S.emptyItems}</strong><p>{S.emptyItemsHint}</p></div> : null}
      {/*
        Para alanları artık **normal para biçiminde**: `250,00` yazılıyor, uca `25000` gidiyor.
        Çeviren tek yer `paraAltBirime` ve tam sayı aritmetiği kullanıyor; gerekçesi orada.
        Alanın kendi metni ekranda tutuluyor (`String` değil, kullanıcının yazdığı dize):
        yazarken her tuşta alt birime çevirip geri yazmak "250," yazmayı imkânsız kılardı.
      */}
      {items.map((item, index) => (
        <fieldset key={index} className="kalem sales-composer-item" data-testid={`quote-item-${index}`}>
          <legend>{M.satis.kalemBasligi(index + 1)}</legend>
          {item.productSlug ? (
            <>
              <p className="kalem-katalog" data-testid={`quote-item-catalogue-${index}`}>
                <span className="state">{M.satis.kalemKatalogRozeti(item.productSlug)}</span>
                <button
                  type="button"
                  className="ikincil"
                  data-testid={`quote-item-detach-${index}`}
                  onClick={() => {
                    setItem(index, { productSlug: undefined, hotelSelection: undefined });
                    void run(async () => M.satis.kalemKatalogKaldirildi);
                  }}
                >
                  {M.satis.kalemKatalogKaldir}
                </button>
              </p>
              {item.itemType === 'HOTEL' ? <HotelOfferPicker slug={item.productSlug} currency={leadCurrency} initialSelection={item.hotelSelection} onInvalidate={() => {
                setItem(index, { hotelSelection: undefined, unitPriceMinor: 0 });
                setKalemMetni(current => ({ ...current, [`${index}:unitPriceMinor`]: '' }));
              }} onSelect={selection => {
                const description = `${item.description.split(' · ')[0]} · ${selection.roomTypes.join(' / ')} · ${selection.board}`.slice(0, 200);
                setItem(index, { hotelSelection: selection, description, quantity: 1, serviceDate: selection.checkIn, unitPriceMinor: selection.unitPriceMinor, taxMinor: 0, feeMinor: 0, discountMinor: 0 });
                setKalemMetni(current => ({ ...current, [`${index}:unitPriceMinor`]: altBirimYazi(selection.unitPriceMinor), [`${index}:taxRate`]: '0', [`${index}:discountMinor`]: '0', [`${index}:feeMinor`]: '0' }));
              }} /> : null}
              {/* 03.10.2026: otel kaleminde de fiyat önerisi (Booking, alış, önerilen satış). PR #45 oda
                  seçiciyi eklerken bu bölümü otelde kaldırmıştı; ikisi ayrı sorulara cevap veriyor. */}
              <details className="sales-item-options"><summary>{S.priceSuggestion}</summary><FiyatOnerisiSatiri slug={item.productSlug} serviceDate={item.hotelSelection?.checkIn ?? item.serviceDate} checkOut={item.hotelSelection?.checkOut} customerId={customerId || undefined} /></details>
              {item.hotelSelection ? <p className="muted" data-testid={`hotel-selection-${index}`}>{item.hotelSelection.roomTypes.join(' / ')} · {item.hotelSelection.board} · {item.hotelSelection.checkIn} / {item.hotelSelection.checkOut}</p> : null}
            </>
          ) : null}
          <div className="sales-item-primary"><AlanSatiri>
            <Alan className="sales-item-type" etiket={M.satis.kalemTuruEtiketi} zorunlu>
              <select data-testid={`quote-item-type-${index}`} value={item.itemType} onChange={(event) => setItem(index, { itemType: event.target.value, hotelSelection: undefined })}>{ITEM_TYPES.map((type) => <option key={type} value={type}>{kalemTuru(type)}</option>)}</select>
            </Alan>
            <Alan className="sales-item-description" etiket={M.satis.kalemAciklamasiEtiketi} zorunlu>
              <input
                data-testid={`quote-item-description-${index}`}
                value={item.description}
                maxLength={200}
                onChange={(event) => setItem(index, { description: event.target.value })}
              />
            </Alan>
            <Alan className="sales-item-supplier" etiket={M.satis.kalemTedarikcisiEtiketi} ipucu={S.supplierLater}>
              <select data-testid={`quote-item-supplier-${index}`} value={item.supplierName} onChange={(event) => setItem(index, { supplierName: event.target.value })}>
                <option value="">{S.supplierPending}</option>
                {item.supplierName && !suppliers.some(supplier => supplier.name === item.supplierName) ? <option value={item.supplierName} disabled>{item.supplierName}</option> : null}
                {suppliers.filter(supplier => item.itemType === 'VISA' ? supplier.kind === 'VISA' : item.itemType === 'HOTEL' ? supplier.kind === 'HOTEL' : item.itemType === 'TOUR' ? supplier.kind === 'TOUR' : true).map(supplier => <option key={supplier.id} value={supplier.name}>{supplier.name}</option>)}
              </select>

            </Alan>
            <Alan className="sales-item-quantity" etiket={M.satis.kalemAdediEtiketi} zorunlu>
              <input
                data-testid={`quote-item-quantity-${index}`}
                readOnly={!!item.hotelSelection}
                inputMode="numeric"
                value={String(item.quantity)}
                onChange={(event) => setItem(index, { quantity: Number.parseInt(event.target.value || '0', 10) })}
              />
            </Alan>
            <Alan className="sales-item-currency" etiket={M.satis.paraBirimiEtiketi} ipucu={S.currencyForQuote} zorunlu>
              <select data-testid={`quote-item-currency-${index}`} value={leadCurrency} disabled={!!opportunityId || !!revision} onChange={(event) => changeQuoteCurrency(event.target.value)}>{SATIS_PARA_BIRIMLERI.map(code => <option key={code} value={code}>{code}</option>)}</select>
            </Alan>
            <Alan className="sales-item-price" etiket={M.satis.kalemBirimFiyatiEtiketi} ipucu={M.genel.tutarIpucu} zorunlu>
              <input
                data-testid={`quote-item-unit-price-${index}`}
                inputMode="decimal"
                value={fiyatMetni(index, item.unitPriceMinor)}
                onChange={(event) => setFiyat(index, 'unitPriceMinor', event.target.value)}
              />
            </Alan>
            <VergiSecici className="sales-item-taxmode" testId={`quote-item-tax-mode-${index}`} deger={taxChoice === 'LEGACY' ? 'LEGACY' : (kalemMetni[kalemKipAnahtari(index)] as TaxChoice | undefined) ?? taxChoice}
              sec={(kip) => { if (taxChoice === 'LEGACY') { setTaxChoice(kip); setKalemMetni(tumSatirlaraUygula); } else setKalemMetni((current) => kalemKipiniAyarla(current, index, kip as Exclude<TaxChoice, 'LEGACY'>, taxChoice)); }} />
            </AlanSatiri>{kalemKdv[index] ? <p className="sales-item-tax muted" data-testid={`quote-item-tax-line-${index}`}>{S.itemTaxLine(kalemKdv[index]!.mode, kalemKdv[index]!.rateBps, para(kalemKdv[index]!.taxMinor, leadCurrency), kalemKdv[index]!.governmentFeeMinor > 0 ? para(kalemKdv[index]!.governmentFeeMinor, leadCurrency) : undefined)}</p> : null}</div><details className="sales-item-options"><summary>{S.itemOptions}</summary><AlanSatiri>
            <Alan etiket={M.satis.yolcuEtiketi}>
              <input data-testid={`quote-item-traveller-${index}`} type="text" value={item.travellerName ?? ''} onChange={(event) => setItem(index, { travellerName: event.target.value })} />
            </Alan>
            <Alan etiket={M.satis.hizmetTarihiEtiketi}>
              <TarihSaatSecici testId={`quote-item-service-date-${index}`} saatli={false} deger={item.serviceDate?.slice(0, 10) ?? ''} setDeger={(value) => setItem(index, { serviceDate: value || undefined })} />
            </Alan>
            {taxChoice !== 'LEGACY' && ((kalemMetni[kalemKipAnahtari(index)] as TaxChoice | undefined) ?? taxChoice) !== 'NONE' ? <Alan etiket={S.taxRate} ipucu={S.taxChoiceItemHint}>
              <input data-testid={`quote-item-tax-${index}`} inputMode="decimal" value={kalemMetni[kalemOranAnahtari(index)] ?? taxRate} onChange={(event) => { const deger = event.target.value; setKalemMetni((current) => ({ ...current, [kalemOranAnahtari(index)]: deger })); }} />
            </Alan> : null}
            {taxChoice === 'LEGACY' ? <Alan etiket={S.taxPercentage} ipucu={S.taxBaseHint}>
              <input data-testid={`quote-item-tax-${index}`} inputMode="decimal" placeholder={revision && item.taxMinor ? S.savedTax(para(item.taxMinor, leadCurrency)) : undefined} value={kalemMetni[`${index}:taxRate`] ?? (revision && item.taxMinor ? '' : '0')} onChange={(event) => setKalemMetni((current) => ({ ...current, [`${index}:taxRate`]: event.target.value }))} />
            </Alan> : null}
            <Alan etiket={M.satis.kalemUcretiEtiketi} ipucu={M.genel.tutarIpucu}>
              <input data-testid={`quote-item-fee-${index}`} inputMode="decimal" value={fiyatMetni(index, item.feeMinor ?? 0, 'feeMinor')} onChange={(event) => setFiyat(index, 'feeMinor', event.target.value)} />
            </Alan>
            {item.itemType === 'VISA' ? <Alan etiket={S.governmentFee} ipucu={S.governmentFeeHint}>
              <input data-testid={`quote-item-government-fee-${index}`} inputMode="decimal" value={fiyatMetni(index, item.governmentFeeMinor ?? 0, 'governmentFeeMinor')} onChange={(event) => setFiyat(index, 'governmentFeeMinor', event.target.value)} />
            </Alan> : null}
            <Alan etiket={S.discountMode}>
              <select data-testid={`quote-item-discount-mode-${index}`} value={kalemMetni[`${index}:discountMode`] ?? 'AMOUNT'} onChange={(event) => setKalemMetni((current) => ({ ...current, [`${index}:discountMode`]: event.target.value, [`${index}:discountMinor`]: '0' }))}>
                <option value="AMOUNT">{S.amountMode}</option><option value="PERCENT">{S.percentMode}</option>
              </select>
            </Alan>
            <Alan etiket={M.satis.kalemIndirimiEtiketi} ipucu={kalemMetni[`${index}:discountMode`] === 'PERCENT' ? S.percentHint : M.genel.tutarIpucu}>
              <input data-testid={`quote-item-discount-${index}`} inputMode="decimal" value={fiyatMetni(index, item.discountMinor ?? 0, 'discountMinor')} onChange={(event) => setFiyat(index, 'discountMinor', event.target.value)} />
            </Alan>
          </AlanSatiri></details>
          <button type="button" className="ikincil" data-testid={`quote-item-remove-${index}`} onClick={() => {
            setItems((current) => syncExpressQuantities(current.filter((_, position) => position !== index)));
            setKalemMetni((current) => Object.fromEntries(Object.entries(current).filter(([key]) => Number(key.split(':')[0]) !== index).map(([key, value]) => { const [position, field] = key.split(':'); return [`${Number(position) > index ? Number(position) - 1 : position}:${field}`, value]; })));
          }}>{M.satis.kalemiSil}</button>
        </fieldset>
      ))}
      <button type="button" className="ikincil" data-testid="add-quote-item" onClick={() => setItems((current) => [...current, { itemType: 'TRANSFER', description: '', supplierName: '', quantity: 1, unitPriceMinor: 0 }])}>{M.satis.kalemEkle}</button>
</div>
          <div className="sales-composer-section" data-testid="sales-details-section"><h3>{S.quoteDetails}</h3>
      <div className="alan-satiri sales-discount-validity">
        <Alan etiket={S.discountMode}>
          <select data-testid="quote-discount-mode" value={discountMode} onChange={(event) => { setDiscountMode(event.target.value as DiscountMode); setDiscountMinor('0'); }}>
            <option value="AMOUNT">{S.amountMode}</option><option value="PERCENT">{S.percentMode}</option>
          </select>
        </Alan>
        <Alan etiket={M.satis.teklifIndirimiEtiketi} ipucu={discountMode === 'PERCENT' ? S.percentHint : M.genel.tutarIpucu}>
          <input data-testid="quote-discount" inputMode="decimal" value={discountMinor} onChange={(event) => setDiscountMinor(event.target.value)} />
        </Alan>

        <Alan etiket={M.satis.teklifGecerlilikEtiketi} ipucu={gecerlilikIpucu(gecerlilikBitisi(validUntil, validUntilOrijinal, dubaiGunu))}>
          <TarihSaatSecici testId="quote-valid-until" saatli={false} deger={validUntil} setDeger={setValidUntil} />
        </Alan>
      </div>
      <fieldset className="sales-tax-default" data-testid="quote-tax-section">
        <legend>{S.taxDefaultTitle}</legend>
        <div className="sales-tax-default-satir">
          <Alan etiket={S.taxDefaultKind}>
            <select data-testid="quote-tax-mode" value={taxChoice} onChange={(event) => { const kip = event.target.value as TaxChoice; setTaxChoice(kip); if (taxChoice === 'LEGACY') setKalemMetni(tumSatirlaraUygula); }}>
              {taxChoice === 'LEGACY' ? <option value="LEGACY">{S.taxChoiceLegacyOption}</option> : null}
              <option value="INCLUSIVE">{S.taxChoiceInclusive}</option>
              <option value="EXCLUSIVE">{S.taxChoiceExclusive}</option>
              <option value="NONE">{S.taxChoiceNone}</option>
            </select>
          </Alan>
          {taxChoice === 'EXCLUSIVE' || taxChoice === 'INCLUSIVE' ? (
            <Alan etiket={S.taxDefaultRate}>
              <input data-testid="quote-tax-rate" inputMode="decimal" value={taxRate} onChange={(event) => setTaxRate(event.target.value)} />
            </Alan>
          ) : null}
          {taxChoice !== 'LEGACY' ? <button type="button" className="ikincil" data-testid="quote-tax-apply-all" onClick={() => setKalemMetni(tumSatirlaraUygula)}>{S.taxApplyAll}</button> : null}
        </div>
        {taxChoice === 'LEGACY' ? <small className="alan-ipucu" data-testid="quote-tax-legacy-note">{S.taxChoiceLegacy}</small> : null}
        {taxChoice !== 'LEGACY' && satirlarFarkli(kalemMetni) ? <small className="alan-ipucu" data-testid="quote-tax-rows-differ">{S.taxRowsDiffer}</small> : null}
      </fieldset>
      <AlanSatiri>
        <Alan etiket={M.satis.icNotlarEtiketi}>
          <textarea data-testid="quote-internal-notes" value={internalNotes} onChange={(event) => setInternalNotes(event.target.value)} />
        </Alan>
        <Alan etiket={M.satis.musteriNotlariEtiketi}>
          <textarea data-testid="quote-customer-notes" value={customerNotes} onChange={(event) => setCustomerNotes(event.target.value)} />
        </Alan>
      </AlanSatiri>



          </div>
        </div><aside className="sales-summary sales-composer-summary"><span className="eyebrow">{S.summary}</span><h3>{revision?.quote.customer.displayName ?? musteriSecenek.find((c) => c.id === customerId)?.displayName ?? S.existing}</h3><p>{S.itemCount(items.length)}</p>
          {draftKirilim ? <TutarKirilimiListesi kirilim={draftKirilim} para={(minor) => para(minor, leadCurrency)} testId="sales-draft-totals" /> : <p role="status">{draftPricingError || S.emptyItems}</p>}
          <small>{S.totalsHint}</small><div className="sales-composer-actions">
      <button
        type="button"
        data-testid="create-quote"
        disabled={!customerId || items.length === 0 || creatingQuote || !!draftPricingError}
        onClick={() =>
          void run(async () => {
            if (quoteInFlight.current) return;
            quoteInFlight.current = true;
            setCreatingQuote(true);
            try {
            assertKalemlerDolu(items);
            const pricing = fiyatlandir();
            assertOzelPaketFiyatli(pricing.items);
            if (revision) {
              const updated = await api.addQuoteVersion(revision.quote.id, { expectedQuoteVersion: revision.quote.version, sourceQuoteVersionId: revision.sourceId, items: pricing.items, discountMinor: pricing.discountMinor, ...vergiAlanlari(pricing), validUntil: gonderilecekGecerlilik(validUntil, validUntilOrijinal, dubaiGunu), notes: M.satis.notBirlestir(internalNotes, customerNotes) });
              revision.restore(); setRevision(null);
              await refreshQuote(updated.id); onView('quotes');
              return M.satis.surumEklendi(tamSayi(updated.currentVersionNo));
            }
            let saleId = opportunityId;
            if (!saleId) {
              const sale = await api.createOpportunity({ customerId, title: firsatBasligi(items[0]?.description ?? '', musteriSecenek.find((entry) => entry.id === customerId)?.displayName), currencyCode: leadCurrency, expectedValueMinor: pricing.tax ? pricing.tax.totalMinor : pricing.items.reduce((sum, item) => sum + item.quantity * item.unitPriceMinor + (item.taxMinor ?? 0) + (item.feeMinor ?? 0) - (item.discountMinor ?? 0), 0) - pricing.discountMinor, ownerId: me.userId });
              saleId = sale.id;
              setOpportunityId(sale.id);
            }
            const created = await api.createQuote({ opportunityId: saleId, items: pricing.items, discountMinor: pricing.discountMinor, ...vergiAlanlari(pricing), validUntil: gonderilecekGecerlilik(validUntil, validUntilOrijinal, dubaiGunu), notes: M.satis.notBirlestir(internalNotes, customerNotes) });
            await refreshQuote(created.id);
            onView('quotes');
            return M.satis.teklifOlusturuldu(created.code, tamSayi(created.currentVersionNo));
            } finally { quoteInFlight.current = false; setCreatingQuote(false); }
          })
        }
      >
        {revision ? S.saveRevision : S.saveQuote}
      </button>
          {!customerId ? <p>{S.noCustomer}</p> : null}{customerId && !items.length ? <p>{S.noItems}</p> : null}</div><hr /><small>{S.draft}</small><details><summary>{S.source}</summary><p data-testid="pricing-mode">{M.satis.fiyatlamaKipi}</p></details></aside></div>
      </div>
      <div hidden={view !== 'quotes'} className="sales-detail-panel sales-record-panel">
      <SalesRecords kind="quotes" onDownload={(id) => run(async () => { await api.downloadQuoteDocument(id); return S.documentDownloaded; })} onCopyPaymentLink={(id) => run(async () => {
        // Bağlantı yoksa üretilir: uç idempotent, aynı sürüme ikinci bağlantı açmaz (`createOrGet`).
        const quote = quotes.find((item) => item.id === id);
        const version = quote?.versions.find((item) => item.versionNo === quote.currentVersionNo) ?? quote?.versions[0];
        if (!quote || !version) throw new Error(S.paymentLinkCopyFailed);
        // 13.10.2026: süresi dolmuş teklifin bağlantısı kopyalanmaz. Bu yalnız ekran kolaylığıdır;
        // asıl kapı sunucuda (`createOrGet`, sunucu saati) ve var olan bağlantıyı da reddeder.
        if (teklifSuresiDolduMu(version.validUntil)) throw new Error(S.paymentLinkExpired);
        const url = version.paymentLink?.paymentUrl ?? (await api.createPaymentLink(quote.id, version.versionNo)).paymentUrl;
        if (!navigator.clipboard) throw new Error(S.paymentLinkCopyFailed);
        await navigator.clipboard.writeText(url).catch(() => { throw new Error(S.paymentLinkCopyFailed); });
        // Yeni üretilen bağlantı satıra yazılsın; liste okunamazsa kopyalama yine başarılı.
        if (!version.paymentLink) void reload().catch(() => undefined);
        return S.paymentLinkCopied;
      })} rows={quotes.map(quoteRecord)} loading={loading} failed={loadFailed} onEdit={(id) => void run(async () => { editQuote(await api.quote(id)); })} onOpen={(id) => void run(() => refreshQuote(id))} />
      {quote && view === 'quotes' ? (
        <SalesDetailDrawer title={quote.code} onClose={() => setQuote(null)}>
        <Notices scope="sales-quote-drawer" error={error} notice={notice} />
        <div data-testid="quote-detail">
          <div className="sales-quote-detail-heading"><div><strong data-testid="quote-code">{quote.code}</strong><p>{quote.customer.displayName}</p></div><span className="sales-pill" data-testid="quote-status">{durumAdi(quote.status)}</span></div>
          <div className="sales-next-step"><span>{S.nextAction}</span><strong>{quoteRecord(quote).next}</strong></div>
          <button type="button" className="birincil" data-testid="edit-quote" disabled={['ACCEPTED', 'CANCELLED'].includes(quote.status)} onClick={() => editQuote()}>{S.editQuote}</button>
          {['ACCEPTED', 'CANCELLED'].includes(quote.status) ? <p>{S.editLocked}</p> : null}
          <QuoteCollection key={`${quote.id}-${quote.currentVersionNo}`} quote={quote} canRecord={me.permissions.includes('finance:write')} onQuote={async updated => { await refreshQuote(updated.id); }} bankAccounts={<BankaHesaplari />} invoice={quote.order ? <QuoteInvoicePanel key={quote.order.id} me={me} orderId={quote.order.id} /> : undefined} />
          <div className="sales-quote-version-info"><span>{S.currentVersion}: <strong data-testid="quote-current-version">{tamSayi(quote.currentVersionNo)}</strong></span><span>{S.acceptedVersion}: <strong data-testid="quote-accepted-version">{tamSayi(quote.acceptedVersionNo)}</strong></span></div>
          {quote.versions.filter((version) => version.versionNo === quote.currentVersionNo).map((version) => <section className="sales-detail-section" key={version.id}>
            <h3>{S.quoteContents}</h3>
            <div className="sales-line-items">{version.items.map((item) => <div key={item.id}><div><strong>{item.description}</strong><small>{item.supplierName} · {tamSayi(item.quantity)} × {para(item.unitPriceMinor, item.currencyCode)}</small></div><strong>{para(item.lineTotalMinor, item.currencyCode)}</strong></div>)}</div>
            <TutarKirilimiListesi kirilim={buildTutarKirilimi({ ...version, items: version.items.map(kalemKirilimi) })} para={(minor) => para(minor, quote.currencyCode)} testId="quote-version-totals" />
          </section>)}
          <details className="sales-disclosure" data-testid="quote-versions"><summary>{S.versionHistory}</summary>
          <div className="table-wrap">
            <table data-testid="quote-versions">
              <thead><tr>{S.versionColumns.map((label) => <th key={label}>{label}</th>)}</tr></thead>
              <tbody>
                {quote.versions.map((version) => (
                  <tr key={version.id} data-testid={`quote-version-${version.versionNo}`}>
                    <th scope="row">{tamSayi(version.versionNo)}</th>
                    <td data-testid={`quote-version-status-${version.versionNo}`}>{durumAdi(version.status)}</td>
                    <td data-testid={`quote-version-total-${version.versionNo}`}>{para(version.totalMinor, quote.currencyCode)}</td>
                    {/*
                      FR-27 yukarı yuvarlaması **görünür**. Ölçüldü 28.08.2026: uç ara toplamı
                      1999, toplamı 2000 ve farkı 1 olarak dönüyordu; ekran yalnız 2000'i
                      yazıyordu. Satışçı 19,99 yazıp 20,00 gören ve sebebini hiçbir yerde
                      bulamayan kişiydi. Fark sıfırsa hücre boş: her satıra "0,00" yazmak
                      kuralın istisnasını değil kendisini gürültüye çevirirdi.
                    */}
                    <td data-testid={`quote-version-rounding-${version.versionNo}`}>
                      {version.roundingAdjustmentMinor
                        ? M.satis.yuvarlamaFarki(para(version.roundingAdjustmentMinor, quote.currencyCode))
                        : ''}
                    </td>
                    <td data-testid={`quote-version-items-${version.versionNo}`}>{tamSayi(version.items.length)}</td>
                    <td>{version.pricingMode}</td>
                    {/*
                      FR-40 proformanın kendisi. Uç 22.08'den beri `application/pdf` dönüyordu ve
                      hiçbir ekran çağırmıyordu (ölçüldü 29.08.2026): satışçı müşteriye
                      göndereceği belgeyi panelden alamıyordu.

                      Düğme her sürüm satırında, çünkü satışçının indirmek istediği sürüm her
                      zaman geçerli sürüm değil (müşteri "geçen haftaki teklifi tekrar yolla"
                      diyor). Taslak sürümde düğme **gizlenmiyor**, edilgen çiziliyor ve
                      gerekçesini `title` ile taşıyor: görev kartlarındaki aynı kural.
                    */}
                    <td>
                      <button
                        type="button"
                        className="ikincil"
                        data-testid={`quote-version-pdf-${version.versionNo}`}
                        disabled={!musteriyeAcik(version.status)}
                        title={musteriyeAcik(version.status) ? M.satis.proformaSurumIcin(tamSayi(version.versionNo)) : M.satis.proformaYayimSarti}
                        onClick={() =>
                          void run(async () => {
                            await api.downloadQuotePdf(quote.id, version.versionNo, quote.code);
                            return M.satis.proformaIndirildi(tamSayi(version.versionNo));
                          })
                        }
                      >
                        {M.satis.proformaIndir}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </details>
          <section className="sales-detail-section sales-accept-actions"><h3>{S.quoteActions}</h3>
          <button
            type="button"
            data-testid="publish-version"
            disabled={yayimDurumu !== 'DRAFT'}
            aria-describedby={yayimDurumu !== 'DRAFT' ? 'publish-version-neden' : undefined}
            onClick={() =>
              void run(async () => {
                const updated = await api.publishQuote(quote.id, { versionNo: quote.currentVersionNo, expectedQuoteVersion: quote.version });
                await refreshQuote(updated.id);
                return M.satis.surumYayimlandi(tamSayi(updated.currentVersionNo));
              })
            }
          >
            {M.satis.surumYayimla}
          </button>
          {yayimDurumu !== 'DRAFT' ? <p id="publish-version-neden" className="sales-publish-reason" data-testid="publish-version-reason">{M.satis.surumYayimlanamaz(yayimDurumu, durumAdi(yayimDurumu))}</p> : null}
          <Alan etiket={M.satis.kabulSurumuEtiketi} zorunlu>
            <input data-testid="accept-version-no" inputMode="numeric" value={acceptVersion} onChange={(event) => setAcceptVersion(event.target.value)} />
          </Alan>
          <button
            type="button"
            data-testid="accept-quote"
            onClick={() =>
              void run(async () => {
                const accepted = await api.acceptQuote(quote.id, {
                  versionNo: parseTamSayi(acceptVersion),
                  expectedQuoteVersion: quote.version,
                  idempotencyKey: `${idempotencyKey}-accept`,
                });
                await refreshQuote(quote.id);
                return accepted.idempotentReplay
                  ? M.satis.kabulYinelendi(tamSayi(accepted.acceptedVersionNo))
                  : M.satis.surumKabulEdildi(tamSayi(accepted.acceptedVersionNo));
              })
            }
          >
            {M.satis.surumuKabulEt}
          </button>
          </section>

          {/*
            Kabulün karşı yarısı. Ölçülen kusur: kabul düğmesi vardı, ret düğmesi yoktu ve
            kaybedilen bir teklif panelde sonsuza kadar açık duruyordu.

            Form kapalı doğuyor ve iki şey istiyor: gerekçe (uca gidiyor, denetim izinde okunuyor)
            ve ne olacağını yazan bir uyarının içindeki onay kutusu. Desen mükerrer müşteri
            kaydındakiyle aynı (`HizliMusteriEkle`, `App.tsx`), gerekçesi de aynı: kapanan teklif
            panelden yeniden açılamıyor.
          */}
          <details className="sales-disclosure" data-testid="quote-renewal-reject"><summary>{S.renewalAndReject}</summary>
          {!teklifRetAcik ? (
            <button type="button" className="ikincil" data-testid="reject-quote" onClick={() => setTeklifRetAcik(true)}>
              {M.satis.teklifRet.reddet}
            </button>
          ) : (
            <div data-testid="reject-quote-form">
              <div className="callout" role="alert">
                <div>
                  <p>{M.satis.teklifRet.onayCumlesi(quote.code, tamSayi(quote.currentVersionNo))}</p>
                  <label>
                    <input
                      type="checkbox"
                      data-testid="reject-quote-ack"
                      checked={teklifRetOnayi}
                      onChange={(event) => setTeklifRetOnayi(event.target.checked)}
                    />{' '}
                    {M.satis.teklifRet.onayEtiketi}
                  </label>
                </div>
              </div>
              <AlanSatiri>
                <Alan etiket={M.satis.teklifRet.gerekceEtiketi} ipucu={M.satis.teklifRet.gerekceIpucu} zorunlu>
                  <input
                    data-testid="reject-quote-reason"
                    value={teklifRetGerekcesi}
                    maxLength={240}
                    onChange={(event) => setTeklifRetGerekcesi(event.target.value)}
                  />
                </Alan>
              </AlanSatiri>
              <button
                type="button"
                className="ikincil"
                data-testid="reject-quote-confirm"
                disabled={!teklifRetOnayi}
                title={teklifRetOnayi ? M.satis.teklifRet.reddet : M.satis.teklifRet.onayUyarisi}
                onClick={() =>
                  void run(async () => {
                    if (teklifRetGerekcesi.trim().length < 3) throw new Error(M.satis.teklifRet.gerekceUyarisi);
                    const kapanan = await api.rejectQuote(quote.id, {
                      versionNo: quote.currentVersionNo,
                      expectedQuoteVersion: quote.version,
                      reason: teklifRetGerekcesi.trim(),
                    });
                    setTeklifRetAcik(false);
                    setTeklifRetGerekcesi('');
                    setTeklifRetOnayi(false);
                    await refreshQuote(kapanan.id);
                    await reload();
                    return M.satis.teklifRet.reddedildi(kapanan.code);
                  })
                }
              >
                {M.satis.teklifRet.reddet}
              </button>
              <button type="button" className="ikincil" data-testid="reject-quote-cancel" onClick={() => setTeklifRetAcik(false)}>
                {M.satis.teklifRet.vazgec}
              </button>
            </div>
          )}

          {/*
            Satış akışı vakası madde 11-13: sepet ödenmeden geçerlilik (10 gün) dolarsa teklif süresini
            yitiriyor ama sepet kayıtlı kalıyor. "Teklifi yenile" **eski fiyatı taşımıyor**, her
            kalemi yeniden sorguluyor (`WorkflowService.renewQuote`) — bu yüzden sonucu
            gösteren iki alan var: toplam değişti mi (satışçının müşteriye söyleyeceği rakam), ve
            hangi kalemlerin elle kontrol gerektirdiği (katalog bağı olmayan/yenilenemeyen).
          */}
          <button
            type="button"
            className="ikincil"
            data-testid="renew-quote"
            onClick={() =>
              void run(async () => {
                const oncekiSurum = quote.versions.find((entry) => entry.versionNo === quote.currentVersionNo);
                const yenilenen = await api.renewQuote(quote.id, { expectedQuoteVersion: quote.version });
                setQuote(yenilenen);
                setQuoteId(yenilenen.id);
                setAcceptVersion(String(yenilenen.currentVersionNo));
                setPaymentLink(null);
                const yeniSurum = yenilenen.versions.find((entry) => entry.versionNo === yenilenen.currentVersionNo);
                setRenewalDelta(
                  oncekiSurum && yeniSurum
                    ? { previousTotalMinor: oncekiSurum.totalMinor, totalMinor: yeniSurum.totalMinor, currencyCode: yenilenen.currencyCode }
                    : null,
                );
                setRenewalWarnings(yenilenen.renewalWarnings);
                await reload();
                return M.satis.teklifYenilendi(tamSayi(yenilenen.currentVersionNo));
              })
            }
          >
            {M.satis.teklifiYenile}
          </button>
          {renewalDelta ? (
            <p
              className={`callout ${renewalDelta.totalMinor === renewalDelta.previousTotalMinor ? 'callout-ok' : 'callout-warn'}`}
              role="status"
              data-testid="renewal-price-delta"
            >
              {renewalDelta.totalMinor === renewalDelta.previousTotalMinor
                ? M.satis.fiyatDegismedi(para(renewalDelta.totalMinor, renewalDelta.currencyCode))
                : M.satis.fiyatDegisti(
                    para(renewalDelta.previousTotalMinor, renewalDelta.currencyCode),
                    para(renewalDelta.totalMinor, renewalDelta.currencyCode),
                  )}
            </p>
          ) : null}
          {renewalWarnings && renewalWarnings.length > 0 ? (
            <div data-testid="renewal-warnings">
              <p className="muted">{M.satis.yenilemeUyarilariBasligi}</p>
              <ul>
                {renewalWarnings.map((uyari, index) => (
                  <li key={index} data-testid={`renewal-warning-${index}`}>
                    {uyari}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/*
            Satış akışı vakası madde 9: proforma ve MamoPay ödeme bağlantısı müşteriye gider.
            Düğme her zaman görünüyor; karma sepet, TRY ve yayımlanmamış sürüm için **basmadan
            önce** açıklama var, sessiz bir 409 beklenmiyor. Uçtaki `ApiError.code` yine de tek
            doğru kaynak: buradaki üç uyarı bir öngörü, ikinci bir karar mekanizması değil.
          */}
          </details>
          <details className="sales-disclosure" data-testid="quote-communications"><summary>{S.communications}</summary>
          <h3>{M.satis.odemeBaglantisiBasligi}</h3>
          {karmaSepet ? (
            <p className="callout callout-ok" role="status" data-testid="payment-link-mixed-basket-notice">
              {M.satis.karmaSepetBilgisi}
            </p>
          ) : null}
          {tryReddi ? (
            <p className="callout callout-warn" role="status" data-testid="payment-link-try-warning">
              {M.satis.tryUyarisi}
            </p>
          ) : null}
          {odemeBaglantisiIcinYayimGerekli ? (
            <p className="callout callout-warn" role="status" data-testid="payment-link-unpublished-warning">
              {M.satis.yalnizYayimliSurumUyarisi}
            </p>
          ) : null}
          <button
            type="button"
            data-testid="create-payment-link"
            disabled={paymentBusy || tryReddi || !guncelSurum || !['DRAFT', 'PUBLISHED'].includes(guncelSurum.status)}
              onClick={() => void run(preparePaymentLink)}
          >
            {guncelSurum?.status === 'DRAFT' ? S.publishAndPayment : M.satis.odemeBaglantisiUret}
          </button>
          {paymentLink ? (
            <p data-testid="payment-link-result">
              {paymentLink.alreadyExisted ? <span className="state">{M.satis.baglantiYinelemeRozeti}</span> : null}
              <span data-testid="payment-link-url">{paymentLink.paymentUrl}</span>
              <span data-testid="payment-link-hold-and-charge-later">
                {paymentLink.holdAndChargeLater ? M.genel.evet : M.genel.hayir}
              </span>
            </p>
          ) : null}

          {/*
            FR-41 müşteri bağlantısı. Oturumsuz iki uç (`/public/quotes/:token` ve `.../pdf`)
            22.08'den beri açıktı ve panelde hiçbir yerden görünmüyordu; satışçı müşteriye
            verecek adresi hiçbir ekrandan alamıyordu.

            Jeton yoksa **bağlantı çizilmiyor**: ölü bir adres vermek, adres vermemekten kötü.
          */}
          <h3>{M.satis.musteriBaglantisiBasligi}</h3>
          {musteriBaglantisi ? (
            <div data-testid="public-quote-link">
              <p className="muted">{M.satis.musteriBaglantisiAcikla}</p>
              {/*
                Adres önce **okunur metin** olarak duruyor: pano yazma izni verilmemiş bir
                tarayıcıda kopyalama düğmesi çalışmayabilir, adresin kendisi her zaman görünür
                ve seçilebilir kalmalı.
              */}
              <p className="uzun-deger" data-veri data-testid="public-quote-url">{musteriBaglantisi.viewUrl}</p>
              {sunucununProformaAdresi ? (
                <p className="callout callout-ok" role="status" data-testid="public-quote-url-from-server">
                  {M.satis.musteriBaglantisiSunucudan}
                </p>
              ) : null}
              {/*
                İki adres cümlenin içinde akan bağlantı (`.link-button`, `display: inline`):
                WCAG 2.5.8'in kendi istisnası ve bu depoda zaten kullanılan desen. Asıl dokunma
                hedefi aşağıdaki kopyalama düğmesi, o tam boyutunda.
              */}
              <p>
                <a href={musteriBaglantisi.viewUrl} className="link-button" target="_blank" rel="noreferrer" data-testid="public-quote-open">
                  {M.satis.musteriBaglantisiAc}
                </a>
                {gosterilecekPdfAdresi ? (
                  <>
                    {' · '}
                    <a href={gosterilecekPdfAdresi} className="link-button" target="_blank" rel="noreferrer" data-testid="public-quote-pdf-open">
                      {M.satis.musteriBaglantisiPdfAc}
                    </a>
                  </>
                ) : null}
              </p>
              <button
                type="button"
                className="ikincil"
                data-testid="public-quote-copy"
                onClick={() =>
                  void run(async () => {
                    // Pano yazımı başarısız olursa `run()` gerçek hatayı ekrana basıyor;
                    // "kopyalandı" cümlesi yalnız yazma gerçekten bittiğinde dönüyor.
                    await navigator.clipboard.writeText(sunucununProformaAdresi ?? musteriBaglantisi.viewUrl);
                    return M.satis.musteriBaglantisiKopyalandi;
                  })
                }
              >
                {M.satis.musteriBaglantisiKopyala}
              </button>
            </div>
          ) : (
            <p className="callout callout-warn" role="status" data-testid="public-quote-link-missing">
              {M.satis.musteriBaglantisiYok}
            </p>
          )}

          {/*
            Vaka madde 9'un tuşu ve madde 6-7'nin tek mesajı.

            **Sağlayıcı seçilmedi ve bu kurulumda hiçbir WhatsApp kimliği yok.** Düğmeler yine de
            duruyor ve basılabiliyor: gönderim katmanı (`whatsapp-send.service.ts`) her denemeye
            adlandırılmış bir ret (`NOT_CONFIGURED`, `BLOCKED_CONSENT`, `BLOCKED_WINDOW`,
            `BLOCKED_MEDIA`, `FAILED`) ve bir kayıt satırı üretiyor. Ekranda gösterilen tam olarak
            o: sunucunun kendi cevabı, yumuşatılmadan. Düğmeyi gizlemek satışçıya "böyle bir yol
            yok" derdi; başarı göstermek yalan olurdu.
          */}
          <h3>{M.satis.whatsappBasligi}</h3>
          <p className="callout callout-warn" role="status" data-testid="whatsapp-provider-notice">
            {M.satis.whatsappSaglayiciNotu}
          </p>

          <p className="muted">{M.satis.whatsappProformaGonderNotu}</p>
          <button
            type="button"
            data-testid="send-quote-whatsapp"
            onClick={() =>
              void run(async () => {
                const sonuc = await api.sendQuotePaymentLinkOnWhatsApp(quote.id, quote.currentVersionNo);
                setQuoteWhatsApp(sonuc);
                // Ödeme bağlantısı bu çağrının içinde üretiliyor; ekrandaki bağlantı kutusu da
                // tazeleniyor ki iki yerde iki farklı gerçek durmasın.
                setPaymentLink((onceki) =>
                  onceki
                    ? { ...onceki, paymentUrl: sonuc.odemeBaglantisi.paymentUrl, alreadyExisted: sonuc.odemeBaglantisi.alreadyExisted }
                    : onceki,
                );
                /*
                  Yeşil bildirim yalnız **gerçekten gittiğinde**. Ret hâlinde bildirim yazılmıyor:
                  yeşil bir kutunun yanında sarı bir ret kutusu, göz ilk yeşile takıldığı için
                  başarı gibi okunurdu. Reddi anlatan tek yüzey aşağıdaki sonuç kutusu.
                */
                return sonuc.gonderim.ok ? M.satis.whatsappGitti(sonuc.gonderim.provider) : undefined;
              })
            }
          >
            {M.satis.whatsappProformaGonder}
          </button>
          {quoteWhatsApp ? (
            <div data-testid="quote-whatsapp-result">
              <p
                className={`callout ${quoteWhatsApp.gonderim.ok ? 'callout-ok' : 'callout-warn'}`}
                role="status"
                data-testid="quote-whatsapp-state"
              >
                {quoteWhatsApp.gonderim.ok
                  ? M.satis.whatsappGitti(quoteWhatsApp.gonderim.provider)
                  : M.satis.whatsappGitmedi(WHATSAPP_DURUM_ADI[quoteWhatsApp.gonderim.state] ?? quoteWhatsApp.gonderim.state)}
                {/* Sunucunun kullandığı ad da duruyor: destek isteğinde verilecek olan bu. */}
                <span className="state" data-testid="quote-whatsapp-state-code">{quoteWhatsApp.gonderim.state}</span>
              </p>
              {quoteWhatsApp.gonderim.ok ? null : (
                <p className="uzun-deger" data-veri data-testid="quote-whatsapp-reason">{quoteWhatsApp.gonderim.reason}</p>
              )}
              <p className="alan-ipucu uzun-deger" data-testid="quote-whatsapp-log">{M.satis.whatsappKayitNo(quoteWhatsApp.gonderim.logId)}</p>
              <p className="alan-ipucu" data-testid="quote-whatsapp-to">{M.satis.whatsappAlici(quoteWhatsApp.toPhone)}</p>
              {/*
                Tutar tam sayı minör birimde geliyor ve yalnız `para()` ile çiziliyor; hiçbir
                adımda ondalıklı sayıya çevrilmiyor. Para birimi her zaman yanında.
              */}
              <p data-testid="quote-whatsapp-amount">
                {M.satis.whatsappOdemeTutari(para(quoteWhatsApp.odemeBaglantisi.amountMinor, quoteWhatsApp.odemeBaglantisi.currencyCode))}
              </p>
              <p data-testid="quote-whatsapp-hold">
                {quoteWhatsApp.odemeBaglantisi.holdAndChargeLater ? M.satis.whatsappProvizyonlu : M.satis.whatsappDogrudan}
              </p>
              {quoteWhatsApp.proforma.attached ? (
                <p data-testid="quote-whatsapp-proforma">
                  {M.satis.whatsappProformaEklendi(sayi(quoteWhatsApp.proforma.bytes ?? 0))}
                </p>
              ) : (
                <p data-testid="quote-whatsapp-proforma-missing">
                  {M.satis.whatsappProformaEklenmedi} <span data-veri>{quoteWhatsApp.proforma.reason}</span>
                </p>
              )}
            </div>
          ) : null}

          <h4>{M.satis.whatsappSerbestBasligi}</h4>
          <p className="muted">{M.satis.whatsappSerbestAcikla}</p>
          <AlanSatiri>
            <Alan etiket={M.satis.whatsappTelefonEtiketi}>
              <input
                data-testid="whatsapp-phone"
                inputMode="tel"
                value={whatsappNumarasi}
                onChange={(event) => setWhatsappTelefon(event.target.value)}
              />
            </Alan>
            <Alan etiket={M.satis.whatsappAmacEtiketi}>
              <select
                data-testid="whatsapp-purpose"
                value={whatsappAmac}
                onChange={(event) => setWhatsappAmac(event.target.value as WhatsAppAmaci)}
              >
                {WHATSAPP_AMACLARI.map((amac) => (
                  <option key={amac} value={amac}>
                    {WHATSAPP_AMAC_ADI[amac] ?? amac}
                  </option>
                ))}
              </select>
            </Alan>
          </AlanSatiri>
          <Alan etiket={M.satis.whatsappMetinEtiketi}>
            <textarea data-testid="whatsapp-text" value={whatsappMetin} onChange={(event) => setWhatsappMetin(event.target.value)} />
          </Alan>
          {musteriBaglantisi ? (
            <button
              type="button"
              className="ikincil"
              data-testid="whatsapp-append-link"
              onClick={() => setWhatsappMetin((onceki) => (onceki === '' ? musteriBaglantisi.viewUrl : `${onceki}\n${musteriBaglantisi.viewUrl}`))}
            >
              {M.satis.whatsappBaglantiyiEkle}
            </button>
          ) : null}
          <button
            type="button"
            data-testid="send-whatsapp-message"
            onClick={() =>
              void run(async () => {
                // İki alan da uca gitmeden önce burada okunuyor: boş bir numarayla gönderim
                // katmanına gitmek `PHONE_UNREADABLE` derdi ve hata bizim çağrımızı işaret ederdi.
                if (whatsappNumarasi.trim() === '') throw new Error(M.satis.whatsappTelefonBos);
                if (whatsappMetin.trim() === '') throw new Error(M.satis.whatsappMetinBos);
                const sonuc = await api.sendWhatsAppMessage({
                  to: whatsappNumarasi.trim(),
                  purpose: whatsappAmac,
                  text: whatsappMetin.trim(),
                });
                setWhatsappSonuc(sonuc);
                // Yukarıdaki aynı kural: yeşil bildirim yalnız gerçek gönderimde.
                return sonuc.ok ? M.satis.whatsappGitti(sonuc.provider) : undefined;
              })
            }
          >
            {M.satis.whatsappGonder}
          </button>
          {whatsappSonuc ? (
            <div data-testid="whatsapp-send-result">
              <p
                className={`callout ${whatsappSonuc.ok ? 'callout-ok' : 'callout-warn'}`}
                role="status"
                data-testid="whatsapp-send-state"
              >
                {whatsappSonuc.ok
                  ? M.satis.whatsappGitti(whatsappSonuc.provider)
                  : M.satis.whatsappGitmedi(WHATSAPP_DURUM_ADI[whatsappSonuc.state] ?? whatsappSonuc.state)}
                <span className="state" data-testid="whatsapp-send-state-code">{whatsappSonuc.state}</span>
              </p>
              {whatsappSonuc.ok ? null : <p className="uzun-deger" data-veri data-testid="whatsapp-send-reason">{whatsappSonuc.reason}</p>}
              <p className="alan-ipucu uzun-deger" data-testid="whatsapp-send-log">{M.satis.whatsappKayitNo(whatsappSonuc.logId)}</p>
            </div>
          ) : null}

          </details>
          <h3>{M.satis.siparisBasligi}</h3>
          <button
            type="button"
            data-testid="create-order"
            onClick={() =>
              void run(async () => {
                const result = await api.createOrder({ quoteId: quote.id, idempotencyKey: `${idempotencyKey}-order` });
                setOrder({ id: result.order.id, code: result.order.code, replay: result.idempotentReplay });
                await refreshQuote(quote.id);
                return result.idempotentReplay
                  ? M.satis.siparisYinelendi(result.order.code)
                  : M.satis.siparisOlusturuldu(result.order.code, tamSayi(result.order.items.length));
              })
            }
          >
            {M.satis.sipariseDonustur}
          </button>
          {order ? (
            <p>
              <span data-testid="order-code">{order.code}</span>
              <span data-testid="order-idempotent-replay">{order.replay ? M.satis.yineleme : M.satis.olusturuldu}</span>
            </p>
          ) : null}
          {quote.order ? <p data-testid="quote-linked-order">{quote.order.code}</p> : null}
        </div>
        </SalesDetailDrawer>
      ) : null}

</div>
      <div hidden={view !== 'orders'} className="sales-detail-panel sales-record-panel">
      <SalesRecords kind="orders" loading={loading} failed={loadFailed} rows={orders.map((entry) => ({ id: entry.id, code: entry.code, customer: entry.customer.displayName, status: entry.status, service: entry.items.map((item) => item.description).join(' · '), metric: entry.tasks.length ? S.taskCount(entry.tasks.filter((task) => task.status === 'FULFILLED').length, entry.tasks.length) : S.noTasks, meta: S.itemCount(entry.items.length), next: entry.status === 'COMPLETED' ? S.reviewRecord : entry.status === 'FULFILLED' ? S.checkClosure : S.taskFollow }))} onOpen={(id) => void run(() => refreshClosure(id))} />
      {closure && view === 'orders' ? (
        <SalesDetailDrawer title={closure.code} onClose={() => setClosure(null)}>
        <Notices scope="sales-order-drawer" error={error} notice={notice} />
        <div data-testid="sales-order-detail">
          <div className="sales-quote-detail-heading"><div><strong data-testid="sales-order-code">{closure.code}</strong><p>{closure.customer.displayName}</p></div><span className="sales-pill" data-testid="sales-order-status">{durumAdi(closure.status)}</span></div>
          <dl className="sales-detail-facts"><div><dt>{S.recordVersion}</dt><dd data-testid="sales-order-version">{tamSayi(closure.version)}</dd></div><div><dt>{S.reconciliation}</dt><dd data-testid="sales-order-reconciliation">{durumYaz(closure.reconciliation?.status)}</dd></div></dl>
          <section className="sales-detail-section"><h3>{S.taskProgress}</h3><p data-testid="sales-order-tasks-fulfilled">{closure.tasks.length ? S.taskCount(closure.tasks.filter((task) => task.status === 'FULFILLED').length, closure.tasks.length) : S.noTasks}</p>{closure.tasks.length ? <progress aria-label={S.taskProgress} max={closure.tasks.length} value={closure.tasks.filter((task) => task.status === 'FULFILLED').length} /> : null}</section>
          <section className="sales-detail-section"><h3>{S.services}</h3><div className="sales-line-items">{closure.items.map((item) => <div key={item.id}><strong>{item.description}</strong></div>)}</div></section>
          <section className="sales-detail-section"><h3>{S.orderClosure}</h3>
          <p className="sales-help" data-testid="order-closure-rule">{M.satis.kapanisKurali(durumAdi('FULFILLED'), durumAdi('COMPLETED'))}</p>
          <div className="sales-action-row">
          <button type="button" data-testid="sales-order-fulfil" onClick={() => closureStep('FULFILLED')}>
            {M.satis.siparisiTamamla}
          </button>
          <button type="button" data-testid="sales-order-complete" onClick={() => closureStep('COMPLETED')}>
            {M.satis.siparisiKapat}
          </button>
          </div></section>
        </div>
        </SalesDetailDrawer>
      ) : null}
</div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Operasyon
// ---------------------------------------------------------------------------

/**
 * Bir operasyon görevinin **sıradaki adımı**.
 *
 * Ölçülen kusur: görev kartında on iki düğme vardı, hepsi aynı yeşil (`article > button`
 * kuralı hepsini birincil boyuyordu), hiçbiri işaretli değildi ve doğru sıra ekranda hiçbir
 * yerde yazmıyordu. Düğmelerin diziliş sırasıyla gidildiğinde on dört tık harcanıyor, ikisi
 * hataya çarpıyordu; yolcu adının **en başta** girilmesi gerektiği ancak beşinci tıkta
 * öğreniliyordu.
 *
 * Sıra buradan okunuyor ve uçtaki kuralın aynısı: `workflow.service.ts` içinde `transitionTask`
 * `FULFILLED` için yolcu adı, kapanmış kontrol listesi, teyitli rezervasyon ve voucher istiyor;
 * `issueVoucher` yolcu adı istiyor; `transitionBooking` `DRAFT → REQUESTED → CONFIRMED`
 * zincirini uyguluyor. Ekran bu kuralı **tekrar** ediyor, yerine geçmiyor: her düğme uca
 * gidiyor ve uç aynı denetimi yeniden yapıyor. Buradaki kopya yalnız sırayı görünür kılmak
 * için var, ve bir gün kural değişirse iki yerde değişmesi gerektiği bu notla kayıtlı.
 */
type Adim = 'AYRINTILAR' | 'BASLAT' | 'REZERVASYON' | 'TALEP' | 'TEYIT' | 'VOUCHER' | 'TAMAMLA' | null;

const KAPANMIS_DURUMLAR = ['FULFILLED', 'FAILED', 'CANCELLED'];

function sonrakiAdim(task: FulfilmentTask, booking: SupplierBooking | undefined, yolcuAdi: string): Adim {
  if (KAPANMIS_DURUMLAR.includes(task.status)) return null;
  if (!yolcuAdi.trim() || task.checklist.some((entry) => entry.state === 'PENDING')) return 'AYRINTILAR';
  if (task.status === 'PENDING' || task.status === 'BLOCKED') return 'BASLAT';
  if (!booking || !['DRAFT', 'REQUESTED', 'CONFIRMED'].includes(booking.status)) return 'REZERVASYON';
  if (booking.status === 'DRAFT') return 'TALEP';
  if (booking.status === 'REQUESTED') return 'TEYIT';
  if (!task.voucher) return 'VOUCHER';
  return 'TAMAMLA';
}

/** Ekranın tuttuğu vize belgesi formu. Boş dize "gönderilmeyecek" demek; uca hiç yazılmıyor. */
interface VizeBelgeFormu {
  belge: VizeBelgeTuru;
  yolcular: string;
  kisiSayisi: string;
  oda: string;
  /** Katalogdan seçilen otelin slug değeri; boş = seçilmedi. */
  katalogSlug: string;
  /** Seçilen otelin katalog kaydı (adres, koordinat, kapak, eksik alanlar). */
  katalogKaydi: VizeOtelKaydi | null;
  /** Üretici servisin hazır otel anahtarı (altı hazır otel); boş = seçilmedi. Katalog seçimiyle birlikte dolu olmaz. */
  hazirOtel: string;
  hazirOtelAd: string;
  baskaOtel: boolean;
  otelAdi: string;
  otelSehir: string;
  otelAdres: string;
  otelTelefon: string;
  otelGps: string;
  odaTipi: string;
  pansiyon: string;
  rota: string;
  ucusTipi: string;
  gidisTarih: string;
  donusTarih: string;
  koltuk: string;
  not: string;
}

function bosVizeFormu(yolcuAdi: string): VizeBelgeFormu {
  return {
    belge: 'otel',
    yolcular: yolcuAdi,
    kisiSayisi: '',
    oda: '',
    katalogSlug: '',
    katalogKaydi: null,
    hazirOtel: '',
    hazirOtelAd: '',
    baskaOtel: false,
    otelAdi: '',
    otelSehir: '',
    otelAdres: '',
    otelTelefon: '',
    otelGps: '',
    odaTipi: '',
    pansiyon: '',
    rota: '',
    ucusTipi: 'roundtrip',
    gidisTarih: '',
    donusTarih: '',
    koltuk: '',
    not: '',
  };
}

/**
 * Formu ucun gövdesine çevirir. Boş alanlar gövdeye **hiç yazılmıyor**: uçtaki varsayılan,
 * uydurulmuş bir boş dizeden iyidir.
 *
 * `parseTamSayi` sayı olmayan bir oda değerinde fırlatıyor ve çağıran onu kendi hata
 * kutusunda gösteriyor; sessizce sıfıra çevirmek yanlış bir belge üretirdi.
 */
function vizeIstekGovdesi(form: VizeBelgeFormu): VizeBelgeIstegi {
  const govde: VizeBelgeIstegi = { belge: form.belge };
  const metin = (anahtar: keyof VizeBelgeIstegi, deger: string) => {
    const temiz = deger.trim();
    if (temiz.length > 0) Object.assign(govde, { [anahtar]: temiz });
  };
  const tamsayi = (anahtar: keyof VizeBelgeIstegi, deger: string) => {
    const temiz = deger.trim();
    if (temiz.length > 0) Object.assign(govde, { [anahtar]: parseTamSayi(temiz) });
  };
  metin('yolcular', form.yolcular.split(/[,\n]+/).map((ad) => ad.trim()).filter(Boolean).join(', '));
  metin('kisiSayisi', form.kisiSayisi);
  metin('not', form.not);
  const tekYon = vizeTekYon(form);
  if (vizeOtelKolu(form.belge)) {
    metin('giris', form.gidisTarih);
    govde.gece = vizeGeceSayisi(form);
    tamsayi('oda', form.oda);
    if (form.baskaOtel) {
      metin('otelAdi', form.otelAdi);
      metin('otelSehir', form.otelSehir);
      metin('otelAdres', form.otelAdres);
      metin('otelTelefon', form.otelTelefon);
      metin('otelGps', form.otelGps);
    } else if (form.hazirOtel) {
      // Altı hazır otel: bilgi üretici serviste tanımlı, telefon ve adres istenmez (02.11 öncesi davranış).
      metin('hazirOtel', form.hazirOtel);
    } else {
      metin('katalogOtel', form.katalogSlug);
      // Telefonun katalogda kaynağı yok, her zaman elle. Adres ve koordinat yalnız katalogda eksikse elle.
      metin('otelTelefon', form.otelTelefon);
      if (form.katalogKaydi?.adres === null) metin('otelAdres', form.otelAdres);
      if (form.katalogKaydi?.gps === null) metin('otelGps', form.otelGps);
    }
    metin('odaTipi', form.odaTipi);
    metin('pansiyon', form.pansiyon);
  }
  if (vizeUcakKolu(form.belge)) {
    metin('rota', form.rota);
    // Birlikte istenen belgede uçuş her zaman gidiş dönüş: otel konaklaması dönüş tarihini ister.
    metin('ucusTipi', form.belge === 'otel_ucak' ? 'roundtrip' : form.ucusTipi);
    metin('gidisTarih', form.gidisTarih);
    if (!tekYon) metin('donusTarih', form.donusTarih);
    metin('koltuk', form.koltuk);
  }
  return govde;
}

/** Türün otel kolu var mı: otel ve otel_ucak. */
function vizeOtelKolu(belge: VizeBelgeTuru): boolean {
  return belge === 'otel' || belge === 'otel_ucak';
}

/** Türün uçak kolu var mı: ucak ve otel_ucak. */
function vizeUcakKolu(belge: VizeBelgeTuru): boolean {
  return belge === 'ucak' || belge === 'otel_ucak';
}

/** Dönüş tarihi istemeyen tek durum: tek yön uçak bileti. Birleşik belge hep gidiş dönüş. */
function vizeTekYon(form: VizeBelgeFormu): boolean {
  return form.belge === 'ucak' && form.ucusTipi === 'oneway';
}

function vizeGeceSayisi(form: VizeBelgeFormu): number {
  return (Date.parse(form.donusTarih) - Date.parse(form.gidisTarih)) / 86_400_000;
}

/** Düğmenin neden basılamadığı. `null` basılabilir demek. İki üretici de aynı sırayı kullanıyor. */
function vizeEngeli(form: VizeBelgeFormu, yazabilir: boolean, calisiyor: boolean): string | null {
  const E = M.operasyon.vizeBelge.engel;
  if (!yazabilir) return E.yetkiYok;
  if (calisiyor) return E.uretimSuruyor;
  if (!form.yolcular.trim()) return E.yolcuAdiYok;
  const tekYon = vizeTekYon(form);
  const otel = vizeOtelKolu(form.belge);
  if (!form.gidisTarih || (!tekYon && !form.donusTarih)) return E.tarihYok;
  if (!tekYon) {
    const gece = vizeGeceSayisi(form);
    if (!Number.isFinite(gece) || gece < (otel ? 1 : 0)) return E.tarihSirasi;
    if (otel && gece > 60) return E.geceSiniri;
  }
  if (otel && (form.baskaOtel ? !form.otelAdi.trim() : !form.hazirOtel && (!form.katalogSlug || !form.katalogKaydi))) return E.otelSecilmedi;
  if (otel && !form.hazirOtel && (!form.kisiSayisi.trim() || !form.oda.trim() || !form.odaTipi.trim() || !form.pansiyon.trim())) return E.otelAyrintiEksik;
  if (otel && !form.baskaOtel && !form.hazirOtel) {
    if (!form.otelTelefon.trim()) return E.otelTelefonYok;
    if ((form.katalogKaydi?.adres === null && !form.otelAdres.trim()) || (form.katalogKaydi?.gps === null && !form.otelGps.trim())) return E.katalogAdresYok;
  }
  if (vizeUcakKolu(form.belge) && !form.rota) return E.rotaSecilmedi;
  return null;
}

/**
 * Uç 400 döndüğünde ekranın kendi tanıdığı geçerli anahtar listesi. İki taraf ayrışmışsa
 * görünsün diye yazılıyor; ucun kendi listesi zaten hata mesajının içinde geliyor.
 */
function vizeAnahtarSatiri(form: VizeBelgeFormu): string {
  const V = M.operasyon.vizeBelge;
  const satirlar: string[] = [];
  if (vizeUcakKolu(form.belge)) satirlar.push(V.gecerliRotaAnahtarlari(VIZE_UCUS_ROTALARI.join(', ')));
  return satirlar.join(' ');
}

/**
 * Vize belgesinin otel seçimi: MBD otel kataloğunda (v3, yayındaki oteller) aranabilir seçim.
 *
 * Seçilen otelin adı, adresi, koordinatı ve kapak karesi **sunucuda** katalogdan okunur; ekran yalnız
 * slug gönderir. Burada gösterilen kart operasyonun belgeye ne gideceğini görmesi için. Katalogda
 * olmayan alan "bilgi eksik" diye işaretlenir ve uydurulmaz: telefonun katalogda kaynağı yok, her
 * otelde elle yazılır; adres ya da koordinat eksikse onlar da.
 */
function VizeOtelSecici({
  form,
  yaz,
  id,
}: {
  form: VizeBelgeFormu;
  yaz: (yama: Partial<VizeBelgeFormu>) => void;
  id: (ad: string) => string;
}) {
  const V = M.operasyon.vizeBelge;
  const [arama, setArama] = useState('');
  const [sonuclar, setSonuclar] = useState<VizeOtelOzeti[] | null>(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const [katalogHatasi, setKatalogHatasi] = useState(false);
  const [odak, setOdak] = useState(false);
  const kayit = form.katalogKaydi;

  useEffect(() => {
    const metin = arama.trim();
    if (form.baskaOtel) {
      setSonuclar(null);
      setHata(null);
      return;
    }
    const kontrol = new AbortController();
    const zamanlayici = setTimeout(() => {
      setYukleniyor(true);
      setHata(null);
      api.vizeOtelAra(metin, kontrol.signal)
        .then((cevap) => {
          setSonuclar(cevap.oteller);
          setKatalogHatasi(cevap.katalogHatasi === true);
        })
        .catch((error: unknown) => {
          if (kontrol.signal.aborted) return;
          setSonuclar(null);
          setHata(error instanceof Error ? error.message : V.otelAramaHatasi);
        })
        .finally(() => { if (!kontrol.signal.aborted) setYukleniyor(false); });
    }, 300);
    return () => {
      clearTimeout(zamanlayici);
      kontrol.abort();
    };
  }, [arama, form.baskaOtel, V.otelAramaHatasi]);

  const sec = async (otel: VizeOtelOzeti) => {
    setHata(null);
    if (otel.hazirOtel) {
      // Hazır otel: ayrıntı sunucuda üretici serviste; katalog çağrısı yok.
      yaz({ hazirOtel: otel.hazirOtel, hazirOtelAd: otel.ad, katalogSlug: '', katalogKaydi: null, baskaOtel: false, otelTelefon: '', otelAdres: '', otelGps: '' });
      setSonuclar(null);
      setArama('');
      return;
    }
    try {
      const detay = await api.vizeOtelDetay(otel.slug);
      yaz({ katalogSlug: detay.slug, katalogKaydi: detay, hazirOtel: '', hazirOtelAd: '', baskaOtel: false, otelAdres: '', otelGps: '' });
      setSonuclar(null);
      setArama('');
    } catch (error) {
      setHata(error instanceof Error ? error.message : V.otelDetayHatasi);
    }
  };
  const eksikMi = (alan: VizeOtelKaydi['eksik'][number]) => kayit?.eksik.includes(alan) === true;

  return (
    <div className="ops-document-hotel" data-testid={id('hotel-picker')}>
      <Alan etiket={V.hazirOtelEtiketi} ipucu={V.otelAraIpucu} zorunlu>
        <input data-testid={id('hotel-search')} type="search" value={arama} placeholder={V.hazirOtelSecilmedi}
          autoComplete="off" maxLength={80} disabled={form.baskaOtel}
          onFocus={() => setOdak(true)} onChange={(event) => setArama(event.target.value)} />
      </Alan>
      {katalogHatasi && !form.baskaOtel ? <p className="ops-document-hint" role="status" data-testid={id('hotel-catalog-down')}>{V.katalogYanitsiz}</p> : null}
      {yukleniyor ? <p className="ops-document-hint" role="status">{V.otelAraniyor}</p> : null}
      {hata ? <p className="ops-document-hint" role="alert" data-testid={id('hotel-error')}>{hata}</p> : null}
      {sonuclar && sonuclar.length === 0 && !katalogHatasi ? <p className="ops-document-hint" data-testid={id('hotel-empty')}>{V.otelBulunamadi}</p> : null}
      {sonuclar && sonuclar.length > 0 && (!(form.hazirOtel || kayit) || odak || arama.trim() !== '') ? (
        <ul className="ops-document-hotel-results" data-testid={id('hotel-results')}>
          {sonuclar.map((otel) => (
            <li key={`${otel.hazirOtel ? 'hazir' : 'katalog'}:${otel.slug}`}>
              <button type="button" data-testid={id(`hotel-option-${otel.slug}`)} onClick={() => void sec(otel)}>
                {otel.ad}{otel.hazirOtel ? ` · ${V.hazirOtelEtiketiKisa}` : ''}{otel.sehir ? ` · ${otel.sehir}` : ''}{otel.yildiz ? ` · ${String(otel.yildiz)}*` : ''}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {!form.baskaOtel && form.hazirOtel ? (
        <div className="ops-document-hotel-card" data-testid={id('hotel-card')}>
          <div>
            <h3>{form.hazirOtelAd}</h3>
            <p className="ops-document-hint">{V.kayitliOtelIpucu}</p>
          </div>
        </div>
      ) : null}
      {!form.baskaOtel && kayit ? (
        <div className="ops-document-hotel-card" data-testid={id('hotel-card')}>
          {kayit.fotoUrl ? <img src={kayit.fotoUrl} alt={kayit.ad} loading="lazy" width={160} height={110} /> : <p className="ops-document-hint">{V.fotografEksik}</p>}
          <div>
            <h3>{kayit.ad}</h3>
            <dl>
              <dt>{V.otelAdresEtiketi}</dt>
              <dd>{kayit.adres ?? <strong data-testid={id('hotel-address-missing')}>{V.bilgiEksik}</strong>}</dd>
              <dt>{V.otelGpsEtiketi}</dt>
              <dd>{kayit.gps ?? <strong data-testid={id('hotel-gps-missing')}>{V.bilgiEksik}</strong>}</dd>
            </dl>
            <p className="ops-document-hint">{V.kayitliOtelIpucu}</p>
          </div>
        </div>
      ) : null}
      {!form.baskaOtel && kayit ? (
        <div className="alan-satiri">
          <Alan etiket={V.otelTelefonEtiketi} ipucu={eksikMi('telefon') ? V.telefonKaynakYok : undefined} zorunlu>
            <input data-testid={id('hotel-phone')} value={form.otelTelefon} maxLength={40} onChange={(event) => yaz({ otelTelefon: event.target.value })} />
          </Alan>
          {eksikMi('adres') ? (
            <Alan etiket={V.otelAdresEtiketi} zorunlu>
              <input data-testid={id('hotel-address')} value={form.otelAdres} maxLength={300} onChange={(event) => yaz({ otelAdres: event.target.value })} />
            </Alan>
          ) : null}
          {eksikMi('koordinat') ? (
            <Alan etiket={V.otelGpsEtiketi} ipucu={V.otelGpsIpucu} zorunlu>
              <input data-testid={id('hotel-gps')} value={form.otelGps} maxLength={60} onChange={(event) => yaz({ otelGps: event.target.value })} />
            </Alan>
          ) : null}
        </div>
      ) : null}
      <button type="button" className="ops-document-other" data-testid={id('hotel-other')}
        onClick={() => yaz({ baskaOtel: !form.baskaOtel, ...(form.baskaOtel ? {} : { katalogSlug: '', katalogKaydi: null, hazirOtel: '', hazirOtelAd: '', otelTelefon: '', otelAdres: '', otelGps: '' }) })}>
        {form.baskaOtel ? V.katalogaDon : V.baskaOtel}
      </button>
    </div>
  );
}

/**
 * Vize belgesi formunun alanları. İki üretici de bunu çiziyor: görev kartındaki ve siparişsiz
 * olan. Ayrı bir bileşen, çünkü iki kopya bir gün ikiye ayrılır ve o gün konsolosluğa iki farklı
 * belge gider.
 *
 * `id` çağırandan geliyor: `data-testid` değerleri iki yüzeyde farklı ve testler onlara bakıyor.
 */
function VizeBelgeAlanlari({
  form,
  yaz,
  id,
}: {
  form: VizeBelgeFormu;
  yaz: (yama: Partial<VizeBelgeFormu>) => void;
  id: (ad: string) => string;
}) {
  const V = M.operasyon.vizeBelge;
  const otelBelgesi = vizeOtelKolu(form.belge);
  const ucakBelgesi = vizeUcakKolu(form.belge);
  const tekYon = vizeTekYon(form);
  const gece = vizeGeceSayisi(form);
  const metinAlani = (alan: keyof VizeBelgeFormu, testId: string, etiket: string, maxLength: number, ipucu?: string) => (
    <Alan etiket={etiket} ipucu={ipucu}>
      <input data-testid={id(testId)} value={String(form[alan])} maxLength={maxLength}
        onChange={(event) => yaz({ [alan]: event.target.value })} />
    </Alan>
  );
  return (
    <div className="ops-document-form">
      <div className="ops-document-type">
        <Alan etiket={V.belgeTuruEtiketi} zorunlu>
          <select data-testid={id('type')} value={form.belge}
            onChange={(event) => yaz({ belge: event.target.value as VizeBelgeTuru })}>
            {VIZE_BELGE_TURLERI.map((tur) => <option key={tur} value={tur}>{V.belgeTuruAdi[tur]}</option>)}
          </select>
        </Alan>
      </div>
      <div className="ops-document-main">
        <Alan etiket={V.yolcularEtiketi} ipucu={V.yolcularIpucu} zorunlu>
          <textarea data-testid={id('travellers')} value={form.yolcular} maxLength={400} rows={2}
            onChange={(event) => yaz({ yolcular: event.target.value })} />
        </Alan>
        {form.belge === 'otel_ucak' ? <p className="ops-document-hint" data-testid={id('combined-hint')}>{V.birlesikIpucu}</p> : null}
        {tekYon ? (
          // Tek yönde aralık yok, tek gün var: aynı takvim, tek gün kipinde.
          <Alan etiket={V.gidisTarihEtiketi} zorunlu>
            <TarihSaatSecici testId={id('depart')} saatli={false} deger={form.gidisTarih}
              setDeger={(yeni) => yaz({ gidisTarih: yeni, donusTarih: '' })} />
          </Alan>
        ) : (
          <Alan etiket={V.tarihAraligiEtiketi} zorunlu>
            <TarihAraligiSecici testId={id('dates')} giris={form.gidisTarih} cikis={form.donusTarih}
              setAralik={(giris, cikis) => yaz({ gidisTarih: giris, donusTarih: cikis })} />
          </Alan>
        )}
        {otelBelgesi ? (
          <>
            <VizeOtelSecici form={form} yaz={yaz} id={id} />
            {Number.isFinite(gece) && gece > 0 ? <p className="ops-document-hint" data-testid={id('nights')} role="status">{V.geceOzeti(gece)}</p> : null}
            {form.baskaOtel ? (
              <div className="ops-document-custom" data-testid={id('custom-hotel')}>
                <h3>{V.baskaOtel}</h3>
                <p>{V.otelAdiIpucu}</p>
                <Alan etiket={V.otelAdiEtiketi} zorunlu>
                  <input data-testid={id('hotel-name')} value={form.otelAdi} maxLength={160}
                    onChange={(event) => yaz({ otelAdi: event.target.value })} />
                </Alan>
                <div className="alan-satiri">
                  {metinAlani('otelSehir', 'hotel-city', V.otelSehirEtiketi, 120)}
                  {metinAlani('otelAdres', 'hotel-address', V.otelAdresEtiketi, 300)}
                  {metinAlani('otelTelefon', 'hotel-phone', V.otelTelefonEtiketi, 40)}
                  {metinAlani('otelGps', 'hotel-gps', V.otelGpsEtiketi, 60, V.otelGpsIpucu)}
                </div>
              </div>
            ) : null}
          </>
        ) : null}
        {otelBelgesi ? (
          <div className="alan-satiri" data-testid={id('stay-details')}>
            <Alan etiket={V.kisiSayisiEtiketi} ipucu={V.kisiSayisiIpucu} zorunlu>
              <input data-testid={id('people')} value={form.kisiSayisi} maxLength={80} onChange={(event) => yaz({ kisiSayisi: event.target.value })} />
            </Alan>
            <Alan etiket={V.odaEtiketi} zorunlu>
              <input data-testid={id('rooms')} value={form.oda} maxLength={2} inputMode="numeric" onChange={(event) => yaz({ oda: event.target.value })} />
            </Alan>
            <Alan etiket={V.odaTipiEtiketi} zorunlu>
              <input data-testid={id('room-type')} value={form.odaTipi} maxLength={160} onChange={(event) => yaz({ odaTipi: event.target.value })} />
            </Alan>
            <Alan etiket={V.pansiyonEtiketi} zorunlu>
              <input data-testid={id('board')} value={form.pansiyon} maxLength={80} onChange={(event) => yaz({ pansiyon: event.target.value })} />
            </Alan>
          </div>
        ) : null}
        {ucakBelgesi ? (
          <div className="alan-satiri">
            <Alan etiket={V.rotaEtiketi} zorunlu>
              <select data-testid={id('route')} value={form.rota} onChange={(event) => yaz({ rota: event.target.value })}>
                <option value="">{V.rotaSecilmedi}</option>
                {VIZE_UCUS_ROTALARI.map((rota) => <option key={rota} value={rota}>{V.rotaAdi[rota]}</option>)}
              </select>
            </Alan>
            {form.belge === 'ucak' ? (
              <Alan etiket={V.ucusTipiEtiketi}>
                <select data-testid={id('trip')} value={form.ucusTipi} onChange={(event) => yaz({ ucusTipi: event.target.value })}>
                  <option value="roundtrip">{V.ucusTipiAdi.roundtrip}</option>
                  <option value="oneway">{V.ucusTipiAdi.oneway}</option>
                </select>
              </Alan>
            ) : null}
          </div>
        ) : null}
      </div>
      <details className="ops-document-options" data-testid={id('options')}>
        <summary>{V.ekSecenekler}</summary>
        <div className="alan-satiri">
          {!otelBelgesi ? metinAlani('kisiSayisi', 'people', V.kisiSayisiEtiketi, 80, V.kisiSayisiIpucu) : null}
          {ucakBelgesi ? metinAlani('koltuk', 'seat', V.koltukEtiketi, 3, V.koltukIpucu) : null}
        </div>
        <Alan etiket={V.notEtiketi} ipucu={V.notIpucu}>
          <textarea data-testid={id('note')} maxLength={500} value={form.not}
            onChange={(event) => yaz({ not: event.target.value })} />
        </Alan>
      </details>
    </div>
  );
}

/**
 * Görev kartındaki vize belgesi üretimi.
 *
 * **Kendi hata durumunu tutuyor**, kartın ortak `run`ını kullanmıyor. Sebebi tek bir cümle:
 * uç 400 döndüğünde geçerli anahtar listesini de yazıyor ve ekran o durumda kendi tanıdığı
 * listeyi de gösteriyor. Ortak `run` yalnız hata metnini taşıyor, durum kodunu taşımıyor.
 *
 * Üretilen PDF tarayıcıya inmiyor: uç onu belge deposuna yazıyor, ekran görev listesini
 * tazeliyor ve dosya görev belgelerinin arasında, diğerleriyle aynı indirme yolundan çıkıyor.
 */
function VizeBelgeUretimi({
  task,
  index,
  yolcuAdi,
  yazabilir,
  refresh,
}: {
  task: FulfilmentTask;
  index: number;
  yolcuAdi: string;
  yazabilir: boolean;
  refresh: () => Promise<void>;
}) {
  const V = M.operasyon.vizeBelge;
  const [form, setForm] = useState<VizeBelgeFormu>(() => bosVizeFormu(yolcuAdi));
  const [calisiyor, setCalisiyor] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const [anahtarlar, setAnahtarlar] = useState<string | null>(null);
  const [bildirim, setBildirim] = useState<string | null>(null);
  const yaz = (yama: Partial<VizeBelgeFormu>) => setForm((mevcut) => ({ ...mevcut, ...yama }));

  // Görevdeki yolcu adı kutuya bir kez tohumlanıyor; sonrasında operatörün yazdığı kazanıyor.
  const tohum = useRef(yolcuAdi);
  useEffect(() => {
    if (tohum.current === yolcuAdi) return;
    tohum.current = yolcuAdi;
    setForm((mevcut) => (mevcut.yolcular.trim() ? mevcut : { ...mevcut, yolcular: yolcuAdi }));
  }, [yolcuAdi]);

  const engel = vizeEngeli(form, yazabilir, calisiyor);

  const uret = async () => {
    setCalisiyor(true);
    setHata(null);
    setAnahtarlar(null);
    setBildirim(null);
    try {
      const sonuc = await api.uretVizeBelgesi(task.id, vizeIstekGovdesi(form));
      await refresh();
      setBildirim(sonuc.belgeler.length > 1
        ? V.uretildiCoklu(sonuc.belgeler.map((belge) => belge.fileName).join(', '), task.code)
        : V.uretildi(sonuc.fileName, task.code));
    } catch (yakalanan) {
      setHata(describe(yakalanan));
      // 400, "hangi değer geçerli" sorusunun cevabıdır. Ucun listesi mesajın içinde geliyor;
      // ekranın kendi tanıdığı liste de yazılıyor ki iki taraf ayrışmışsa görünsün.
      if (yakalanan instanceof ApiError && yakalanan.status === 400) setAnahtarlar(vizeAnahtarSatiri(form));
    } finally {
      setCalisiyor(false);
    }
  };

  return (
    <details className="operational-record" data-testid={`task-visa-${index}`}>
      <summary>{V.baslik}</summary>
      <p>{V.aciklama}</p>
      {hata ? (
        <p className="error callout" role="alert" aria-live="assertive" data-testid={`task-visa-error-${index}`}>
          {hata}
        </p>
      ) : null}
      {anahtarlar ? (
        <p className="callout" data-testid={`task-visa-keys-${index}`}>
          {anahtarlar}
        </p>
      ) : null}
      {bildirim ? (
        <p className="callout callout-ok" role="status" data-testid={`task-visa-notice-${index}`}>
          {bildirim}
        </p>
      ) : null}
      <VizeBelgeAlanlari form={form} yaz={yaz} id={(ad) => `task-visa-${ad}-${index}`} />
      <button
        type="button"
        className="ikincil"
        data-testid={`task-visa-generate-${index}`}
        disabled={engel !== null}
        title={engel ?? undefined}
        onClick={() => void uret()}
      >
        {calisiyor ? V.uretiliyor : V.uret}
      </button>
      {engel ? <p className="ops-document-hint" role="status">{engel}</p> : null}
    </details>
  );
}

function OperationsPanel({ me, selectedOrderId }: { me: Me; selectedOrderId?: string }) {
  const { error, notice, run } = useNotice();
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [orderId, setOrderId] = useState('');
  const [tasks, setTasks] = useState<FulfilmentTask[]>([]);
  const [travellers, setTravellers] = useState<Record<string, string>>({});
  const [references, setReferences] = useState<Record<string, string>>({});
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [statusFilter, setStatusFilter] = useState('');
  const [assigneeId, setAssigneeId] = useState(me.userId);
  const [dueAt, setDueAt] = useState('');
  const [priorities, setPriorities] = useState<Record<string, 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT'>>({});
  const [taskNotes, setTaskNotes] = useState<Record<string, string>>({});
  const [taskFiles, setTaskFiles] = useState<Record<string, File | undefined>>({});

  const loadOrders = useCallback(async () => {
    const page = await api.orders();
    setOrders(page.items);
    return page.items;
  }, []);

  const loadTasks = useCallback(async (id: string) => {
    if (!id) {
      setTasks([]);
      return;
    }
    const page = await api.tasks(id);
    setTasks(page.items);
  }, []);

  // İlk seçim bir kez tohumlanıyor; sonraki her seçim `select` üzerinden geçiyor.
  useEffect(() => {
    void run(async () => {
      const items = await loadOrders();
      const first = selectedOrderId ? items.find((item) => item.id === selectedOrderId) : items[0];
      if (selectedOrderId && !first) {
        setOrderId('');
        setTasks([]);
        throw new Error(M.operasyon.seciliSiparisBulunamadi);
      }
      if (first) {
        setOrderId(first.id);
        await loadTasks(first.id);
      }
    });
  }, [loadOrders, loadTasks, run, selectedOrderId]);

  const select = (id: string) =>
    void run(async () => {
      setOrderId(id);
      await loadTasks(id);
    });

  const allDone = (checklist: ChecklistEntry[]): ChecklistEntry[] =>
    checklist.map((entry) => ({ key: entry.key, state: 'DONE', note: M.operasyon.kontrolListesiNotu }));

  /**
   * Vize belgesi üretiminin izni `document:write`, panelin kapısı ise `fulfilment:write`.
   * İkisi aynı rolde birlikte duruyor (OPERATIONS) ama aynı izin değiller; ayrı okunuyorlar ki
   * biri bir gün ayrılırsa düğme sessizce çalışır görünmesin.
   */
  const belgeYazabilir = me.permissions.includes('document:write');

  return (
    <section id="operations-workflow" data-testid="operations-panel">
      <div className="ops-order-heading"><div><h2>{D.orderTitle}</h2><p>{D.orderHint}</p></div><span className="ops-state">{D.taskCount(tasks.length)}</span></div>
      {/* Tedarikçi kanalı satırı yeniden tasarımda düşmüştü; operatörün dış rezervasyonun elle yürüdüğünü görmesi gerekiyor (crm-a2/a3 E2E ölçüyor). */}
      <p className="muted" data-testid="supplier-channel">{M.operasyon.tedarikciKanali}</p>
      <Notices scope="operations" error={error} notice={notice} />
      <div className="ops-order-toolbar">
      <button type="button" className="ikincil" data-testid="operations-csv-export" onClick={() => void run(async () => { await api.downloadOperationsCsv(); return M.operasyon.csvUretildi; })}>{M.operasyon.csvIndir}</button>
      <Alan etiket={M.genel.siparisSecin}>
        <select data-testid="operations-order" value={orderId} onChange={(event) => select(event.target.value)}>
          <option value="">{M.genel.siparisSecin}</option>
          {orders.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {M.genel.kodDurum(entry.code, durumAdi(entry.status))}
            </option>
          ))}
        </select>
      </Alan>
      <div className="alan-satiri" data-testid="operations-filters">
        <Alan etiket={M.operasyon.durumFiltresiEtiketi}>
          <select data-testid="task-status-filter" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="">{M.operasyon.tumDurumlar}</option>{TASK_STATUS_CODES.map((status) => <option key={status} value={status}>{durumAdi(status)}</option>)}
          </select>
        </Alan>

      </div>
      </div>
      <p className="ops-task-count" data-testid="task-count">{D.taskCount(tasks.filter((task) => !statusFilter || task.status === statusFilter).length)}</p>
      {tasks.length === 0 ? <div className="ops-empty">{D.noTasks}</div> : null}
      {tasks.filter((task) => !statusFilter || task.status === statusFilter).map((task, index) => {
        const booking = task.bookings[task.bookings.length - 1];
        const refresh = async () => loadTasks(orderId);
        /**
         * Kartın bütün düğme durumları tek yerden okunuyor.
         *
         * `yolcuAdi` **kutunun içindeki** değer, uçtaki değil: operatör adı yazdığı anda
         * voucher düğmesi açılıyor, kaydetmeyi beklemeden. Uç yine de kendi denetimini
         * yapıyor; buradaki tek iş, sırayı ekranda göstermek.
         */
        const yolcuAdi = travellers[task.id] ?? task.requiredDetails.travellerName ?? '';
        const kapandi = KAPANMIS_DURUMLAR.includes(task.status);
        const kontrolAcik = task.checklist.some((entry) => entry.state === 'PENDING');
        const adim = sonrakiAdim(task, booking, yolcuAdi);
        const E = M.operasyon.engel;
        /**
         * Her düğmenin **neden** basılamadığı. `null` basılabilir demek.
         *
         * Gizlemek yerine gerekçe göstermek bilinçli: gizlenen düğme kullanıcıya "bu ekran
         * bozuk" dedirtir ve sırayı öğretmez; edilgen ve gerekçeli düğme, bir sonraki sefer
         * doğru sırayla gidilmesini sağlar.
         */
        const engeller: Record<string, string | null> = {
          baslat: kapandi ? E.kapandi : task.status === 'IN_PROGRESS' ? E.zatenBasladi : null,
          bloke: kapandi ? E.kapandi : task.status === 'BLOCKED' ? E.zatenBasladi : null,
          rezervasyon: kapandi ? E.kapandi : booking?.status === 'CONFIRMED' ? E.rezervasyonZatenTeyitli : null,
          talep: !booking ? E.rezervasyonYok : booking.status !== 'DRAFT' ? E.rezervasyonTalepEdilmedi : null,
          teyit: !booking
            ? E.rezervasyonYok
            : booking.status === 'CONFIRMED'
              ? E.rezervasyonZatenTeyitli
              : booking.status !== 'REQUESTED'
                ? E.rezervasyonTeyitBekliyor
                : null,
          voucher: task.voucher ? E.voucherVar : !yolcuAdi.trim() ? E.yolcuAdiYok : null,
          tamamla: kapandi
            ? E.kapandi
            : !yolcuAdi.trim()
              ? E.yolcuAdiYok
              : kontrolAcik
                ? E.kontrolListesiAcik
                : task.status !== 'IN_PROGRESS'
                  ? E.baslamadi
                  : booking?.status !== 'CONFIRMED'
                    ? E.teyitliRezervasyonYok
                    : !task.voucher
                      ? E.voucherYok
                      : null,
        };
        /** Sıradaki adımın düğmesi birincil, geri kalanı ikincil. Kartta tek bir vurgulu düğme var. */
        const sinif = (anahtar: Adim) => (adim === anahtar ? 'birincil' : 'ikincil');
        const adimAdi: Record<Exclude<Adim, null>, string> = {
          AYRINTILAR: M.operasyon.ayrintilariKaydet,
          BASLAT: M.operasyon.basla,
          REZERVASYON: M.operasyon.rezervasyonKaydet,
          TALEP: M.operasyon.talepEt,
          TEYIT: M.operasyon.referansIleTeyitEt,
          VOUCHER: M.operasyon.voucherDuzenle,
          TAMAMLA: M.operasyon.tamamlandiIsaretle,
        };
        const actions = [
          { key: 'AYRINTILAR', element: (<button
                type="button"
                className={sinif('AYRINTILAR')}
                data-testid={`task-save-details-${index}`}
                onClick={() =>
                  void run(async () => {
                    await api.updateTaskDetails(task.id, {
                      expectedVersion: task.version,
                      travellerName: yolcuAdi,
                      checklist: allDone(task.checklist),
                    });
                    await refresh();
                    return M.operasyon.ayrintilarKaydedildi(task.code);
                  })
                }
              >
                {M.operasyon.ayrintilariKaydet}
              </button>) },
          { key: 'BASLAT', element: (<button
                type="button"
                className={sinif('BASLAT')}
                data-testid={`task-start-${index}`}
                disabled={engeller.baslat !== null}
                title={engeller.baslat ?? undefined}
                onClick={() =>
                  void run(async () => {
                    await api.transitionTask(task.id, { status: 'IN_PROGRESS', expectedVersion: task.version });
                    await refresh();
                    return M.operasyon.baslatildi(task.code);
                  })
                }
              >
                {M.operasyon.basla}
              </button>) },
          { key: 'REZERVASYON', element: (<button
                type="button"
                className={sinif('REZERVASYON')}
                data-testid={`task-create-booking-${index}`}
                disabled={engeller.rezervasyon !== null}
                title={engeller.rezervasyon ?? undefined}
                onClick={() =>
                  void run(async () => {
                    await api.createBooking(task.id, {
                      supplierName: task.orderItem?.supplierName ?? '',
                      expectedTaskVersion: task.version,
                      notes: M.operasyon.rezervasyonNotu,
                    });
                    await refresh();
                    return M.operasyon.rezervasyonKaydedildi(durumAdi('DRAFT'));
                  })
                }
              >
                {M.operasyon.rezervasyonKaydet}
              </button>) },
          { key: 'TALEP', element: (<button
                type="button"
                className={sinif('TALEP')}
                data-testid={`booking-request-${index}`}
                disabled={engeller.talep !== null}
                title={engeller.talep ?? undefined}
                onClick={() =>
                  void run(async () => {
                    if (!booking) throw new Error(M.operasyon.rezervasyonUyari);
                    await api.transitionBooking(booking.id, { status: 'REQUESTED', expectedVersion: booking.version });
                    await refresh();
                    return M.operasyon.talepEdildi;
                  })
                }
              >
                {M.operasyon.talepEt}
              </button>) },
          { key: 'TEYIT', element: (<button
                type="button"
                className={sinif('TEYIT')}
                data-testid={`booking-confirm-${index}`}
                disabled={engeller.teyit !== null}
                title={engeller.teyit ?? undefined}
                onClick={() =>
                  void run(async () => {
                    if (!booking) throw new Error(M.operasyon.rezervasyonUyari);
                    await api.transitionBooking(booking.id, {
                      status: 'CONFIRMED',
                      expectedVersion: booking.version,
                      supplierReference: references[task.id] ?? '',
                    });
                    await refresh();
                    return M.operasyon.teyitKaydedildi;
                  })
                }
              >
                {M.operasyon.referansIleTeyitEt}
              </button>) },
          { key: 'VOUCHER', element: (<button
                type="button"
                className={sinif('VOUCHER')}
                data-testid={`task-issue-voucher-${index}`}
                disabled={engeller.voucher !== null}
                title={engeller.voucher ?? undefined}
                onClick={() =>
                  void run(async () => {
                    const travellerName = yolcuAdi.trim();
                    if (!travellerName) throw new Error(M.operasyon.voucherYolcuUyarisi);
                    const voucher = await api.issueVoucher(task.id, {
                      expectedTaskVersion: task.version,
                      travellerName,
                      instructions: M.operasyon.voucherYonergesi,
                    });
                    await refresh();
                    return M.operasyon.voucherDuzenlendi(voucher.code);
                  })
                }
              >
                {M.operasyon.voucherDuzenle}
              </button>) },
          { key: 'TAMAMLA', element: (<button
                type="button"
                className={sinif('TAMAMLA')}
                data-testid={`task-fulfil-${index}`}
                disabled={engeller.tamamla !== null}
                title={engeller.tamamla ?? undefined}
                onClick={() =>
                  void run(async () => {
                    await api.transitionTask(task.id, { status: 'FULFILLED', expectedVersion: task.version });
                    await refresh();
                    return M.operasyon.tamamlandi(task.code);
                  })
                }
              >
                {M.operasyon.tamamlandiIsaretle}
              </button>) },
        ];
        const stage = task.status === 'FULFILLED' ? 5 : ({ AYRINTILAR: 0, BASLAT: 0, REZERVASYON: 1, TALEP: 1, TEYIT: 2, VOUCHER: 3, TAMAMLA: 4 } as const)[adim ?? 'AYRINTILAR'];
        return (
          <article key={task.id} className="gorev-karti" data-testid={`task-${index}`}>
            <div className="ops-task-title"><div><span className="eyebrow" data-testid={`task-code-${index}`}>{task.code}</span><h3>{task.orderItem?.description || orders.find((order) => order.id === task.orderId)?.items.find((item) => item.id === task.orderItemId)?.description || task.code}</h3></div><span className="ops-state">{durumAdi(task.status)}</span></div>
            <ol className="ops-stepper" aria-label={D.nextStep}>{[D.prep, D.booking, D.confirmation, D.voucher, D.done].map((label, step) => <li key={label} className={step < stage ? 'complete' : step === stage ? 'current' : ''} aria-current={step === stage ? 'step' : undefined}><span>{step < stage ? '✓' : step + 1}</span>{label}</li>)}</ol>
            {/* Sıradaki adım kartın en üstünde, düğmeleri aramadan önce okunacak yerde. */}
            <p className="callout sonraki-adim" data-testid={`task-next-step-${index}`}>
              <strong>{M.operasyon.sonrakiAdimBasligi}:</strong>{' '}
              {adim ? adimAdi[adim] : M.operasyon.sonrakiAdimYok}
            </p>


            <div className="ops-task-body"><div className="ops-task-main">
            {task.requiredDetails.packageIdentity ? <div className="stat-grid" data-testid={`task-package-identity-${index}`}>
              <Stat label={D.originalProduct} testId={`task-original-product-${index}`}>{task.requiredDetails.packageIdentity.originalName}</Stat>
              <Stat label={D.catalogueCode} testId={`task-product-code-${index}`}>{task.requiredDetails.packageIdentity.catalogueCode}</Stat>
              <Stat label={D.quotedProduct} testId={`task-quoted-product-${index}`}>{task.requiredDetails.packageIdentity.quotedName}</Stat>
              {task.requiredDetails.packageIdentity.notes ? <Stat label={D.salesNote} testId={`task-sales-note-${index}`}>{task.requiredDetails.packageIdentity.notes}</Stat> : null}
            </div> : null}
            {/*
              Ön şart alanları kartın **başında**: yolcu adı ve tedarikçi referansı olmadan
              zincir zaten yürümüyor, ve ölçümde ikisi de etiketsiz boş kutulardı.
            */}
            <div className="alan-satiri">
              <Alan etiket={M.operasyon.yolcuAdiEtiketi} ipucu={M.operasyon.yolcuAdiIpucu} zorunlu>
                <input
                  data-testid={`task-traveller-${index}`}
                  value={yolcuAdi}
                  maxLength={200}
                  onChange={(event) => setTravellers((current) => ({ ...current, [task.id]: event.target.value }))}
                />
              </Alan>
              <Alan etiket={M.operasyon.referansEtiketi}>
                <input
                  data-testid={`booking-reference-input-${index}`}
                  value={references[task.id] ?? ''}
                  maxLength={80}
                  onChange={(event) => setReferences((current) => ({ ...current, [task.id]: event.target.value }))}
                />
              </Alan>


            </div>

            {/* Zincirin adımları, uçtaki sırayla ve tek birincil düğmeyle. */}
            <div className="gorev-adimlari">{actions.filter((action) => action.key === adim).map((action) => <div key={action.key}>{action.element}</div>)}</div>


            {/*
              Zincirin dışındaki eylemler ayrı bir kümede: bunlar sıranın adımları değil,
              her an yapılabilen yan işler. Hiçbiri birincil değil ve olmamalı.
            */}

            <div className="ops-task-support">
            <details className="operational-record" data-testid={`task-notes-${index}`}>
              <summary>{D.notes}<span>{task.notes?.length ?? 0}</span></summary>
              {task.notes?.length ? <ol>{task.notes.map((note) => <li key={note.id}>{M.operasyon.notSatiri(note.body, note.author?.displayName ?? M.genel.sistem)}</li>)}</ol> : <p>{M.operasyon.notYok}</p>}
              <Alan etiket={M.operasyon.yeniNotEtiketi} ipucu={M.operasyon.notIpucu}>
                <textarea data-testid={`task-note-input-${index}`} maxLength={1000} value={taskNotes[task.id] ?? ''} onChange={(event) => setTaskNotes((current) => ({ ...current, [task.id]: event.target.value }))} />
              </Alan>
              <button type="button" className="ikincil" data-testid={`task-add-note-${index}`} disabled={!taskNotes[task.id]?.trim()} title={taskNotes[task.id]?.trim() ? undefined : E.notBos} onClick={() => void run(async () => {
                const body = taskNotes[task.id]?.trim(); if (!body) throw new Error(M.operasyon.notBos);
                await api.addTaskNote(task.id, body); setTaskNotes((current) => ({ ...current, [task.id]: '' })); await refresh(); return M.operasyon.notKaydedildi;
              })}>{M.operasyon.notEkle}</button>
            </details>
            <details className="operational-record" data-testid={`task-documents-${index}`}>
              <summary>{D.files}<span>{task.documents?.length ?? 0}</span></summary>
              {task.documents?.length ? <ul>{task.documents.map((document) => <li key={document.id}><button type="button" className="link-button" data-testid={`task-document-download-${document.id}`} onClick={() => void run(async () => { await api.downloadTaskDocument(document.id, document.fileName); return M.operasyon.belgeIndirildi(document.fileName); })}>{document.fileName}</button> · {M.operasyon.belgeBoyutu(tamSayi(document.byteSize))}</li>)}</ul> : <p>{M.operasyon.belgeYok}</p>}
              <label className="ops-file-drop">
                <input key={taskFiles[task.id]?.name ?? 'empty'} aria-label={M.operasyon.belgeSecEtiketi} data-testid={`task-document-input-${index}`} type="file" accept="application/pdf,image/png,image/jpeg,text/plain" onChange={(event) => setTaskFiles((current) => ({ ...current, [task.id]: event.target.files?.[0] }))} />
                <span className="ops-upload-icon" aria-hidden="true">↑</span><strong>{taskFiles[task.id]?.name ?? D.upload}</strong><small>{D.uploadHint}</small>
              </label>
              {taskFiles[task.id] ? <button type="button" className="ikincil" onClick={() => setTaskFiles((current) => ({ ...current, [task.id]: undefined }))}>{D.removeFile}</button> : null}
              <button type="button" className="ikincil" data-testid={`task-document-upload-${index}`} disabled={!taskFiles[task.id]} title={taskFiles[task.id] ? undefined : E.belgeSecilmedi} onClick={() => void run(async () => {
                const file = taskFiles[task.id]; if (!file) throw new Error(M.operasyon.belgeSecUyari);
                const contentBase64 = await new Promise<string>((resolve, reject) => { const reader = new FileReader(); reader.onerror = () => reject(new Error(M.operasyon.belgeOkunamadi)); reader.onload = () => resolve(String(reader.result).split(',')[1] ?? ''); reader.readAsDataURL(file); });
                await api.uploadTaskDocument({ orderId: task.orderId, fulfilmentTaskId: task.id, kind: 'SUPPLIER_CONFIRMATION', fileName: file.name, contentType: file.type || 'application/octet-stream', contentBase64 });
                setTaskFiles((current) => ({ ...current, [task.id]: undefined })); await refresh(); return M.operasyon.belgeYuklendi(file.name, task.code);
              })}>{M.operasyon.belgeYukle}</button>
            </details>
            </div>
            {/*
              Vize belgesi üretimi belge listesinin **hemen altında**: üretilen PDF yukarıdaki
              listeye düşüyor ve iki kutunun arasında bir mesafe olması onların ayrı işler
              olduğunu ima ederdi.
            */}
            </div><aside className="ops-task-side"><details className="ops-task-disclosure ops-booking-metadata" data-testid={`task-booking-details-${index}`}><summary>{D.bookingDetails}</summary>            <div className="stat-grid ops-task-metadata">
              <Stat label={M.operasyon.kartEtiketleri.kalem} testId={`task-item-${index}`}>{task.orderItem?.description ?? ''}</Stat>
              <Stat label={M.operasyon.kartEtiketleri.durum} testId={`task-status-${index}`}>{durumAdi(task.status)}</Stat>
              <Stat label={M.operasyon.kartEtiketleri.atanan} testId={`task-assignee-${index}`}>{task.assignedTo?.displayName ?? M.genel.atanmamis}</Stat>
              <Stat label={M.operasyon.kartEtiketleri.sonTarih} testId={`task-due-${index}`}>{task.dueAt ? tarihSaat(task.dueAt) : M.operasyon.sonTarihYok}</Stat>
              <Stat label={M.operasyon.kartEtiketleri.basariImasi} testId={`task-implies-success-${index}`}>
                {['BLOCKED', 'FAILED', 'CANCELLED'].includes(task.status) ? M.operasyon.basariImaEdilmiyor : M.operasyon.suruyor}
              </Stat>
              <Stat label={M.operasyon.kartEtiketleri.gerekce} testId={`task-reason-${index}`}>{task.blockedReason ?? task.failureReason ?? TANIMSIZ}</Stat>
              <Stat label={M.operasyon.kartEtiketleri.bekleyenKontrol} testId={`task-checklist-pending-${index}`}>{tamSayi(task.checklist.filter((entry) => entry.state === 'PENDING').length)}</Stat>
              <Stat label={M.operasyon.kartEtiketleri.rezervasyonDurumu} testId={`booking-status-${index}`}>{durumYaz(booking?.status)}</Stat>
              <Stat label={M.operasyon.kartEtiketleri.rezervasyonReferansi} testId={`booking-reference-${index}`}>{booking?.supplierReference ?? TANIMSIZ}</Stat>
              <Stat label={M.operasyon.kartEtiketleri.voucher} testId={`task-voucher-${index}`}>{task.voucher?.code ?? TANIMSIZ}</Stat>
            </div></details><details className="ops-task-disclosure" data-testid={`task-all-actions-${index}`}><summary>{D.allActions}</summary><div className="ops-action-options">{actions.filter((action) => action.key !== adim).map((action) => <div key={action.key}>{action.element}</div>)}</div></details><details className="ops-task-disclosure" data-testid={`task-assignment-${index}`}><summary>{D.management}</summary><div className="alan-satiri">        <Alan etiket={M.operasyon.atananEtiketi} ipucu={M.operasyon.atananIpucu}>
          <input data-testid="task-assignee-input" value={assigneeId} onChange={(event) => setAssigneeId(event.target.value)} />
        </Alan>
        <Alan etiket={M.operasyon.sonTarihEtiketi}>
          <TarihSaatSecici deger={dueAt} setDeger={setDueAt} testId="task-due-at-input" />
        </Alan>              <Alan etiket={M.operasyon.gerekceEtiketi}>
                <input
                  data-testid={`task-reason-input-${index}`}
                  value={reasons[task.id] ?? ''}
                  maxLength={200}
                  onChange={(event) => setReasons((current) => ({ ...current, [task.id]: event.target.value }))}
                />
              </Alan>              <Alan etiket={M.operasyon.oncelikEtiketi}>
                <select data-testid={`task-priority-input-${index}`} value={priorities[task.id] ?? task.priority ?? 'NORMAL'} onChange={(event) => setPriorities((current) => ({ ...current, [task.id]: event.target.value as 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT' }))}>
                  {PRIORITY_CODES.map((priority) => <option key={priority} value={priority}>{oncelikAdi(priority)}</option>)}
                </select>
              </Alan></div><div className="gorev-yan-eylemler">
              <button
                type="button"
                className="ikincil"
                data-testid={`task-block-${index}`}
                disabled={engeller.bloke !== null}
                title={engeller.bloke ?? undefined}
                onClick={() =>
                  void run(async () => {
                    await api.transitionTask(task.id, { status: 'BLOCKED', expectedVersion: task.version, reason: reasons[task.id] ?? '' });
                    await refresh();
                    return M.operasyon.blokeEdildi(task.code);
                  })
                }
              >
                {M.operasyon.blokeEt}
              </button>
              <button type="button" className="ikincil" data-testid={`task-save-operations-${index}`} onClick={() => void run(async () => {
                await api.updateTaskOperations(task.id, { expectedVersion: task.version, priority: priorities[task.id] ?? task.priority ?? 'NORMAL', dueAt: dueAt ? new Date(dueAt).toISOString() : undefined });
                await refresh(); return M.operasyon.oncelikKaydedildi(task.code);
              })}>{M.operasyon.oncelikKaydet}</button>
              <button type="button" className="ikincil" data-testid={`task-assign-${index}`} onClick={() => void run(async () => {
                await api.assignTask(task.id, { assignedToId: assigneeId, expectedVersion: task.version, dueAt: dueAt || undefined });
                await refresh(); return M.operasyon.atandi(task.code);
              })}>{M.operasyon.atamaYap}</button>
            </div>

            </details>
            <VizeBelgeUretimi task={task} index={index} yolcuAdi={yolcuAdi} yazabilir={belgeYazabilir} refresh={refresh} />
            </aside></div>
          </article>
        );
      })}

    </section>
  );
}

/**
 * "Belge üretici": siparişten ve görevden bağımsız vize belgesi üretimi (10.09.2026).
 *
 * Ürün Sahibi: *"Sipariş şu anda WhatsApp'tan geliyor; CRM'in içinden belgeyi siparişsiz
 * üretebilmem lazım. Oraya belge üretici gibi konumlandır, tam entegrasyonu sonra yaparız."*
 *
 * Görev kartındaki üreticiyle **aynı form** çiziliyor (`VizeBelgeAlanlari`) ve aynı gövde
 * kuruluyor; ayrı olan tek şey nereye yazıldığı: burada üretilen belge hiçbir siparişe
 * bağlanmıyor ve ekranda bu yazılı duruyor.
 *
 * İzin `visa-document:generate`, `document:write` değil: bu iş bir siparişin belgelerine yazmak
 * değil, sipariş olmadan belge basmak. İzni ADMIN de tutuyor ve gerekçesi `permissions.ts`
 * içinde yazılı.
 */
function SerbestBelgeUreticiBolumu({ me }: { me: Me }) {
  const S = M.operasyon.serbestBelge;
  const VB = M.operasyon.vizeBelgeleri;
  const { error, notice, run } = useNotice();
  const [form, setForm] = useState<VizeBelgeFormu>(() => bosVizeFormu(''));
  const [customerId, setCustomerId] = useState('');
  const [customers, setCustomers] = useState<CustomerListItem[]>([]);
  const [belgeler, setBelgeler] = useState<SerbestBelge[]>([]);
  // Sayfalama (11.09.2026): liste 20'şer geliyor; üretimden sonra ilk sayfaya dönülüyor ki yeni
  // belge görünsün. Toplam uçtan geliyor, sayfa sayısı buradan türetiliyor.
  const [sayfa, setSayfa] = useState(1);
  const [toplam, setToplam] = useState(0);
  const [calisiyor, setCalisiyor] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const [anahtarlar, setAnahtarlar] = useState<string | null>(null);
  const [sonUretilen, setSonUretilen] = useState<VizeBelgeUretimSonucu | null>(null);
  const yaz = (yama: Partial<VizeBelgeFormu>) => setForm((mevcut) => ({ ...mevcut, ...yama }));

  const listeyiYukle = useCallback(async (istenen: number = 1) => {
    const sonuc = await api.serbestVizeBelgeleri({ page: istenen, pageSize: S.sayfaBoyu });
    setBelgeler(sonuc.items);
    setToplam(sonuc.total);
    setSayfa(sonuc.page);
  }, [S.sayfaBoyu]);
  const sayfaSayisi = Math.max(1, Math.ceil(toplam / S.sayfaBoyu));

  useEffect(() => {
    void run(async () => {
      // Müşteri listesi bağ kurmak için; boş kalması da geçerli bir sonuç ve üretimi engellemiyor.
      const [musteriler] = await Promise.all([api.customers({ pageSize: 100 }), listeyiYukle()]);
      setCustomers(musteriler.items);
    });
  }, [listeyiYukle, run]);

  const yazabilir = me.permissions.includes('visa-document:generate');
  const engel = vizeEngeli(form, yazabilir, calisiyor);

  const uret = async () => {
    setCalisiyor(true);
    setHata(null);
    setAnahtarlar(null);
    setSonUretilen(null);
    try {
      const govde = vizeIstekGovdesi(form);
      const sonuc = await api.uretSerbestVizeBelgesi(customerId ? { ...govde, customerId } : govde);
      setSonUretilen(sonuc);
      await listeyiYukle();
    } catch (yakalanan) {
      setHata(describe(yakalanan));
      if (yakalanan instanceof ApiError && yakalanan.status === 400) setAnahtarlar(vizeAnahtarSatiri(form));
    } finally {
      setCalisiyor(false);
    }
  };

  const musteriAdi = (id: string | null) => customers.find((entry) => entry.id === id)?.displayName ?? id ?? '';

  return (
    <section data-testid="standalone-document-panel">
      <h2>{S.baslik}</h2>
      <p>{S.aciklama}</p>
      <p className="callout" data-testid="standalone-document-warning">
        {S.uyari}
      </p>
      <Notices scope="standalone-document" error={error} notice={notice} />
      {hata ? (
        <p className="error callout" role="alert" aria-live="assertive" data-testid="standalone-visa-error">
          {hata}
        </p>
      ) : null}
      {anahtarlar ? (
        <p className="callout" data-testid="standalone-visa-keys">
          {anahtarlar}
        </p>
      ) : null}
      {sonUretilen ? (
        <p className="callout callout-ok" role="status" data-testid="standalone-visa-notice">
          {sonUretilen.belgeler.length > 1
            ? S.uretildiCoklu(sonUretilen.belgeler.map((belge) => belge.fileName).join(', '))
            : S.uretildi(sonUretilen.fileName)}{' '}
          {sonUretilen.belgeler.map((belge, sira) => (
            <button
              key={belge.documentId}
              type="button"
              className="link-button"
              data-testid={sira === 0 ? 'standalone-visa-download' : `standalone-visa-download-${sira + 1}`}
              onClick={() =>
                void run(async () => {
                  await api.downloadTaskDocument(belge.documentId, belge.fileName);
                  return VB.indirildi(belge.fileName);
                })
              }
            >
              {sonUretilen.belgeler.length > 1 ? `${S.indir}: ${belge.fileName}` : S.indir}
            </button>
          ))}
        </p>
      ) : null}
      <VizeBelgeAlanlari form={form} yaz={yaz} id={(ad) => `standalone-visa-${ad}`} />
      <details className="ops-document-options ops-document-customer">
      <summary>{S.musteriEtiketi}</summary>
      <Alan etiket={S.musteriEtiketi} ipucu={S.musteriIpucu}>
        <select
          data-testid="standalone-visa-customer"
          value={customerId}
          onChange={(event) => setCustomerId(event.target.value)}
        >
          <option value="">{S.musteriSecilmedi}</option>
          {customers.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.displayName}
            </option>
          ))}
        </select>
      </Alan>
      </details>
      <button
        type="button"
        className="birincil"
        data-testid="standalone-visa-generate"
        disabled={engel !== null}
        title={engel ?? undefined}
        onClick={() => void uret()}
      >
        {calisiyor ? S.uretiliyor : S.uret}
      </button>
      {engel ? <p className="ops-document-hint" role="status">{engel}</p> : null}

      <h3>{S.sonUretilenler}</h3>
      <button
        type="button"
        className="ikincil"
        data-testid="standalone-documents-refresh"
        onClick={() =>
          void run(async () => {
            await listeyiYukle(sayfa);
            return S.yenilendi;
          })
        }
      >
        {S.yenile}
      </button>
      {belgeler.length === 0 ? (
        <p data-testid="standalone-documents-empty">{S.yok}</p>
      ) : (
        <ul>
          {belgeler.map((belge) => (
            <li key={belge.id} data-testid="standalone-document-row">
              <button
                type="button"
                className="link-button"
                data-testid={`standalone-document-download-${belge.id}`}
                onClick={() =>
                  void run(async () => {
                    await api.downloadTaskDocument(belge.id, belge.fileName);
                    return VB.indirildi(belge.fileName);
                  })
                }
              >
                {belge.fileName}
              </button>{' '}
              ·{' '}
              {S.satirAyrinti(
                VB.turAdi[belge.kind as keyof typeof VB.turAdi] ?? belge.kind,
                tarihSaat(belge.uploadedAt),
                belge.customerId ? S.musteriyeBagli(musteriAdi(belge.customerId)) : S.musteriyeBagliDegil,
              )}
            </li>
          ))}
        </ul>
      )}
      {toplam > 0 ? (
        <div className="pagination ops-pagination">
          <p data-testid="standalone-documents-total">
            {`${tamSayi(toplam)} ${S.sayfaEki} · ${ORTAK.sayfa} ${tamSayi(sayfa)}/${tamSayi(sayfaSayisi)}`}
          </p>
          <div>
            <button
              type="button"
              className="ikincil"
              data-testid="standalone-documents-prev"
              disabled={sayfa <= 1}
              onClick={() => void run(() => listeyiYukle(sayfa - 1))}
            >
              {ORTAK.onceki}
            </button>
            <button
              type="button"
              className="ikincil"
              data-testid="standalone-documents-next"
              disabled={sayfa >= sayfaSayisi}
              onClick={() => void run(() => listeyiYukle(sayfa + 1))}
            >
              {ORTAK.sonraki}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

/**
 * Operasyon bölümünün "Vize Belgeleri" alt görünümü.
 *
 * Görev kartlarını taşıyan panelin dışında ve kendi izniyle (`document:read`) çiziliyor.
 * Gerekçe `SalesWorkflowPanel`deki iki bölümün gerekçesiyle aynı: panelin kapısı
 * `fulfilment:write` ve bu listeyi okuması gereken rol o yetkiyi tutmuyor. ADMIN üretilmiş
 * belgeleri görür, üretmez.
 *
 * Liste **görevlerden** kuruluyor, ayrı bir uçtan değil: `/documents?orderId` belgeleri
 * döndürüyor ama görev kodunu döndürmüyor, ve bir vize belgesinin hangi göreve ait olduğu
 * listedeki en önemli sütun. Süzgeç `DocumentAsset.kind`: dosya adına bakmak bir kural değil,
 * bir tahmin olurdu.
 */
function VizeBelgeleriBolumu({ me }: { me: Me }) {
  const VB = M.operasyon.vizeBelgeleri;
  const { error, notice, run } = useNotice();
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [orderId, setOrderId] = useState('');
  const [tasks, setTasks] = useState<FulfilmentTask[]>([]);

  const loadTasks = useCallback(async (id: string) => {
    if (!id) {
      setTasks([]);
      return;
    }
    const page = await api.tasks(id);
    setTasks(page.items);
  }, []);

  useEffect(() => {
    void run(async () => {
      const page = await api.orders();
      setOrders(page.items);
      const first = page.items[0];
      if (first) {
        setOrderId(first.id);
        await loadTasks(first.id);
      }
    });
  }, [loadTasks, run]);

  const satirlar = useMemo(() => {
    const kindler: readonly string[] = VIZE_BELGE_KINDLERI;
    return tasks
      .flatMap((task) =>
        (task.documents ?? [])
          .filter((document) => kindler.includes(document.kind))
          .map((document) => ({ document, taskCode: task.code })),
      )
      .sort((a, b) => b.document.uploadedAt.localeCompare(a.document.uploadedAt));
  }, [tasks]);

  return (
    <section data-testid="visa-documents-panel">
      <h2>{VB.baslik}</h2>
      <p>{VB.aciklama}</p>
      {me.permissions.includes('document:write') ? null : (
        <p className="callout" data-testid="visa-documents-read-only">
          {VB.saltOkunur}
        </p>
      )}
      <Notices scope="visa-documents" error={error} notice={notice} />
      <Alan etiket={VB.siparisEtiketi}>
        <select
          data-testid="visa-documents-order"
          value={orderId}
          onChange={(event) =>
            void run(async () => {
              setOrderId(event.target.value);
              await loadTasks(event.target.value);
            })
          }
        >
          <option value="">{M.genel.siparisSecin}</option>
          {orders.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {M.genel.kodDurum(entry.code, durumAdi(entry.status))}
            </option>
          ))}
        </select>
      </Alan>
      <button
        type="button"
        className="ikincil"
        data-testid="visa-documents-refresh"
        onClick={() =>
          void run(async () => {
            await loadTasks(orderId);
            return VB.yenilendi;
          })
        }
      >
        {VB.yenile}
      </button>
      {satirlar.length === 0 ? (
        <p data-testid="visa-documents-empty">{VB.yok}</p>
      ) : (
        <ul>
          {satirlar.map(({ document, taskCode }) => (
            <li key={document.id} data-testid="visa-document-row">
              <button
                type="button"
                className="link-button"
                data-testid={`visa-document-download-${document.id}`}
                onClick={() =>
                  void run(async () => {
                    await api.downloadTaskDocument(document.id, document.fileName);
                    return VB.indirildi(document.fileName);
                  })
                }
              >
                {document.fileName}
              </button>{' '}
              ·{' '}
              {VB.satirAyrinti(
                taskCode,
                VB.turAdi[document.kind as keyof typeof VB.turAdi] ?? document.kind,
                tarihSaat(document.uploadedAt),
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * CRM denetimi B6: tedarikçi iptal kuyruğu (`api/src/modules/operations/supplier-cancellation.service.ts`).
 * Backend testli (`integration/supplier-cancellations.spec.ts`, `security/supplier-cancellation-rbac.spec.ts`),
 * bu ekranın açılışına kadar hiçbir yerden çağrılmıyordu.
 */
function SupplierCancellationsSection() {
  const { error, notice, run } = useNotice();
  const [state, setState] = useState('OPEN');
  const [items, setItems] = useState<SupplierCancellation[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<'SUBSTITUTED' | 'CANCELLED'>('SUBSTITUTED');
  const [decisionNote, setDecisionNote] = useState('');
  const [replacement, setReplacement] = useState('');
  const [form, setForm] = useState({ orderReference: '', customerId: '', supplierCode: '', productId: '', departureId: '', departureOn: '', reason: '' });

  const load = useCallback(
    (targetState: string) =>
      run(async () => {
        const page = await api.supplierCancellations({ state: targetState, pageSize: 50 });
        setItems(page.items);
      }),
    [run],
  );

  useEffect(() => {
    void load(state);
  }, [state, load]);

  const selected = items.find((item) => item.id === selectedId) ?? null;

  const decide = () =>
    void run(async () => {
      if (!selected) return;
      await api.decideSupplierCancellation(selected.id, {
        outcome,
        note: decisionNote,
        ...(outcome === 'SUBSTITUTED' ? { replacementOrderReference: replacement.trim() } : {}),
      });
      const reference = selected.orderReference;
      setSelectedId(null);
      setDecisionNote('');
      setReplacement('');
      await load(state);
      return M.tedarikciIptali.kararVerildi(reference);
    });

  const report = () =>
    void run(async () => {
      const created = await api.reportSupplierCancellation({
        orderReference: form.orderReference.trim(),
        customerId: form.customerId.trim(),
        supplierCode: form.supplierCode.trim(),
        reason: form.reason.trim(),
        ...(form.productId.trim() ? { productId: form.productId.trim() } : {}),
        ...(form.departureId.trim() ? { departureId: form.departureId.trim() } : {}),
        ...(form.departureOn ? { departureOn: new Date(form.departureOn).toISOString() } : {}),
      });
      setForm({ orderReference: '', customerId: '', supplierCode: '', productId: '', departureId: '', departureOn: '', reason: '' });
      await load(state);
      return M.tedarikciIptali.bildirildi(created.orderReference);
    });

  return (
    <section data-testid="supplier-cancellations-panel">
      <h3>{M.tedarikciIptali.baslik}</h3>
      <p>{M.tedarikciIptali.aciklama}</p>
      <Notices scope="supplier-cancellations" error={error} notice={notice} />
      <Alan etiket={M.tedarikciIptali.durumFiltresi}>
        <select
          data-testid="supplier-cancellations-state"
          value={state}
          onChange={(event) => {
            setState(event.target.value);
            setSelectedId(null);
          }}
        >
          <option value="OPEN">{durumAdi('OPEN')}</option>
          <option value="SUBSTITUTED">{durumAdi('SUBSTITUTED')}</option>
          <option value="CANCELLED">{durumAdi('CANCELLED')}</option>
        </select>
      </Alan>
      {items.length === 0 ? (
        <p className="muted" data-testid="supplier-cancellations-empty">
          {M.tedarikciIptali.listeYok}
        </p>
      ) : (
        <div className="table-wrap">
          <table data-testid="supplier-cancellations-table">
            <thead>
              <tr>
                <th>{M.tedarikciIptali.sutun.siparis}</th>
                <th>{M.tedarikciIptali.sutun.tedarikci}</th>
                <th>{M.tedarikciIptali.sutun.neden}</th>
                <th>{M.tedarikciIptali.sutun.bildirilme}</th>
                <th>{M.tedarikciIptali.sutun.durum}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} data-testid="supplier-cancellation-row">
                  <td>
                    {item.state === 'OPEN' ? (
                      <button type="button" data-testid="supplier-cancellation-open" onClick={() => setSelectedId(item.id)}>
                        {item.orderReference}
                      </button>
                    ) : (
                      item.orderReference
                    )}
                  </td>
                  <td>{item.supplierCode}</td>
                  <td>{item.reason}</td>
                  <td>{tarihSaat(item.reportedAt)}</td>
                  <td>
                    {durumAdi(item.state)}
                    {item.gezipuan ? ` · ${M.tedarikciIptali.gezipuanGeriAlindi(para(item.gezipuan.clawedBackMinor, item.gezipuan.currency))}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected ? (
        <div className="card" data-testid="supplier-cancellation-decision">
          <h4>
            {M.tedarikciIptali.kararBasligi}: {selected.orderReference}
          </h4>
          <Alan etiket={M.tedarikciIptali.sonuc}>
            <select data-testid="cancellation-outcome" value={outcome} onChange={(event) => setOutcome(event.target.value as 'SUBSTITUTED' | 'CANCELLED')}>
              <option value="SUBSTITUTED">{M.tedarikciIptali.ikameEdildi}</option>
              <option value="CANCELLED">{M.tedarikciIptali.iptalEdildi}</option>
            </select>
          </Alan>
          {outcome === 'SUBSTITUTED' ? (
            <Alan etiket={M.tedarikciIptali.ikameSiparis} zorunlu>
              <input data-testid="cancellation-replacement" value={replacement} onChange={(event) => setReplacement(event.target.value)} />
            </Alan>
          ) : null}
          <Alan etiket={M.tedarikciIptali.kararNotu} zorunlu>
            <textarea data-testid="cancellation-note" value={decisionNote} onChange={(event) => setDecisionNote(event.target.value)} />
          </Alan>
          <button
            type="button"
            data-testid="cancellation-decide"
            disabled={!decisionNote.trim() || (outcome === 'SUBSTITUTED' && !replacement.trim())}
            onClick={decide}
          >
            {M.tedarikciIptali.kararVer}
          </button>
        </div>
      ) : null}

      <h4>{M.tedarikciIptali.yeniBasligi}</h4>
      <AlanSatiri>
        <Alan etiket={M.tedarikciIptali.alan.siparisReferansi} zorunlu>
          <input data-testid="cancellation-order-ref" value={form.orderReference} onChange={(event) => setForm((current) => ({ ...current, orderReference: event.target.value }))} />
        </Alan>
        <Alan etiket={M.tedarikciIptali.alan.musteriId} zorunlu>
          <input data-testid="cancellation-customer-id" value={form.customerId} onChange={(event) => setForm((current) => ({ ...current, customerId: event.target.value }))} />
        </Alan>
        <Alan etiket={M.tedarikciIptali.alan.tedarikciKodu} zorunlu>
          <input data-testid="cancellation-supplier-code" value={form.supplierCode} onChange={(event) => setForm((current) => ({ ...current, supplierCode: event.target.value }))} />
        </Alan>
      </AlanSatiri>
      <AlanSatiri>
        <Alan etiket={M.tedarikciIptali.alan.urunId}>
          <input data-testid="cancellation-product-id" value={form.productId} onChange={(event) => setForm((current) => ({ ...current, productId: event.target.value }))} />
        </Alan>
        <Alan etiket={M.tedarikciIptali.alan.kalkisId}>
          <input data-testid="cancellation-departure-id" value={form.departureId} onChange={(event) => setForm((current) => ({ ...current, departureId: event.target.value }))} />
        </Alan>
        <Alan etiket={M.tedarikciIptali.alan.kalkisTarihi}>
          <input data-testid="cancellation-departure-on" type="date" value={form.departureOn} onChange={(event) => setForm((current) => ({ ...current, departureOn: event.target.value }))} />
        </Alan>
      </AlanSatiri>
      <Alan etiket={M.tedarikciIptali.alan.neden} zorunlu>
        <textarea data-testid="cancellation-reason" value={form.reason} onChange={(event) => setForm((current) => ({ ...current, reason: event.target.value }))} />
      </Alan>
      <button
        type="button"
        data-testid="cancellation-report"
        disabled={!form.orderReference.trim() || !form.customerId.trim() || !form.supplierCode.trim() || form.reason.trim().length < 4}
        onClick={report}
      >
        {M.tedarikciIptali.bildir}
      </button>
    </section>
  );
}

/**
 * CRM denetimi B7: vize talebi kuyruğu (`api/src/modules/operations/visa.service.ts`). v3 talebi
 * açar (makine ucu, burada yok); operatör Arkan'a gönderir, sonucu kaydeder, sözleşme dışı ücret
 * ekler. Maliyet bloğu yalnız `finance:read` tutan aktöre geliyor — uç göndermezse burada `null`.
 */
function VisaRequestsSection({ me, orderReference = '' }: { me: Me; orderReference?: string }) {
  const { error, notice, run } = useNotice();
  const [state, setState] = useState(orderReference ? '' : 'RECEIVED');
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<VisaRequest[]>([]);
  const listRequest = useRef(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selection = useRef<{ id: string | null; generation: number }>({ id: null, generation: 0 });
  const selectCase = (id: string | null) => {
    if (selection.current.id === id) return;
    selection.current = { id, generation: selection.current.generation + 1 };
    setSelectedId(id);
    setDetail(null);
    setFeeCode(''); setFeeQuantity('1'); setFeeAmount('');
    setSupplierReference(''); setChargedMinor(''); setDecisionNote('');
  };
  const stillSelected = (id: string, generation: number) => selection.current.id === id && selection.current.generation === generation;
  const [detail, setDetail] = useState<VisaRequest | null>(null);
  const [outcome, setOutcome] = useState<'ISSUED' | 'REJECTED'>('ISSUED');
  const [supplierReference, setSupplierReference] = useState('');
  const [chargedMinor, setChargedMinor] = useState('');
  const [decisionNote, setDecisionNote] = useState('');
  const [feeCode, setFeeCode] = useState('');
  const [feeQuantity, setFeeQuantity] = useState('1');
  const [feeAmount, setFeeAmount] = useState('');

  const load = useCallback(
    (targetState: string) =>
      run(async () => {
        const request = ++listRequest.current;
        const page = await api.visaRequests({ state: targetState || undefined, orderReference: orderReference || undefined, pageSize: 50 });
        if (request === listRequest.current) setItems(page.items);
      }),
    [run, orderReference],
  );

  useEffect(() => { selectCase(null); setDetail(null); setState(orderReference ? '' : 'RECEIVED'); setQuery(''); }, [orderReference]);
  useEffect(() => {
    setItems([]);
    void load(state);
    return () => { listRequest.current += 1; };
  }, [state, load]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    let active = true;
    setDetail(null);
    void api.visaRequest(selectedId).then(value => { if (active) setDetail(value); }).catch(error => {
      if (active) void run(async () => { throw error; });
    });
    return () => { active = false; };
  }, [selectedId, run]);

  const submit = () =>
    void run(async () => {
      if (!detail) return;
      const reference = detail.orderReference;
      const generation = selection.current.generation;
      await api.submitVisaRequest(detail.id);
      if (!stillSelected(detail.id, generation)) return;
      selectCase(null);
      await load(state);
      return M.vizeTalebi.gonderildi(reference);
    });

  const decide = () =>
    void run(async () => {
      if (!detail) return;
      const reference = detail.orderReference;
      const generation = selection.current.generation;
      await api.decideVisaRequest(detail.id, {
        outcome,
        note: decisionNote,
        ...(outcome === 'ISSUED' ? { supplierReference: supplierReference.trim() } : {}),
        ...(chargedMinor.trim() ? { chargedMinor: paraAltBirime(chargedMinor) } : {}),
      });
      if (!stillSelected(detail.id, generation)) return;
      selectCase(null);
      setSupplierReference('');
      setChargedMinor('');
      setDecisionNote('');
      await load(state);
      return M.vizeTalebi.kararVerildi(reference);
    });

  const addExtraCharge = () =>
    void run(async () => {
      if (!detail) return;
      const generation = selection.current.generation;
      const result = await api.addVisaExtraCharge(detail.id, {
        feeCode: feeCode.trim(),
        ...(feeQuantity.trim() ? { quantity: parseTamSayi(feeQuantity) } : {}),
        ...(feeAmount.trim() ? { amountMinor: paraAltBirime(feeAmount) } : {}),
      });
      if (!stillSelected(detail.id, generation)) return;
      const updated = await api.visaRequest(detail.id);
      if (!stillSelected(detail.id, generation)) return;
      setFeeCode('');
      setFeeQuantity('1');
      setFeeAmount('');
      setDetail(updated);
      return M.vizeTalebi.ekUcretEklendi(result.feeCode, para(result.amountMinor, result.currency));
    });

  const visibleItems = items.filter((item) => `${item.orderReference} ${item.applicantName} ${item.productLabel} ${vizeUrunAdi(item.productCode, item.productLabel)}`.toLocaleLowerCase('tr-TR').includes(query.toLocaleLowerCase('tr-TR')));
  return (
    <section data-testid="visa-requests-panel">
      <h3>{M.vizeTalebi.baslik}</h3>
      <p>{D.visaHint}</p>
      {orderReference ? <p className="visa-order-filter">{M.vizeTalebi.siparisFiltresi(orderReference)} <a href="/operasyon?sekme=visa">{M.vizeTalebi.tumBasvurular}</a></p> : null}
      <Notices scope="visa-requests" error={error} notice={notice} />
      <div className="ops-visa-toolbar"><Alan etiket={D.search}><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={D.searchHint} /></Alan>
      <Alan etiket={M.vizeTalebi.durumFiltresi}>
        <select
          data-testid="visa-requests-state"
          value={state}
          onChange={(event) => {
            setState(event.target.value);
            selectCase(null);
          }}
        >
          {orderReference ? <option value="">{M.vizeTalebi.tumDurumlar}</option> : null}
          <option value="RECEIVED">{durumAdi('RECEIVED')}</option>
          <option value="SUBMITTED">{durumAdi('SUBMITTED')}</option>
          <option value="ISSUED">{durumAdi('ISSUED')}</option>
          <option value="REJECTED">{durumAdi('REJECTED')}</option>
        </select>
      </Alan>
      </div><p className="ops-list-count" role="status">{D.results(visibleItems.length)}</p>
      {visibleItems.length === 0 ? (
        <p className="muted" data-testid="visa-requests-empty">
          {M.vizeTalebi.listeYok}
        </p>
      ) : (
        <div className="table-wrap">
          <table data-testid="visa-requests-table">
            <thead>
              <tr>
                <th>{M.vizeTalebi.sutun.siparis}</th>
                <th>{M.vizeTalebi.sutun.basvuran}</th>
                <th>{M.vizeTalebi.sutun.urun}</th>
                <th>{M.vizeTalebi.sutun.durum}</th>
                <th>{M.vizeTalebi.sutun.alindi}</th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.map((item) => (
                <tr key={item.id} data-testid="visa-request-row">
                  <td>
                    <button type="button" data-testid="visa-request-open" onClick={() => selectCase(item.id)}>
                      {item.orderReference}
                    </button>
                  </td>
                  <td>
                    {item.applicantName} ({durumAdi(item.applicantType)})
                  </td>
                  <td>{vizeUrunAdi(item.productCode, item.productLabel)}</td>
                  <td><span className="ops-visa-state">{durumAdi(item.state)}</span></td>
                  <td data-label={M.vizeTalebi.sutun.alindi}>{tarihSaat(item.receivedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail ? (
        <div className="card" data-testid="visa-request-detail">
          <h4>
            {detail.orderReference} · {detail.applicantName}
          </h4>
          <div className="stat-grid">
            <Stat label={M.vizeTalebi.detay.basvuranTuru} testId="visa-applicant-type">
              {durumAdi(detail.applicantType)}
            </Stat>
            <Stat label={M.vizeTalebi.detay.listeFiyati} testId="visa-list-price">
              {detail.listPrice ? para(detail.listPrice.amountMinor, detail.listPrice.currency) : TANIMSIZ}
            </Stat>
            <Stat label={ORTAK.durum} testId="visa-state">
              {durumAdi(detail.state)}
            </Stat>
            {detail.supplierReference ? (
              <Stat label={M.vizeTalebi.detay.arkanNumarasi} testId="visa-supplier-ref">
                {detail.supplierReference}
              </Stat>
            ) : null}
          </div>

          <p>{vizeUrunAdi(detail.productCode, detail.productLabel)}</p>
          <VisaDocuments key={detail.id} requestId={detail.id} applicantName={detail.applicantName} me={me} />
          {detail.state === 'RECEIVED' ? (
            <button type="button" data-testid="visa-submit" onClick={submit}>
              {M.vizeTalebi.gonder}
            </button>
          ) : null}

          {detail.state === 'SUBMITTED' ? (
            <div>
              <h5>{M.vizeTalebi.kararBasligi}</h5>
              <Alan etiket={ORTAK.durum}>
                <select data-testid="visa-outcome" value={outcome} onChange={(event) => setOutcome(event.target.value as 'ISSUED' | 'REJECTED')}>
                  <option value="ISSUED">{M.vizeTalebi.verildi}</option>
                  <option value="REJECTED">{M.vizeTalebi.reddedildi}</option>
                </select>
              </Alan>
              {outcome === 'ISSUED' ? (
                <Alan etiket={M.vizeTalebi.supplierReferansi} zorunlu>
                  <input data-testid="visa-supplier-reference-input" value={supplierReference} onChange={(event) => setSupplierReference(event.target.value)} />
                </Alan>
              ) : null}
              <Alan etiket={M.vizeTalebi.ucretlendirilen}>
                <input data-testid="visa-charged-minor" value={chargedMinor} onChange={(event) => setChargedMinor(event.target.value)} />
              </Alan>
              <Alan etiket={M.vizeTalebi.kararNotu} zorunlu>
                <textarea data-testid="visa-decision-note" value={decisionNote} onChange={(event) => setDecisionNote(event.target.value)} />
              </Alan>
              <button
                type="button"
                data-testid="visa-decide"
                disabled={!decisionNote.trim() || (outcome === 'ISSUED' && !supplierReference.trim())}
                onClick={decide}
              >
                {M.vizeTalebi.kararVer}
              </button>
            </div>
          ) : null}

          {detail.cost !== undefined ? (
            detail.cost ? (
              <div data-testid="visa-cost">
                <h5>{M.vizeTalebi.detay.maliyetBasligi}</h5>
                <div className="stat-grid">
                  <Stat label={M.vizeTalebi.detay.beklenen} testId="visa-cost-expected">
                    {para(detail.cost.expectedMinor, detail.cost.currency)}
                  </Stat>
                  <Stat label={M.vizeTalebi.detay.taahhut} testId="visa-cost-committed">
                    {para(detail.cost.committedMinor, detail.cost.currency)}
                  </Stat>
                  <Stat label={M.vizeTalebi.detay.gerceklesen} testId="visa-cost-realized">
                    {para(detail.cost.realizedMinor, detail.cost.currency)}
                  </Stat>
                  <Stat label={M.vizeTalebi.detay.fark} testId="visa-cost-variance">
                    {detail.cost.varianceMinor === null ? TANIMSIZ : para(detail.cost.varianceMinor, detail.cost.currency)}
                  </Stat>
                </div>
                {detail.state === 'SUBMITTED' || detail.state === 'ISSUED' ? (
                  <div>
                    <h5>{M.vizeTalebi.ekUcretBasligi}</h5>
                    <AlanSatiri>
                      <Alan etiket={M.vizeTalebi.ucretKodu} zorunlu>
                        <input data-testid="visa-fee-code" value={feeCode} onChange={(event) => setFeeCode(event.target.value)} />
                      </Alan>
                      <Alan etiket={M.vizeTalebi.adet}>
                        <input data-testid="visa-fee-quantity" type="number" min={1} value={feeQuantity} onChange={(event) => setFeeQuantity(event.target.value)} />
                      </Alan>
                      <Alan etiket={M.vizeTalebi.tutarEzme}>
                        <input data-testid="visa-fee-amount" value={feeAmount} onChange={(event) => setFeeAmount(event.target.value)} />
                      </Alan>
                    </AlanSatiri>
                    <button type="button" data-testid="visa-fee-add" disabled={!feeCode.trim()} onClick={addExtraCharge}>
                      {M.vizeTalebi.ekUcretEkle}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : (
              <p className="muted" data-testid="visa-cost-hidden">
                {M.vizeTalebi.detay.maliyetGizli}
              </p>
            )
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/**
 * CRM denetimi B8: `operations/service-completions`. Yalnız bitiş tarihi olmayan üründe gerekli;
 * liste ucu yok, bu yüzden sipariş referansı elle girilir.
 */
function ServiceCompletionSection() {
  const { error, notice, run } = useNotice();
  const [orderReference, setOrderReference] = useState('');
  const [completedOn, setCompletedOn] = useState('');
  const [note, setNote] = useState('');
  const [result, setResult] = useState<ServiceCompletionResult | null>(null);

  const submit = () =>
    void run(async () => {
      const outcome = await api.markServiceCompleted({
        orderReference: orderReference.trim(),
        ...(completedOn ? { completedOn: new Date(completedOn).toISOString() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      setResult(outcome);
      return outcome.matured
        ? M.hizmetTamamlama.olgunlastiEvet(para(outcome.amountMinor, 'EUR'))
        : (outcome.reason ? M.hizmetTamamlama.olgunlastiHayirNeden[outcome.reason] : M.hizmetTamamlama.olgunlastiEvet(para(0, 'EUR')));
    });

  return (
    <section data-testid="service-completion-panel">
      <h3>{M.hizmetTamamlama.baslik}</h3>
      <p>{M.hizmetTamamlama.aciklama}</p>
      <Notices scope="service-completion" error={error} notice={notice} />
      <AlanSatiri>
        <Alan etiket={M.hizmetTamamlama.siparisReferansi} zorunlu>
          <input data-testid="service-completion-order-ref" value={orderReference} onChange={(event) => setOrderReference(event.target.value)} />
        </Alan>
        <Alan etiket={M.hizmetTamamlama.tamamlanmaTarihi}>
          <input data-testid="service-completion-date" type="date" value={completedOn} onChange={(event) => setCompletedOn(event.target.value)} />
        </Alan>
      </AlanSatiri>
      <Alan etiket={M.hizmetTamamlama.not}>
        <textarea data-testid="service-completion-note" value={note} onChange={(event) => setNote(event.target.value)} />
      </Alan>
      <button type="button" data-testid="service-completion-submit" disabled={!orderReference.trim()} onClick={submit}>
        {M.hizmetTamamlama.kaydet}
      </button>
      {result ? (
        <p data-testid="service-completion-result">
          {result.matured
            ? M.hizmetTamamlama.olgunlastiEvet(para(result.amountMinor, 'EUR'))
            : result.reason
              ? M.hizmetTamamlama.olgunlastiHayirNeden[result.reason]
              : ''}
        </p>
      ) : null}
    </section>
  );
}

/**
 * Satış akışı vakası madde 16: "Operasyoncu otel tedariklerini, safariyi, şehir turunu ve
 * jetskileri operasyon takvimine yazar." `operations/calendar`in ilk panel yüzeyi.
 *
 * Tarihsiz kalemler (`kind: 'UNDATED'`) pencereden bağımsız, **her zaman** ayrı bir listede
 * gösteriliyor — uçtaki garantinin aynısı ekranda da geçerli: dar bir pencere seçmek bir vizeyi
 * kaybettirmiyor.
 */

/**
 * Satış akışı vakası madde 17: "Tedarikçilere bildirimi yapar." Gerçek bir kanal yok; bu ekran
 * operatörün elle ulaştığını **kaydettiği** yer. Kanal yapılandırılmamış kalem listeden
 * düşmüyor, `NOT_CONFIGURED`/`PENDING` olarak görünür kalıyor (`M.tedarikciBildirimi.kanalYapilandirilmamis`).
 */
function SupplierNotificationsSection() {
  const { error, notice, run } = useNotice();
  const [items, setItems] = useState<SupplierNotificationRow[]>([]);
  const [durumFiltresi, setDurumFiltresi] = useState('');
  const [notlar, setNotlar] = useState<Record<string, string>>({});
  const T = M.tedarikciBildirimi;

  const yukle = useCallback(
    () =>
      run(async () => {
        const page = await api.supplierNotifications(durumFiltresi ? { state: durumFiltresi } : {});
        setItems(page.items);
      }),
    [durumFiltresi, run],
  );

  useEffect(() => {
    void yukle();
  }, [yukle]);

  return (
    <section data-testid="supplier-notifications">
      <h3>{T.baslik}</h3>
      <p>{T.aciklama}</p>
      <p className="callout" role="note">{T.kanalYapilandirilmamis}</p>
      <Notices scope="supplier-notifications" error={error} notice={notice} />
      <Alan etiket={T.durumFiltresi}>
        <select data-testid="supplier-notification-filter" value={durumFiltresi} onChange={(event) => setDurumFiltresi(event.target.value)}>
          <option value="">{T.tumDurumlar}</option>
          <option value="PENDING">{T.beklemede}</option>
          <option value="SENT">{T.gonderildi}</option>
        </select>
      </Alan>
      <div className="table-wrap">
        <table data-testid="supplier-notifications-table">
          <thead>
            <tr>
              <th>{T.sutunKalem}</th>
              <th>{T.sutunTedarikci}</th>
              <th>{T.sutunDurum}</th>
              <th>{T.sutunUlasilmaAni}</th>
              <th>{T.sutunUlasan}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={6} className="muted">{T.bos}</td></tr>
            ) : (
              items.map((row) => (
                <tr key={row.orderItemId} data-testid="supplier-notification-row">
                  <td>{row.orderCode} — {row.description}</td>
                  <td>{row.supplierName}</td>
                  <td>{row.state === 'SENT' ? T.gonderildi : T.beklemede}</td>
                  <td>{row.sentAt ? tarihSaat(row.sentAt) : TANIMSIZ}</td>
                  <td>{row.sentBy ?? TANIMSIZ}</td>
                  <td>
                    {row.state === 'PENDING' ? (
                      <>
                        <input
                          data-testid={`supplier-notification-note-${row.orderItemId}`}
                          placeholder={T.notEtiketi}
                          value={notlar[row.orderItemId] ?? ''}
                          onChange={(event) => setNotlar((current) => ({ ...current, [row.orderItemId]: event.target.value }))}
                        />
                        <button
                          type="button"
                          data-testid={`supplier-notification-mark-sent-${row.orderItemId}`}
                          onClick={() =>
                            void run(async () => {
                              const not = notlar[row.orderItemId]?.trim();
                              await api.markSupplierNotified(row.orderItemId, {
                                expectedVersion: row.version,
                                ...(not ? { note: not } : {}),
                              });
                              await yukle();
                              return T.kaydedildi;
                            })
                          }
                        >
                          {T.ulasildiIsaretle}
                        </button>
                      </>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Satış akışı vakası madde 18: "hangi tedarikçiye ne ödeneceğini girer." 21.08.2026 kararı
 * burada da geçerli: ekranda karşılaştırma yok, "beklenen tutar" ipucu yok — operatör yalnız
 * kendi girdiğini görür, uç zaten hiçbir tutarı geri döndürmüyor.
 */
function SupplierCostSection() {
  const { error, notice, run } = useNotice();
  const [items, setItems] = useState<SupplierNotificationRow[]>([]);
  const [itemId, setItemId] = useState('');
  const [status, setStatus] = useState<SupplierCostStatus | null>(null);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('AED');
  const [note, setNote] = useState('');
  const T = M.tedarikciMaliyeti;

  useEffect(() => {
    void run(async () => {
      const page = await api.supplierNotifications();
      setItems(page.items);
    });
  }, [run]);

  const select = (id: string) =>
    void run(async () => {
      setItemId(id);
      setStatus(id ? await api.supplierCostStatus(id) : null);
    });

  return (
    <section data-testid="supplier-cost">
      <h3>{T.baslik}</h3>
      <p>{T.aciklama}</p>
      <Notices scope="supplier-cost" error={error} notice={notice} />
      <Alan etiket={T.kalemSec}>
        <select data-testid="supplier-cost-item" value={itemId} onChange={(event) => select(event.target.value)}>
          <option value="">{T.kalemSecin}</option>
          {items.map((row) => (
            <option key={row.orderItemId} value={row.orderItemId}>
              {row.orderCode} — {row.description} ({row.supplierName})
            </option>
          ))}
        </select>
      </Alan>

      {itemId && status ? (
        <>
          <p data-testid="supplier-cost-status">
            {status.hasCommittedCost ? T.durumGirilmis(status.lastEnteredAt ? tarihSaat(status.lastEnteredAt) : TANIMSIZ) : T.durumBekliyor}
          </p>
          <AlanSatiri>
            <Alan etiket={T.tutar} ipucu={M.genel.tutarIpucu} zorunlu>
              <input data-testid="supplier-cost-amount" inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} />
            </Alan>
            <Alan etiket={T.paraBirimi} zorunlu>
              <input data-testid="supplier-cost-currency" value={currency} onChange={(event) => setCurrency(event.target.value.toUpperCase())} maxLength={3} />
            </Alan>
            <Alan etiket={T.not}>
              <input data-testid="supplier-cost-note" value={note} onChange={(event) => setNote(event.target.value)} />
            </Alan>
          </AlanSatiri>
          <button
            type="button"
            data-testid="supplier-cost-submit"
            disabled={!amount.trim() || !currency.trim()}
            onClick={() =>
              void run(async () => {
                await api.recordSupplierCost(itemId, {
                  amountMinor: paraAltBirime(amount),
                  currencyCode: currency,
                  ...(note.trim() ? { note: note.trim() } : {}),
                });
                setAmount('');
                setNote('');
                setStatus(await api.supplierCostStatus(itemId));
                return T.kaydedildi;
              })
            }
          >
            {T.kaydet}
          </button>
        </>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Finans
// ---------------------------------------------------------------------------

/**
 * Eski CRM siparişinin fatura bölümü: kur dondurma ve koşulların görünümü.
 *
 * 08.10.2026: CRM resmi fatura kesmez. "Faturayı kes" düğmesi ve onay kutusu silindi (kod
 * `pp-otomasyon-oncesi-20261008` etiketinde); fatura PayPartner'da Finans tarafından kesilir.
 */
function FaturaBolumu({
  summary,
  orderVersion,
  canWrite,
  onDegisti,
}: {
  summary: FinanceSummary;
  orderVersion: number | null;
  canWrite: boolean;
  onDegisti: () => Promise<void>;
}) {
  const { error, notice, run } = useNotice();
  const [kur, setKur] = useState('');
  const [kurKaynagi, setKurKaynagi] = useState('MANUAL_LOCAL');
  const [kurTarihi, setKurTarihi] = useState('');
  const metin = M.finans.fatura;

  const kosullar = [...summary.invoice.preconditions, ...summary.invoice.data];
  const kurHazir = kosullar.find((kosul) => kosul.key === 'parabirimi')?.met ?? false;
  const eksikler = kosullar.filter((kosul) => !kosul.met);
  const hazir = summary.invoice.state === 'READY';
  const kesildi = summary.invoice.state === 'ISSUED';
  const saglayiciYok = summary.invoice.state === 'NOT_CONFIGURED';
  const kosulAdi = (anahtar: string) => metin.kosulAdi[anahtar] ?? anahtar;

  return (
    <section data-testid="invoice-section">
      <h3>{metin.baslik}</h3>
      <p data-testid="invoice-state">{summary.invoice.note}</p>
      {saglayiciYok && summary.invoice.missingConfig.length > 0 ? (
        <p data-testid="invoice-missing-config">{M.finans.faturaEksikDegiskenler(summary.invoice.missingConfig)}</p>
      ) : null}
      <Notices scope="invoice" error={error} notice={notice} />

      <h4>{metin.adimKur}</h4>
      {kurHazir ? (
        <p className="callout callout-ok" role="status" data-testid="invoice-fx-ready">
          {metin.kurTamam}
        </p>
      ) : (
        <>
          <p className="callout callout-warn" role="note" data-testid="invoice-fx-once">
            {metin.kurTekSefer}
          </p>
          {canWrite ? (
            <>
              <AlanSatiri>
                <Alan etiket={metin.kurEtiketi} ipucu={metin.kurIpucu} zorunlu>
                  <input data-testid="invoice-fx-rate" inputMode="decimal" value={kur} onChange={(event) => setKur(event.target.value)} />
                </Alan>
                <Alan etiket={metin.kurKaynagiEtiketi} zorunlu>
                  <select data-testid="invoice-fx-source" value={kurKaynagi} onChange={(event) => setKurKaynagi(event.target.value)}>
                    {(['MAMOPAY_SETTLEMENT', 'BANK_STATEMENT', 'MANUAL_LOCAL'] as const).map((deger) => (
                      <option key={deger} value={deger}>
                        {metin.kaynakAdi[deger] ?? deger}
                      </option>
                    ))}
                  </select>
                </Alan>
                <Alan etiket={metin.kurTarihiEtiketi} ipucu={metin.kurTarihiIpucu} zorunlu>
                  <input data-testid="invoice-fx-date" type="date" value={kurTarihi} onChange={(event) => setKurTarihi(event.target.value)} />
                </Alan>
              </AlanSatiri>
              <button
                type="button"
                className="birincil"
                data-testid="freeze-invoice-fx"
                onClick={() =>
                  void run(async () => {
                    if (orderVersion === null) throw new Error(M.genel.siparisSecinUyari);
                    if (kurTarihi === '') throw new Error(metin.kurTarihiUyarisi);
                    const sonuc = await api.freezeInvoiceFx(summary.orderId, {
                      // Altı basamak: uç mikro birim bekliyor ve tarayıcı ondalıklı bir kur taşımıyor.
                      rateMicros: ondalikTamSayiya(kur, 6, metin.kurBicimiUyarisi),
                      source: kurKaynagi,
                      rateDate: new Date(kurTarihi).toISOString(),
                      expectedVersion: orderVersion,
                    });
                    await onDegisti();
                    return sonuc.idempotentReplay ? metin.kurZatenYazildi : metin.kurYazildi;
                  })
                }
              >
                {metin.kuruDondur}
              </button>
            </>
          ) : null}
        </>
      )}

      <h4>{metin.adimKes}</h4>
      {kesildi ? (
        <p className="callout callout-ok" role="status" data-testid="invoice-issued">
          {summary.invoice.note}
        </p>
      ) : null}
      {hazir ? (
        <p className="callout callout-ok" role="status" data-testid="invoice-ready">
          {metin.hazir}
        </p>
      ) : (
        <div data-testid="invoice-missing-fields">
          <p>{saglayiciYok ? metin.saglayiciYok : metin.hazirDegilBaslik}</p>
          {eksikler.length > 0 ? (
            <ul>
              {eksikler.map((kosul) => (
                <li key={kosul.key} data-testid={`invoice-missing-${kosul.key}`}>
                  {kosulAdi(kosul.key)} — {metin.kosulEksik}
                </li>
              ))}
            </ul>
          ) : null}
          <p className="muted">{metin.hazirDegilIpucu}</p>
        </div>
      )}
      {/*
        Sağlanan koşullar da yazılı. Yalnız eksikleri göstermek, "başka ne gerekiyordu" sorusunu
        cevapsız bırakır ve bir gün sağlanan bir koşulun sessizce kaybolmasını görünmez kılar.
      */}
      <details data-testid="invoice-requirements">
        <summary>{metin.baslik}</summary>
        <ul>
          {kosullar.map((kosul) => (
            <li key={kosul.key} data-testid={`invoice-requirement-${kosul.key}`}>
              {kosulAdi(kosul.key)} — {kosul.met ? metin.kosulSaglandi : metin.kosulEksik}
              {kosul.key === 'tedarikci_rezervasyonu_onayli' ? ` · ${metin.durdurmayanKosul}` : ''}
            </li>
          ))}
        </ul>
      </details>

    </section>
  );
}

/**
 * Bir ödeme kanıtının reddi: doğrulamanın karşı yarısı.
 *
 * Ölçülen kusur: satırda "Doğrula" vardı, "Reddet" yoktu. Tutmayan bir dekontla karşılaşan
 * Finans kullanıcısı kanıtı kuyrukta bırakmak zorundaydı, ve kuyrukta duran bir kanıt bir gün
 * yanlışlıkla doğrulanır.
 *
 * Ret geri alınamıyor (durum makinesi `REJECTED`ten geri bir yol tanımıyor), o yüzden iki şey
 * isteniyor: uca giden ve denetim izinde okunan bir **gerekçe**, ve ne olacağını yazan bir
 * uyarının içindeki **onay kutusu**. Form kapalı doğuyor: ret, tek tıkla yapılacak bir şey değil.
 */
function KanitRetDugmesi({
  payment,
  index,
  onReddedildi,
}: {
  payment: PaymentEvidence;
  index: number;
  onReddedildi: () => Promise<void>;
}) {
  const { error, notice, run } = useNotice();
  const [acik, setAcik] = useState(false);
  const [gerekce, setGerekce] = useState('');
  const [onay, setOnay] = useState(false);
  const metin = M.finans.kanitRet;

  if (!acik) {
    return (
      <button type="button" className="ikincil" data-testid={`payment-reject-${index}`} onClick={() => setAcik(true)}>
        {metin.reddet}
      </button>
    );
  }

  return (
    <div data-testid={`payment-reject-form-${index}`}>
      <Notices scope={`payment-reject-${index}`} error={error} notice={notice} />
      <div className="callout" role="alert">
        <div>
          <p>{metin.onayCumlesi(durumAdi(payment.kind), para(payment.amountMinor, payment.currencyCode))}</p>
          <label>
            <input
              type="checkbox"
              data-testid={`payment-reject-ack-${index}`}
              checked={onay}
              onChange={(event) => setOnay(event.target.checked)}
            />{' '}
            {metin.onayEtiketi}
          </label>
        </div>
      </div>
      <AlanSatiri>
        <Alan etiket={metin.gerekceEtiketi} ipucu={metin.gerekceIpucu} zorunlu>
          <input
            data-testid={`payment-reject-reason-${index}`}
            value={gerekce}
            maxLength={240}
            onChange={(event) => setGerekce(event.target.value)}
          />
        </Alan>
      </AlanSatiri>
      <button
        type="button"
        className="ikincil"
        data-testid={`payment-reject-confirm-${index}`}
        disabled={!onay}
        title={onay ? metin.reddet : metin.onayUyarisi}
        onClick={() =>
          void run(async () => {
            if (gerekce.trim().length < 3) throw new Error(metin.gerekceUyarisi);
            await api.rejectPayment(payment.id, { expectedVersion: payment.version, reason: gerekce.trim() });
            setAcik(false);
            setGerekce('');
            setOnay(false);
            await onReddedildi();
            return metin.reddedildi(para(payment.amountMinor, payment.currencyCode));
          })
        }
      >
        {metin.reddet}
      </button>
      <button type="button" className="ikincil" data-testid={`payment-reject-cancel-${index}`} onClick={() => setAcik(false)}>
        {metin.vazgec}
      </button>
    </div>
  );
}

function FinancePanel({ me, selectedOrderId, initialTab = 'payments' }: { me: Me; selectedOrderId?: string; initialTab?: FinanceDetailTab }) {
  const { error, notice, run } = useNotice();
  const [detailTab, setDetailTab] = useState<FinanceDetailTab>(initialTab);
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [orderId, setOrderId] = useState(selectedOrderId ?? '');
  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [orderDetail, setOrderDetail] = useState<Order | null>(null);
  const [costOrderItemId, setCostOrderItemId] = useState('');
  /**
   * Ödeme formu **boş** doğuyor, ve bu bir tercih değil bir para riski düzeltmesi.
   *
   * Ölçülen hâli: `useState('40000')` ve `useState('PARTIAL')`. 990,00 avroluk bir siparişte
   * tek bir dalgın tık, 400,00 avroluk bir kısmi ödeme yazıyordu. Hazır gelen tutar zaman
   * kazandırıyor gibi görünür; yanlış yazılmış bir ödeme kaydını geri almak, bir alanı elle
   * yazmaktan çok daha pahalıdır — ve bir muhasebe defterinde geri almak her zaman mümkün de
   * değildir, ikinci bir düzeltme kaydı gerekir.
   *
   * Aynı gerekçe maliyet ve iade tutarına da uygulandı: onlar da `30000` ve `1000` ile
   * doğuyordu. Tür seçimi de bir varsayılan dayatmıyor; "Kısmi" ile "Son ödeme" arasındaki
   * fark mutabakatı kapatıp kapatmadığıdır, ve o kararı ekran veremez.
   */
  const [amount, setAmount] = useState('');
  const [kind, setKind] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  /**
   * Ödeme kaydının idempotency anahtarı: **bir gönderim denemesinin kimliği.**
   *
   * Ölçülen kusur (24.08.2026, uca doğrudan): anahtar `pay-<tur>-<siparis>` diye kuruluyordu,
   * yani bir siparişin ömrü boyunca **sabit** bir dizeydi. Sonuç, aynı siparişe ikinci bir
   * kısmi ödemenin uçta 409 ile reddedilmesiydi: ORD-E68A954ECB üzerinde ilk taksit 201,
   * ikinci taksit 409. Bu bir arayüz süsü değil, satışın önünde duran bir engeldi — otel
   * siparişi provizyonda başlıyor, müşteri otel değiştirdiğinde fark ek tahsilat ya da iade
   * oluyor, ve iki taksitle ödeyen bir müşteri hiç kaydedilemiyordu.
   *
   * Anahtar **kaldırılmadı**, çünkü var olma sebebi hâlâ geçerli: aynı ödemenin iki kez
   * kaydedilmesini önlemek. Değişen, anahtarın neyi tanımladığı:
   *
   *   - Eskiden "bu siparişin bu türdeki ödemesi" — ikinci taksit aynı şey sayılıyordu.
   *   - Şimdi "bu gönderim denemesi" — her başarılı kayıttan sonra yenisi üretiliyor.
   *
   * Böylece iki farklı taksit iki ayrı kayıt oluyor, ama tek bir denemenin tekrarı (çift tık,
   * ağ koptuğu için yeniden gönderme) hâlâ **aynı** anahtarı taşıyor ve uç onu ikinci bir
   * tutar olarak yazmıyor. Başarısız bir denemeden sonra anahtar **korunuyor**: o denemenin
   * hiçbir kaydı oluşmadığı için tekrar denemek güvenli, ve kullanıcı tutarı düzeltip yeniden
   * gönderdiğinde aynı anahtar altında ilk kayıt yine tek kayıt oluyor.
   */
  const [paymentAttemptKey, setPaymentAttemptKey] = useState(yeniDenemeAnahtari);
  const [costAmount, setCostAmount] = useState('');
  const [costSupplier, setCostSupplier] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const canWrite = me.permissions.includes('finance:write');
  /**
   * Doğrulama ve ret aynı izni istiyor (`payment:verify`), ve o izin `finance:write` ile aynı şey
   * değil: bugün ikisi de yalnız Finans'ta duruyor ama uç ayrı ayrı kontrol ediyor. Doğrula
   * düğmesi bugüne kadar `finance:write`a bakıyordu; ret düğmesi doğru izne bağlanıyor ve
   * doğrulama da onunla hizalanıyor, yoksa iki düğme aynı satırda iki farklı kurala uyar.
   */
  const canVerify = me.permissions.includes('payment:verify');

  const loadSummary = useCallback(async (id: string) => {
    if (!id) return;
    const [nextSummary, nextOrder] = await Promise.all([api.financeSummary(id), api.order(id)]);
    setSummary(nextSummary);
    setOrderDetail(nextOrder);
    setCostOrderItemId((current) => current || nextOrder.items[0]?.id || '');
  }, []);

  useEffect(() => {
    void run(async () => {
      setLoading(true);
      try {
      if (selectedOrderId) { await loadSummary(selectedOrderId); return; }
      const page = await api.orders();
      setOrders(page.items);
      const first = page.items[0];
      if (first) {
        setOrderId(first.id);
        await loadSummary(first.id);
      }
      } finally { setLoading(false); }
    });
  }, [loadSummary, run, selectedOrderId]);

  return (
    <section id="finance-workflow" data-testid="finance-panel">
      {!selectedOrderId ? <h2>{M.finans.baslik}</h2> : null}
      {/*
        Fatura şeridi artık kendi bölümünde (`FaturaBolumu`), sipariş özeti yüklendikten sonra.
        Özet yokken burada duran yer tutucu cümle kaldı: ekran hiçbir siparişe bakmıyorken bir
        fatura durumu iddia etmiyor.
      */}
      {loading ? <p role="status">{F.loading}</p> : null}
      <Notices scope="finance" error={error} notice={notice} />
      {!selectedOrderId ? <>
      <button type="button" data-testid="finance-csv-export" onClick={() => void run(async () => { await api.downloadFinanceCsv(); return M.finans.csvUretildi; })}>{M.finans.csvIndir}</button>
      <Alan etiket={M.genel.incelenecekSiparis}>
        <select
          data-testid="finance-order"
          value={orderId}
          onChange={(event) =>
            void run(async () => {
              setOrderId(event.target.value);
              await loadSummary(event.target.value);
            })
          }
        >
          <option value="">{M.genel.siparisSecin}</option>
          {orders.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {M.genel.kodDurumMusteri(entry.code, durumAdi(entry.status), entry.customer.displayName)}
            </option>
          ))}
        </select>
      </Alan>
      </> : null}
      {summary ? (
        <div data-testid="finance-summary">
          {/*
            Dokuz rakamın hiçbirinin görünür etiketi yoktu, yalnız `data-testid`si vardı — o
            özniteliği yalnız test okuyor. Her rakam artık `Stat` içinde: etiket rakamın yanında,
            değerin metni testlerin beklediği ham hâliyle aynı kalıyor (`toHaveText`).

            Uçtan gelen `display.*` dizeleri **çizilmiyor**. Ürün sahibinin ekranında aynı sayı
            yan yana iki biçimde duruyordu: `€1.020,00` ile `1020.00 EUR`, `%55,88` ile `55.88%`.
            Uçtaki biçim dışa aktarma ve makine tüketimi için doğru; ekranın biçimi
            `metin/ortak.ts` içindeki tr-TR biçimleyicileri. Bir ekranda bir sayının bir biçimi olur.
          */}
          <div className="finance-order-customer"><strong>{orderDetail?.customer.displayName}</strong><span className="finance-badge">{durumYaz(orderDetail?.status)}</span></div>
          <div className="stat-grid finance-order-metrics">
            <Stat label={M.finans.ozet.ciro} testId="finance-revenue-minor">{para(summary.revenueMinor, summary.currencyCode)}</Stat>
            <Stat label={M.finans.ozet.dogrulanmisTahsilat} testId="finance-verified-paid-minor">{para(summary.verifiedPaidMinor, summary.currencyCode)}</Stat>
            <Stat label={M.finans.ozet.acikBakiye} testId="finance-outstanding-minor">{para(summary.outstandingMinor, summary.currencyCode)}</Stat>
            <Stat label={M.finans.ozet.dogrulanmamisKanit} testId="finance-unverified-minor">{para(summary.recordedUnverifiedMinor, summary.currencyCode)}</Stat>
          </div>
          <details className="finance-disclosure" data-testid="finance-financial-details"><summary>{F.financialDetails}</summary><div className="stat-grid">
            <Stat label={M.finans.ozet.paraBirimi} testId="finance-currency">{summary.currencyCode}</Stat>
            <Stat label={M.finans.ozet.maliyet} testId="finance-cost-minor">{para(summary.costMinor, summary.currencyCode)}</Stat>
            <Stat label={M.finans.ozet.brutKar} testId="finance-gross-profit-minor">{para(summary.grossProfitMinor, summary.currencyCode)}</Stat>
            <Stat label={M.finans.ozet.marj} testId="finance-margin-bps">{M.genel.yuzde(sayi(summary.marginBps / 100))}</Stat>
            <Stat label={M.finans.ozet.iadeEdilen} testId="finance-refunded-minor">{para(summary.refundedMinor, summary.currencyCode)}</Stat>
            <Stat label={M.finans.ozet.mutabakatDurumu} testId="reconciliation-status">{durumYaz(summary.reconciliation?.status)}</Stat>
            <Stat label={M.finans.ozet.maliyetSayisi} testId="finance-cost-count">{tamSayi(summary.costs.length)}</Stat>
          </div></details>
          {typeof summary.overpaidMinor === 'number' && summary.overpaidMinor > 0 ? <p className="callout" data-testid="finance-overpayment" role="note">{F.overpayment(para(summary.overpaidMinor, summary.currencyCode))}</p> : null}
          <nav className="finance-detail-tabs" aria-label={F.title}>{(Object.keys(F.detailTabs) as FinanceDetailTab[]).map((tab) => <button key={tab} data-testid={`finance-detail-tab-${tab}`} aria-current={detailTab === tab ? 'page' : undefined} onClick={() => setDetailTab(tab)}>{F.detailTabs[tab]}</button>)}</nav>

          <div hidden={detailTab !== 'payments'} className="finance-detail-content">
          <h3>{M.finans.kanitBasligi}</h3>
          <p className="finance-hint" data-testid="payment-count">{F.count(summary.payments.length)} · {F.paymentHint}</p>
          {!summary.payments.length ? <div className="finance-empty">{F.noPayments}</div> : null}
          {summary.payments.map((payment, index) => (
            <div key={payment.id} className="finance-payment-row" data-testid={`payment-${index}`} data-payment-id={payment.id}>
              <span data-testid={`payment-kind-${index}`}>{durumAdi(payment.kind)}</span>
              <span data-testid={`payment-status-${index}`}>{durumAdi(payment.status)}</span>
              <span data-testid={`payment-amount-${index}`}>{para(payment.amountMinor, payment.currencyCode)}</span>
              <span data-testid={`payment-funds-${index}`}>
                {payment.status === 'VERIFIED' ? M.finans.paraAlindi : M.finans.paraAlinmadi}
              </span>
              <small>{payment.reference}</small>
              {canVerify && payment.status === 'RECORDED' ? (
                <FinanceConfirm
                  testId={`payment-verify-${index}`}
                  disabledReason={payment.recordedById === me.userId ? F.ownPayment : undefined}
                  title={F.verifyTitle}
                  description={`${para(payment.amountMinor, payment.currencyCode)} · ${payment.reference}. ${F.verifyHint}`}
                  label={M.finans.dogrula}
                  onConfirm={async () => {
                    await run(async () => {
                      const result = await api.verifyPayment(payment.id, {
                        expectedVersion: payment.version,
                        confirmedAmountMinor: payment.amountMinor,
                      });
                      await loadSummary(orderId);
                      return result.idempotentReplay
                        ? M.finans.zatenDogrulandi
                        : M.finans.kanitDogrulandi(durumAdi(payment.kind), para(payment.amountMinor, payment.currencyCode));
                    });
                  }}
                />
              ) : null}
              {/*
                Ret, doğrulamayla **aynı** izne bağlı (`payment:verify`): bir kanıtı para saymak da
                saymamak da aynı karar. Yalnız henüz karara bağlanmamış (kaydedilmiş) kanıtta
                çiziliyor — doğrulanmış ya da zaten reddedilmiş bir kanıtta uç geçişi reddediyor ve
                orada bir düğme göstermek boş bir vaat olurdu.
              */}
              {canVerify && payment.status === 'RECORDED' ? (
                <KanitRetDugmesi payment={payment} index={index} onReddedildi={() => loadSummary(orderId)} />
              ) : null}
              {payment.status === 'REJECTED' && payment.rejectedReason ? (
                <span data-testid={`payment-rejected-reason-${index}`} className="uzun-deger" data-veri>
                  {payment.rejectedReason}
                </span>
              ) : null}
            </div>
          ))}

          {canWrite ? (
            <>
              <details className="finance-disclosure" data-testid="finance-add-payment"><summary>{F.addPayment}</summary>
              <h3>{M.finans.kanitKaydetBasligi}</h3>
              <AlanSatiri>
                <Alan etiket={M.finans.kanitTuruEtiketi} zorunlu>
                  <select data-testid="payment-kind-input" value={kind} onChange={(event) => setKind(event.target.value)}>
                    <option value="">{M.finans.kanitTuruSecin}</option>
                    <option value="PARTIAL">{durumAdi('PARTIAL')}</option>
                    <option value="FINAL">{durumAdi('FINAL')}</option>
                  </select>
                </Alan>
                {/*
                  Açık bakiye alanın **altında** yazılı, içine yazılı değil. Beklenen tutarı
                  söylemek ile alana yazmak aynı şey değil: biri okunur, öteki imzalanır.
                */}
                <Alan
                  etiket={M.finans.kanitTutariEtiketi}
                  ipucu={M.finans.kanitTutariIpucu(para(summary.outstandingMinor, summary.currencyCode))}
                  zorunlu
                >
                  <input data-testid="payment-amount-input" inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} />
                </Alan>
              </AlanSatiri>
              <Alan etiket={F.reference} ipucu={F.referenceHelp} zorunlu><input data-testid="payment-reference-input" value={paymentReference} maxLength={100} onChange={(event) => setPaymentReference(event.target.value)} /></Alan>
              <button
                type="button"
                data-testid="record-payment"
                onClick={() =>
                  void run(async () => {
                    if (!kind) throw new Error(M.finans.kanitTuruSecinUyari);
                    if (!paymentReference.trim()) throw new Error(F.requiredReference);
                    const result = await api.recordPayment(orderId, {
                      kind,
                      amountMinor: paraAltBirime(amount),
                      currencyCode: summary.currencyCode,
                      method: 'BANK_TRANSFER',
                      reference: paymentReference.trim(),
                      idempotencyKey: paymentAttemptKey,
                    });
                    /**
                     * Anahtar **yalnız başarıdan sonra** yenileniyor, ve form da o an temizleniyor.
                     *
                     * Sıralama önemli: yenileme başarısızlıkta da yapılsaydı, ağ koptuğu için
                     * yeniden gönderilen bir ödeme ikinci bir kayıt olurdu — yani idempotency'nin
                     * korumaya çalıştığı tam da o durum kaybolurdu.
                     */
                    setPaymentAttemptKey(yeniDenemeAnahtari());
                    setAmount('');
                    setKind('');
                    setPaymentReference('');
                    await loadSummary(orderId);
                    return result.idempotentReplay ? M.finans.kanitYinelendi : M.finans.kanitKaydedildi;
                  })
                }
              >
                {M.finans.kanitKaydet}
              </button>
              </details>
            </>
          ) : null}
          </div>

          <div hidden={detailTab !== 'costs'} className="finance-detail-content">
          {canWrite ? <details className="finance-disclosure" data-testid="finance-add-cost"><summary>{F.addCost}</summary>
              <h3>{M.finans.maliyetBasligi}</h3>
              <p className="finance-hint">{F.costHint}</p>
              <AlanSatiri>
                <Alan etiket={M.finans.kalemSecin}>
                  <select data-testid="cost-order-item" value={costOrderItemId} onChange={(event) => setCostOrderItemId(event.target.value)}>
                    <option value="">{M.finans.kalemSecin}</option>{orderDetail?.items.map((item) => <option key={item.id} value={item.id}>{M.finans.kalemSatiri(tamSayi(item.lineNo), item.description)}</option>)}
                  </select>
                </Alan>
                <Alan etiket={M.finans.maliyetTedarikcisiEtiketi} zorunlu>
                  <input data-testid="cost-supplier-input" value={costSupplier} onChange={(event) => setCostSupplier(event.target.value)} />
                </Alan>
                <Alan etiket={M.finans.maliyetTutariEtiketi} ipucu={M.genel.tutarIpucu} zorunlu>
                  <input data-testid="cost-amount-input" inputMode="decimal" value={costAmount} onChange={(event) => setCostAmount(event.target.value)} />
                </Alan>
              </AlanSatiri>
              <button
                type="button"
                data-testid="record-cost"
                onClick={() =>
                  void run(async () => {
                    await api.recordCost(orderId, {
                      category: 'SUPPLIER',
                      supplierName: costSupplier,
                      description: M.finans.maliyetAciklamasi(costSupplier),
                      amountMinor: paraAltBirime(costAmount),
                      currencyCode: summary.currencyCode,
                      orderItemId: costOrderItemId,
                    });
                    await loadSummary(orderId);
                    return M.finans.maliyetKaydedildi;
                  })
                }
              >
                {M.finans.maliyetKaydet}
              </button>
          </details> : null}
              {!summary.costs.length ? <div className="finance-empty">{F.noCosts}</div> : null}
              {summary.costs.map((cost, index) => (
                <p key={cost.id} data-testid={`cost-${index}`}>
                  {M.finans.maliyetSatiri(
                    cost.supplierName,
                    para(cost.amountMinor, cost.currencyCode),
                    cost.orderItemId ? M.finans.kalemKapsami(cost.orderItemId) : M.finans.siparisKapsami,
                  )}
                </p>
              ))}
          </div>

          <div hidden={detailTab !== 'refunds'} className="finance-detail-content">
          <p className="finance-hint">{F.refundHint}</p>
          {canWrite ? <details className="finance-disclosure" data-testid="finance-add-refund"><summary>{F.addRefund}</summary>
              <h3>{M.finans.iadeBasligi}</h3>
              <AlanSatiri>
                <Alan etiket={M.finans.iadeTutariEtiketi} ipucu={M.genel.tutarIpucu} zorunlu>
                  <input data-testid="refund-amount-input" inputMode="decimal" value={refundAmount} onChange={(event) => setRefundAmount(event.target.value)} />
                </Alan>
                <Alan etiket={M.finans.iadeGerekcesiEtiketi} zorunlu>
                  <input data-testid="refund-reason-input" value={refundReason} onChange={(event) => setRefundReason(event.target.value)} />
                </Alan>
              </AlanSatiri>
              {summary.refundPolicy?.allVisa ? <p className="finance-hint" data-testid="refund-visa-blocked">{M.finans.iadeVizeYok}</p> : null}
              {summary.refundPolicy?.hasVisa && !summary.refundPolicy.allVisa ? <p className="finance-hint" data-testid="refund-visa-share">{M.finans.iadeVizePayi(para(summary.refundPolicy.visaLineMinor, summary.currencyCode), para(summary.refundPolicy.refundableMinor, summary.currencyCode))}</p> : null}
              <button type="button" data-testid="request-refund" disabled={summary.refundPolicy?.allVisa === true} title={summary.refundPolicy?.allVisa ? M.finans.iadeVizeYok : undefined} onClick={() => void run(async () => {
                await api.requestRefund(orderId, { amountMinor: paraAltBirime(refundAmount), currencyCode: summary.currencyCode, reason: refundReason });
                await loadSummary(orderId); return M.finans.iadeTalepEdildi;
              })}>{M.finans.iadeTalepEt}</button>
          </details> : null}
          <section data-testid="refund-decisions"><h3>{M.finans.iadeKararlariBasligi}</h3>
            {!summary.refunds.length ? <div className="finance-empty">{F.noRefunds}</div> : null}
            {summary.refunds.map((refund, index) => <div key={refund.id} className="finance-refund-row" data-testid={`refund-${index}`}><span>{M.finans.iadeSatiri(durumAdi(refund.status), para(refund.amountMinor, refund.currencyCode), refund.reason)}</span>
              {me.permissions.includes('refund:decide') && refund.status === 'REQUESTED' ? <div className="finance-actions">
                <FinanceConfirm title={M.finans.iadeyiOnayla} description={`${para(refund.amountMinor, refund.currencyCode)} · ${refund.reason}. ${F.refundHint}`} label={M.finans.iadeyiOnayla} onConfirm={async () => { await run(async () => { await api.decideRefund(refund.id, { decision: 'APPROVED', expectedVersion: refund.version, note: M.finans.mudurOnayNotu }); await loadSummary(orderId); return M.finans.iadeOnaylandi; }); }} />
                <FinanceConfirm title={M.finans.iadeyiReddet} description={refund.reason} label={M.finans.iadeyiReddet} onConfirm={async () => { await run(async () => { await api.decideRefund(refund.id, { decision: 'REJECTED', expectedVersion: refund.version, note: M.finans.mudurRetNotu }); await loadSummary(orderId); return M.finans.iadeReddedildi; }); }} />
              </div> : null}</div>)}
          </section></div>

          <div hidden={detailTab !== 'reconciliation'} className="finance-detail-content"><h3>{M.finans.mutabakatBasligi}</h3><p className="finance-badge">{durumYaz(summary.reconciliation?.status)}</p>
          <p className="finance-hint">{F.reconcileHint}</p>
          <ul className="finance-reconcile-checks">{[{ label: F.balanceCheck, met: summary.outstandingMinor === 0 }, { label: F.costCheck, met: summary.costMinor !== 0 }, { label: F.refundCheck, met: !summary.refunds.some((refund) => refund.status === 'REQUESTED') }].map((check) => <li key={check.label}><span>{check.label}</span><span className={`finance-badge${check.met ? '' : ' warning'}`}>{check.met ? F.met : F.unmet}</span></li>)}</ul>
          {me.permissions.includes('reconciliation:write') ? <div className="finance-actions">
              <button
                type="button"
                data-testid="reconcile-ready"
                disabled={!['PENDING', 'BLOCKED'].includes(summary.reconciliation?.status ?? '') || summary.outstandingMinor !== 0 || (summary.overpaidMinor ?? 0) !== 0 || summary.costMinor === 0 || summary.refunds.some((refund) => refund.status === 'REQUESTED')}
                onClick={() =>
                  void run(async () => {
                    await api.reconcile(orderId, { status: 'READY', expectedVersion: summary.reconciliation?.version ?? 1 });
                    await loadSummary(orderId);
                    return M.finans.mutabakatHazir;
                  })
                }
              >
                {M.finans.mutabakatHazirla}
              </button>
              <button
                type="button"
                data-testid="reconcile-done"
                disabled={summary.reconciliation?.status !== 'READY' || summary.outstandingMinor !== 0 || (summary.overpaidMinor ?? 0) !== 0 || summary.costMinor === 0 || summary.refunds.some((refund) => refund.status === 'REQUESTED')}
                onClick={() =>
                  void run(async () => {
                    await api.reconcile(orderId, { status: 'RECONCILED', expectedVersion: summary.reconciliation?.version ?? 1 });
                    await loadSummary(orderId);
                    return M.finans.mutabikOldu;
                  })
                }
              >
                {M.finans.mutabikIsaretle}
              </button>
            </div> : null}
          </div>
          {/*
            Fatura en sonda, çünkü akışta da en sonda: ödemenin tamamı doğrulanmadan ve maliyet
            belli olmadan kesilmiyor. `canWrite` kapısının **dışında**: izni olmayan (Müdür, sistem
            yöneticisi) faturanın niye kesilmediğini görebilmeli; yazma düğmeleri bölümün kendi
            içinde ayrıca kapalı.
          */}
          <div hidden={detailTab !== 'invoice'} className="finance-detail-content"><FaturaBolumu
            summary={summary}
            orderVersion={orderDetail?.version ?? null}
            canWrite={canWrite}
            onDegisti={() => loadSummary(orderId)}
          /></div>
        </div>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Müdür gözetimi
// ---------------------------------------------------------------------------

function OversightPanel() {
  const { error, notice, run } = useNotice();
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [orderId, setOrderId] = useState('');
  const [view, setView] = useState<Oversight | null>(null);
  const [exceptions, setExceptions] = useState<ManagerExceptions | null>(null);

  const load = useCallback(async (id: string) => {
    if (!id) return;
    setView(await api.oversight(id));
  }, []);

  useEffect(() => {
    void run(async () => {
      const [page, currentExceptions] = await Promise.all([api.orders(), api.managerExceptions()]);
      setExceptions(currentExceptions);
      setOrders(page.items);
      const first = page.items[0];
      if (first) {
        setOrderId(first.id);
        await load(first.id);
      }
    });
  }, [load, run]);

  // Kuyruk adları tabloda; buradaki dizi yalnız hangi alanın hangi başlıkla çizileceğini söylüyor.
  const kuyruklar = [
    ['overdue', M.gozetim.istisnaKuyruklari.overdue],
    ['blocked', M.gozetim.istisnaKuyruklari.blocked],
    ['paymentGap', M.gozetim.istisnaKuyruklari.paymentGap],
    ['reconciliationVariance', M.gozetim.istisnaKuyruklari.reconciliationVariance],
    ['lowMargin', M.gozetim.istisnaKuyruklari.lowMargin],
  ] as const;

  return (
    <section id="oversight-workflow" data-testid="oversight-panel">
      <h2>{M.gozetim.baslik}</h2>
      <Notices scope="oversight" error={error} notice={notice} />
      <section className="exception-board" data-testid="manager-exceptions">
        <div className="section-heading"><div><span className="eyebrow">{M.gozetim.ilgiGerekiyor}</span><h3>{M.gozetim.istisnaBasligi}</h3></div><span className="count-badge" data-testid="manager-exceptions-generated">{exceptions ? tarihSaat(exceptions.generatedAt) : ORTAK.yukleniyor}</span></div>
        <div className="exception-grid">
          {exceptions ? kuyruklar.map(([key, label]) => (
            <article key={key} data-testid={`exception-${key}`}><span>{label}</span><strong>{tamSayi(exceptions[key].length)}</strong><small>{exceptions[key].length ? M.gozetim.kayitlariIncele : M.gozetim.istisnaYok}</small>{exceptions[key].slice(0, 5).map((entry, index) => <code key={index}>{(entry.order as { code?: string } | undefined)?.code ?? entry.code ?? (entry.status ? durumAdi(entry.status) : M.gozetim.kayit)}</code>)}</article>
          )) : <p>{M.gozetim.istisnaYukleniyor}</p>}
        </div>
      </section>
      <select
        data-testid="oversight-order"
        aria-label={M.genel.incelenecekSiparis}
        value={orderId}
        onChange={(event) =>
          void run(async () => {
            setOrderId(event.target.value);
            await load(event.target.value);
          })
        }
      >
        <option value="">{M.genel.siparisSecin}</option>
        {orders.map((entry) => (
          <option key={entry.id} value={entry.id}>
            {M.genel.kodDurumMusteri(entry.code, durumAdi(entry.status), entry.customer.displayName)}
          </option>
        ))}
      </select>
      {view ? (
        <div data-testid="oversight-detail">
          <p data-testid="oversight-order-status">{durumAdi(view.order.status)}</p>
          <p data-testid="timeline-count">{tamSayi(view.timeline.length)}</p>
          <ol data-testid="timeline">
            {view.timeline.map((entry, index) => (
              <li key={`${entry.entityId}-${index}`} data-testid={`timeline-${index}`}>
                {M.gozetim.cizelgeSatiri(
                  varlikAdi(entry.entityType),
                  durumYaz(entry.from),
                  durumYaz(entry.to),
                  entry.actor ? rolAdi(entry.actor.role) : M.genel.sistem,
                )}
              </li>
            ))}
          </ol>
          <p data-testid="bottleneck-count">{tamSayi(view.bottlenecks.length)}</p>
          <ul data-testid="bottlenecks">
            {view.bottlenecks.map((entry) => (
              <li key={entry.taskId} data-testid={`bottleneck-${entry.code}`}>
                {M.gozetim.darBogazSatiri(
                  entry.code,
                  durumAdi(entry.status),
                  entry.reason,
                  entry.impliesSuccess ? M.genel.evet : M.genel.hayir,
                )}
              </li>
            ))}
          </ul>
          <p data-testid="audit-count">{tamSayi(view.auditTrail.length)}</p>
          <ul data-testid="audit-trail">
            {view.auditTrail.map((entry, index) => (
              <li key={`${entry.targetId}-${index}`} data-testid={`audit-${index}`}>
                {/*
                  Eylem adı (`order.status_changed`) çevrilmiyor ve `code` olarak çiziliyor:
                  denetim izninin bütün değeri, veritabanına yazılan değerin **aynısını**
                  göstermesinde. Çevrilseydi ekrandaki satırla defterdeki satır eşleşmezdi.
                  Yanına okunur karşılığı (`M.eylem`) ekleniyor, kodun kendisi değişmiyor.
                */}
                <code>{entry.action}</code> {M.eylem[entry.action] ?? ''}{' '}
                {M.gozetim.denetimSatiri(durumAdi(entry.outcome), rolAdi(entry.actorRole), tarihSaat(entry.occurredAt))}
              </li>
            ))}
          </ul>
          {view.financial ? (
            <p data-testid="oversight-margin">{M.genel.yuzde(sayi(view.financial.marginBps / 100))}</p>
          ) : (
            <p data-testid="oversight-margin-hidden">{M.gozetim.marjGizli}</p>
          )}
        </div>
      ) : null}
    </section>
  );
}

/**
 * Denetim maddesi B11: bağımsız "Tüm denetim kaydı" ekranı.
 *
 * `OversightPanel`deki `audit-trail` tek bir siparişe bağlı (`Oversight.auditTrail`, yalnız
 * seçili siparişin geçmişini gösterir). Bu panel ondan bağımsız: `GET /audit`, bütün denetim
 * defterini sayfalayarak okur (`AuditService.list`, izin `audit:read`). İzin bilerek farklı
 * kontrol ediliyor: `oversight:read` sipariş odaklı gözetimi açar, `audit:read` bu geniş
 * defteri; ADMIN ve MANAGER ikisini de taşıyor ama sözleşmeleri ayrı, o yüzden panel kendi
 * iznini kendi denetliyor (`SalesWorkflowPanel` üstündeki yorumdaki gerekçenin aynısı).
 */
function GeneralAuditPanel() {
  const [action, setAction] = useState('');
  const [targetType, setTargetType] = useState('');
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<Paged<AuditRecord> | null>(null);
  const { error, run } = useNotice();
  const G = M.genelDenetim;
  const pageSize = 20;

  const load = useCallback(
    () =>
      run(async () => {
        setResult(
          await api.auditLog({
            action: action || undefined,
            targetType: targetType || undefined,
            page,
            pageSize,
          }),
        );
      }),
    [action, targetType, page, run],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const sonSayfa = Math.max(1, Math.ceil((result?.total ?? 0) / (result?.pageSize ?? pageSize)));

  return (
    <section id="general-audit" data-testid="general-audit-panel">
      <h2>{G.baslik}</h2>
      <p>{G.aciklama}</p>
      <Notices scope="general-audit" error={error} notice={null} />
      <div className="toolbar">
        <label>
          {G.eylemEtiketi}{' '}
          <input
            data-testid="general-audit-action"
            value={action}
            placeholder={G.tumEylemler}
            onChange={(event) => {
              setAction(event.target.value);
              setPage(1);
            }}
          />
        </label>{' '}
        <label>
          {G.hedefTipiEtiketi}{' '}
          <input
            data-testid="general-audit-target-type"
            value={targetType}
            placeholder={G.tumHedefTipleri}
            onChange={(event) => {
              setTargetType(event.target.value);
              setPage(1);
            }}
          />
        </label>{' '}
        <button type="button" data-testid="general-audit-reload" onClick={() => void load()}>
          {ORTAK.yeniden}
        </button>
      </div>
      <div className="table-wrap">
        <table data-testid="general-audit-table">
          <thead>
            <tr>
              <th>{G.sutun.an}</th>
              <th>{G.sutun.eylem}</th>
              <th>{G.sutun.aktor}</th>
              <th>{G.sutun.hedef}</th>
              <th>{G.sutun.sonuc}</th>
            </tr>
          </thead>
          <tbody>
            {(result?.items.length ?? 0) === 0 ? (
              <tr>
                <td colSpan={5} className="muted">
                  {G.bos}
                </td>
              </tr>
            ) : (
              result?.items.map((satir) => (
                <tr key={satir.id} data-testid="general-audit-row">
                  <td>{tarihSaat(satir.occurredAt)}</td>
                  <td>
                    <code>{satir.action}</code> {M.eylem[satir.action] ?? ''}
                  </td>
                  <td data-veri>{satir.actorId ?? (satir.actorRole ? rolAdi(satir.actorRole) : TANIMSIZ)}</td>
                  {/*
                    Hedef türü **karşılığıyla** yazılıyor: uç `Customer` gönderiyor, operatör
                    "Müşteri" okuyor (`varlikAdi`, tablo `metin/workflow.ts` içinde). Kimliğin
                    kendisi ham kalıyor ve `data-veri` ile işaretli: o bir kayıt numarası, ekranın
                    cümlesi değil (ölçüldü 28.08.2026, `/denetim` ekranında "Customer" yazıyordu).
                  */}
                  <td>
                    {varlikAdi(satir.targetType)}
                    {satir.targetId ? <> · <span data-veri>{satir.targetId}</span></> : null}
                  </td>
                  <td>{durumAdi(satir.outcome)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <p data-testid="general-audit-total">{G.sayac(tamSayi(result?.items.length ?? 0), tamSayi(result?.total ?? 0))}</p>
      <p>
        <button type="button" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={page <= 1}>
          {ORTAK.onceki}
        </button>{' '}
        {G.sayfa(tamSayi(page))}{' '}
        <button type="button" onClick={() => setPage((current) => current + 1)} disabled={page >= sonSayfa}>
          {ORTAK.sonraki}
        </button>
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------

function IntegrationBanner() {
  const [states, setStates] = useState<{ key: string; authority: string; state: string; missing: string[]; localFallback: string; liveCallsPermitted: boolean }[]>([]);
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api
      .integrations()
      .then((result) => { setStates(result.providers); setLoaded(true); })
      .catch(() => setFailed(true));
  }, []);
  const pending = states.filter((entry) => entry.state !== 'CONFIGURED');
  if (loaded && pending.length === 0) return null;

  return (
    <section data-testid="integration-banner" aria-label={M.entegrasyon.baslik}>
      <strong>{M.entegrasyon.baslik}</strong>
      {!loaded && !failed ? <span>{M.entegrasyon.denetleniyor}</span> : null}
      {failed ? <span>{M.entegrasyon.okunamadi}</span> : null}
      {pending.map((entry) => (
        <article key={entry.key} data-testid={`integration-${entry.key}`} className={`integration-state integration-${entry.state.toLowerCase()}`}>
          {/*
            `entry.key` (`chatwoot`, `paximum`, `globdmc`…) bir iç sistem adı, operatörün
            tanıması gereken bir kelime değil — bu kural bir kez uygulanmış ve geri gelmişti,
            gerekçesi `metin/workflow.ts` `entegrasyon.saglayiciAdi` üstünde. `<strong>` artık
            operatörün okuduğu ad, `<small>` uçtaki `authority`: ne işe yaradığının açıklaması.
            Karşılığı yoksa (uç yeni bir sağlayıcı eklerse) anahtarın kendisi görünür kalıyor.
          */}
          <strong>{M.entegrasyon.saglayiciAdi[entry.key] ?? entry.key}</strong><span>{durumAdi(entry.state)}</span><small>{entry.authority}</small>
          <p>{entry.liveCallsPermitted ? M.entegrasyon.canliCagriSerbest : entry.localFallback}</p>
          {entry.missing.length ? <details><summary>{M.entegrasyon.eksikYapilandirma(tamSayi(entry.missing.length))}</summary><code>{entry.missing.join(', ')}</code></details> : null}
        </article>
      ))}
    </section>
  );
}

function WorkflowReadOnlyPanel() {
  const { error, run } = useNotice();
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  useEffect(() => { void run(async () => {
    const [opportunityPage, orderPage] = await Promise.all([api.opportunities(), api.orders()]);
    setOpportunities(opportunityPage.items); setOrders(orderPage.items);
  }); }, [run]);
  return <section data-testid="workflow-read-only">
    <h2>{M.saltOkunur.baslik}</h2>
    <p>{M.saltOkunur.aciklama}</p>
    {error ? <p className="error">{error}</p> : null}
    <h3>{M.saltOkunur.boruHatti}</h3>
    <ul>{opportunities.map((entry) => <li key={entry.id}>{M.saltOkunur.firsatSatiri(entry.code, entry.title, durumAdi(entry.stage))}</li>)}</ul>
    <h3>{M.saltOkunur.siparisler}</h3>
    <ul>{orders.map((entry) => <li key={entry.id}>{M.saltOkunur.siparisSatiri(entry.code, entry.customer.displayName, durumAdi(entry.status), M.genel.kalemSayisi(tamSayi(entry.items.length)))}</li>)}</ul>
  </section>;
}

/**
 * Akış panelleri artık **kendi bölümlerinin sayfasında** duruyor, ayrı menü maddesi olarak değil.
 *
 * Eskiden dört pano bölümünün yanına dört akış bağlantısı daha konmuştu ve gerekçesi bir yorumda
 * yazılıydı: *"pano onların üstüne geliyor, yerine değil."* Sonuç, aynı odaya açılan iki kapıydı:
 * satış yetkisi olan bir kullanıcı menüde hem "Satış" hem "Sales" görüyordu. Yorum yazmak kararı
 * doğru yapmıyor; karar geri alındı.
 *
 * İzin denetimi panelin **kendisinde** duruyor, çağıranda değil. Sayfa açılabiliyor olsa bile
 * izin yoksa panel çizilmiyor, ve bu bir yetkilendirme değil: arkadaki her uç sunucuda yeniden
 * kontrol ediyor. Burada gizlenen şey yalnız kullanılamayacak bir düğme.
 */
/**
 * FR-24: alt marj eşiğinin altında kalıp taslakta bekleyen sürümlerin onayı.
 *
 * **Neden satış panelinin dışında.** `SalesPanel` teklif yazma yetkisiyle çiziliyor ve müdürde o
 * yetki yok; onay yetkisi (`quote:margin_approve`) ise yalnız müdürde. İki yetki tek panelde
 * birleştirilseydi, onayı verecek tek rol o paneli hiç görmeyeceği için düğme yine erişilemez
 * kalırdı — bugünkü kusurun aynısı, yeri değişmiş hâli.
 *
 * Ekran hangi sürümün eşik altında olduğunu **kendi hesaplamıyor**: teklif listesi bu bilgiyi
 * taşımıyor ve marj yüzdesi FR-25 kapsamında. Karar uçta; taslak sürümler seçilebilir olarak
 * çiziliyor ve onay gerektirmeyen bir sürüm seçilirse ucun cümlesi olduğu gibi yazılıyor.
 */
function MarjOnayiBolumu() {
  const { error, notice, run } = useNotice();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [quoteId, setQuoteId] = useState('');
  const [quote, setQuote] = useState<Quote | null>(null);
  const [surumNo, setSurumNo] = useState('');
  const [not, setNot] = useState('');
  const metin = M.satis.marjOnayi;

  const listeyiOku = useCallback(async () => {
    const sayfa = await api.quotes();
    setQuotes(sayfa.items);
  }, []);

  useEffect(() => {
    void run(() => listeyiOku());
  }, [listeyiOku, run]);

  const teklifiOku = (id: string) =>
    void run(async () => {
      setQuoteId(id);
      if (id === '') {
        setQuote(null);
        setSurumNo('');
        return;
      }
      const okunan = await api.quote(id);
      setQuote(okunan);
      // Onay yalnız taslak bir sürümde anlamlı; ilk taslak sürüm hazır seçili geliyor.
      const taslak = okunan.versions.find((surum) => surum.status === 'DRAFT');
      setSurumNo(taslak ? String(taslak.versionNo) : '');
    });

  const taslaklar = quote?.versions.filter((surum) => surum.status === 'DRAFT') ?? [];

  return (
    <section className="content-section" data-testid="margin-approval" aria-labelledby="margin-approval-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{metin.ustBaslik}</span>
          <h2 id="margin-approval-title">{metin.baslik}</h2>
          <p className="muted">{metin.tarif}</p>
        </div>
      </div>
      <Notices scope="margin-approval" error={error} notice={notice} />
      <p className="muted">{metin.eslesmeNotu}</p>
      <AlanSatiri>
        <Alan etiket={metin.teklifSecin}>
          <select data-testid="margin-approval-quote" value={quoteId} onChange={(event) => teklifiOku(event.target.value)}>
            <option value="">{metin.teklifSecin}</option>
            {quotes.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {M.genel.kodDurum(entry.code, durumAdi(entry.status))}
              </option>
            ))}
          </select>
        </Alan>
        <Alan etiket={metin.surumEtiketi} zorunlu>
          <select data-testid="margin-approval-version" value={surumNo} onChange={(event) => setSurumNo(event.target.value)}>
            <option value="">{metin.surumEtiketi}</option>
            {taslaklar.map((surum) => (
              <option key={surum.id} value={String(surum.versionNo)}>
                {tamSayi(surum.versionNo)}
              </option>
            ))}
          </select>
        </Alan>
        <Alan etiket={metin.notEtiketi} ipucu={metin.notIpucu}>
          <input data-testid="margin-approval-note" value={not} maxLength={240} onChange={(event) => setNot(event.target.value)} />
        </Alan>
      </AlanSatiri>
      <button
        type="button"
        className="birincil"
        data-testid="margin-approval-approve"
        onClick={() =>
          void run(async () => {
            if (!quote || surumNo === '') throw new Error(metin.teklifYok);
            const onaylanan = await api.approveQuoteMargin(quote.id, {
              versionNo: parseTamSayi(surumNo),
              expectedQuoteVersion: quote.version,
              ...(not.trim() ? { note: not.trim() } : {}),
            });
            setQuote(onaylanan);
            setNot('');
            await listeyiOku();
            return metin.onaylandi(onaylanan.code, surumNo);
          })
        }
      >
        {metin.onayla}
      </button>
    </section>
  );
}

/**
 * Gelen talepler: yazılmış bir talebi **yeniden bulmanın** tek yolu.
 *
 * Ölçülen kusur: listeleme ucu 27.08'den beri açıktı ve panelde tek giriş, talebi o an oluşturan
 * formdu. Sayfa yenilenince talep gitmiş görünüyordu; oysa kayıt duruyordu.
 *
 * Liste **salt okunur** ve bilerek öyle. Düzeltme, onay ve fırsata bağlama adımları talep alma
 * formunun kendi işi ve o form teklif yazma yetkisi istiyor; bu liste ise okuma yetkisi olan
 * herkese açık, çünkü "bu talep ne oldu" sorusu satışçının olduğu kadar müdürün de sorusu.
 */
function TalepListesiBolumu() {
  const { error, run } = useNotice();
  const [sayfa, setSayfa] = useState<Paged<QuoteRequest> | null>(null);
  const [durum, setDurum] = useState('');
  const [sayfaNo, setSayfaNo] = useState(1);
  const [secili, setSecili] = useState<QuoteRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const requestSequence = useRef(0);
  const metin = M.satis.talepListesi;

  const yukle = useCallback(
    () =>
      run(async () => {
        const sequence = ++requestSequence.current;
        setLoading(true);
        try {
          const result = await api.salesRequests({ page: sayfaNo, pageSize: 20, ...(durum === '' ? {} : { status: durum }) });
          if (sequence === requestSequence.current) setSayfa(result);
        } finally { if (sequence === requestSequence.current) setLoading(false); }
      }),
    [durum, run, sayfaNo],
  );

  useEffect(() => {
    void yukle();
  }, [yukle]);

  const toplam = sayfa?.total ?? 0;
  const sonSayfa = Math.max(1, Math.ceil(toplam / (sayfa?.pageSize ?? 20)));

  return (
    <section className="content-section sales-request-panel" data-testid="sales-requests" aria-labelledby="sales-requests-title">
      <div className="section-heading">
        <div>
          <h3 id="sales-requests-title">{S.requestRecord}</h3>
          <p className="sales-section-intro">{S.requestListHint}</p>
        </div>
        <span className="count-badge">{loading || error ? '—' : tamSayi(toplam)} {ORTAK.kayit}</span>
      </div>
      <Notices scope="sales-requests" error={error} notice={null} />
      <div className="sales-record-toolbar">
        <Alan etiket={metin.durumSuzgeci}>
          <select
            data-testid="sales-requests-status"
            value={durum}
            onChange={(event) => {
              setSayfaNo(1);
              setDurum(event.target.value);
            }}
          >
            <option value="">{metin.tumDurumlar}</option>
            {(['DRAFT', 'CONFIRMED', 'LINKED'] as const).map((deger) => (
              <option key={deger} value={deger}>
                {durumAdi(deger)}
              </option>
            ))}
          </select>
        </Alan>
      </div>
      {loading ? <p role="status">{S.loading}</p>
        : !error ? <div className="sales-request-list sales-data-frame" data-testid="sales-requests-table">
        <div className="sales-record-columns sales-request-columns" aria-hidden="true"><span>{S.requestRecord}</span><span>{S.stay}</span><span>{S.requestState}</span><span>{S.received}</span><span>{S.action}</span></div>
        {sayfa?.items.map((talep) => <article key={talep.id} className="sales-record-row sales-request-columns" data-testid="sales-request-row">
          <div><strong>{talep.destination ?? TANIMSIZ}</strong><small>{talep.hotelName ?? S.noHotel}</small><small>{talep.adultCount === null ? TANIMSIZ : S.people(talep.adultCount)}</small></div>
          <div><span>{S.departure}: {talep.checkIn ? new Intl.DateTimeFormat('tr-TR').format(new Date(talep.checkIn)) : S.noValidity}</span><small>{S.return}: {talep.checkOut ? new Intl.DateTimeFormat('tr-TR').format(new Date(talep.checkOut)) : S.noValidity}</small></div>
          <div><span className="sales-pill" data-status={talep.status}>{durumAdi(talep.status)}</span><small>{metin.ayristirmaDurumu[talep.parsedAt === null ? 'PENDING' : talep.parsingStatus] ?? talep.parsingStatus}</small></div>
          <span className="sales-record-next">{tarihSaat(talep.createdAt)}</span>
          <button type="button" className="ikincil" aria-label={`${talep.destination ?? S.requestRecord} · ${metin.detay}`} data-testid={`sales-request-open-${talep.id}`} onClick={() => setSecili(talep)}>{S.recordOpen}</button>
        </article>)}
        {!sayfa?.items.length ? <div className="sales-empty">{metin.bos}</div> : null}
      </div> : null}
      <div className="pagination">
        <p data-testid="sales-requests-page">{metin.sayfaBilgisi(tamSayi(sayfa?.page ?? 1), tamSayi(toplam))}</p>
        <div>
          <button type="button" disabled={sayfaNo <= 1} onClick={() => setSayfaNo((simdiki) => Math.max(1, simdiki - 1))}>
            {ORTAK.onceki}
          </button>
          <button type="button" data-testid="sales-requests-next" disabled={sayfaNo >= sonSayfa} onClick={() => setSayfaNo((simdiki) => simdiki + 1)}>
            {ORTAK.sonraki}
          </button>
        </div>
      </div>

      {secili ? (
        <SalesDetailDrawer title={metin.detayBasligi} onClose={() => setSecili(null)}>
        <div data-testid="sales-request-detail">
          <h3>{secili.destination ?? S.requestRecord}</h3>
          <dl className="sales-detail-facts">
            <div><dt>{metin.sutunOtel}</dt><dd>{secili.hotelName ?? S.noHotel}</dd></div>
            <div><dt>{metin.sutunKisi}</dt><dd>{secili.adultCount === null ? TANIMSIZ : S.people(secili.adultCount)}</dd></div>
            <div><dt>{S.departure}</dt><dd>{secili.checkIn ? new Intl.DateTimeFormat('tr-TR').format(new Date(secili.checkIn)) : S.noValidity}</dd></div>
            <div><dt>{S.return}</dt><dd>{secili.checkOut ? new Intl.DateTimeFormat('tr-TR').format(new Date(secili.checkOut)) : S.noValidity}</dd></div>
          </dl>
          <p className="stat-row">
            <span>{metin.sutunDurum}: <strong>{durumAdi(secili.status)}</strong></span>
            <span>{metin.sutunAyristirma}: <strong data-testid="sales-request-detail-parsing">{metin.ayristirmaDurumu[secili.parsedAt === null ? 'PENDING' : secili.parsingStatus] ?? secili.parsingStatus}</strong></span>
          </p>
          {secili.parsingReasonText ? (
            <p className="callout callout-warn" role="note" data-testid="sales-request-detail-parsing-reason">
              {secili.parsingReasonText}
            </p>
          ) : null}
          <p data-testid="sales-request-detail-customer">
            {secili.customerId ? metin.musteriBagi : metin.musteriBagiYok}
          </p>
          <p data-testid="sales-request-detail-opportunity">{secili.opportunityId ? metin.firsatBagi : metin.firsatBagiYok}</p>
          <h4>{metin.hamMetinBasligi}</h4>
          <p className="uzun-deger" data-veri data-testid="sales-request-detail-raw">{secili.rawText}</p>
          {Object.keys(secili.fieldMeta).length === 0 ? null : (
            <div className="table-wrap">
              <table>
                <tbody>
                  {Object.keys(secili.fieldMeta).map((anahtar) => (
                    <tr key={anahtar}>
                      <th scope="row">{M.satis.talep.alanAdi[anahtar] ?? anahtar}</th>
                      <td>{talepOkunurYazi(secili, anahtar)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </SalesDetailDrawer>
      ) : null}
    </section>
  );
}

export function SalesWorkflowPanel({ me, view = 'overview', onView = () => undefined }: { me: Me; view?: SalesView; onView?: (view: SalesView) => void }) {
  const [visited, setVisited] = useState<SalesView[]>([view]);
  useEffect(() => { setVisited((current) => current.includes(view) ? current : [...current, view]); }, [view]);
  return (
    <>
      {me.permissions.includes('sales:write') ? <SalesPanel me={me} view={view} onView={onView} /> : null}
      {/*
        İki bölüm satış panelinin dışında ve kendi izinleriyle çiziliyor. Gerekçe her ikisinde de
        aynı: panelin kapısı teklif yazma yetkisi, ama bu iki işi yapan roller o yetkiyi
        tutmuyor — talep listesini okumak akış okuma yetkisi, marj onayı ise müdürün kendi yetkisi.
      */}
      {me.permissions.includes('workflow:read') && (view === 'requests' || visited.includes('requests')) ? <div className="sales-extra-panel" hidden={view !== 'requests'}><TalepListesiBolumu /></div> : null}
      {me.permissions.includes('quote:margin_approve') && (view === 'approvals' || visited.includes('approvals')) ? <div className="sales-extra-panel" hidden={view !== 'approvals'}><MarjOnayiBolumu /></div> : null}
    </>
  );
}

export type OperationsView = 'orders' | 'visa' | 'calendar' | 'documents' | 'create-document' | 'cancellations' | 'notifications' | 'cost' | 'completion';

export function OperationsWorkflowPanel({ me, view = 'orders', selectedOrderId, visaOrderReference }: { me: Me; view?: OperationsView; selectedOrderId?: string; visaOrderReference?: string }) {
  return (
    <>
      {me.permissions.includes('fulfilment:write') ? <>
        {view === 'orders' ? <OperationsPanel me={me} selectedOrderId={selectedOrderId} /> : null}
        {view === 'visa' ? <VisaRequestsSection me={me} orderReference={visaOrderReference} /> : null}
        {view === 'calendar' ? <OperationsCalendarPanel /> : null}
        {view === 'cancellations' ? <SupplierCancellationsSection /> : null}
        {view === 'notifications' ? <SupplierNotificationsSection /> : null}
        {view === 'cost' ? <SupplierCostSection /> : null}
        {view === 'completion' ? <ServiceCompletionSection /> : null}
      </> : null}
      {/*
        Alt görünüm panelin dışında ve kendi izniyle: `document:read` tutan ama
        `fulfilment:write` tutmayan rol (ADMIN) üretilmiş vize belgelerini görür, üretmez.
      */}
      {view === 'documents' && me.permissions.includes('document:read') ? <VizeBelgeleriBolumu me={me} /> : null}
      {/*
        Belge üretici (10.09.2026). Kendi izniyle çiziliyor ve o izni ADMIN de tutuyor:
        WhatsApp'tan gelen siparişte belgeyi Ürün Sahibi kendisi basıyor.
      */}
      {view === 'create-document' && me.permissions.includes('visa-document:generate') ? <SerbestBelgeUreticiBolumu me={me} /> : null}
    </>
  );
}

function QuoteInvoicePanel({ me, orderId }: { me: Me; orderId: string }) {
  const [open, setOpen] = useState(false);
  if (!me.permissions.includes('finance:read')) return <p>{COLLECTION.invoicePermission}</p>;
  return <div>
    <button type="button" data-testid="quote-invoice-open" aria-expanded={open} onClick={() => setOpen(!open)}>{COLLECTION.invoiceOpen}</button>
    {open ? <FinanceWorkflowPanel me={me} selectedOrderId={orderId} initialTab="invoice" /> : null}
  </div>;
}

export function FinanceWorkflowPanel({ me, selectedOrderId, initialTab }: { me: Me; selectedOrderId?: string; initialTab?: FinanceDetailTab }) {
  return me.permissions.includes('finance:read') ? <FinancePanel key={selectedOrderId} me={me} selectedOrderId={selectedOrderId} initialTab={initialTab} /> : null;
}

export function OversightWorkflowPanel({ me }: { me: Me }) {
  return (
    <>
      {me.permissions.includes('oversight:read') ? <OversightPanel /> : null}
      {/* Denetim B11: sipariş odaklı gözetimden bağımsız, kendi izniyle (`audit:read`) açılan ayrı bir bölüm. */}
      {me.permissions.includes('audit:read') ? <GeneralAuditPanel /> : null}
    </>
  );
}

/**
 * Entegrasyon hazırlık şeridi ve salt okunur uyarısı.
 *
 * İkisi de bir bölüme ait değil: biri bütün sistemin dış bağlantılarını, öteki rolün
 * kendisini anlatıyor. Açılış sayfasında duruyorlar, yani her girişte bir kez görünüyorlar.
 */
export function WorkspaceNotices({ me }: { me: Me }) {
  return (
    <>
      <IntegrationBanner />
      {me.role === 'READ_ONLY' ? <WorkflowReadOnlyPanel /> : null}
    </>
  );
}
